/**
 * BPF Memorial Designer — Phase 1.3 + Phase 2 + Phase 2.2 (+2.2.1) + Phase 2.3 (+2.3.1, +2.3.2, +2.3.3, +2.3.4, +2.3.4.1, +2.3.4.3) + Phase 2.5 (+2.5.1, +2.5.2, +2.5.3, +2.5.4, +2.5.5, +2.5.6, +2.5.6.1, +2.5.6.2, +2.5.6.3) + Phase 3.0
 *
 * Phase 3.0 (Memorial Canvas — breed-specific proof path, Dog -> Pitbull
 * -> Vault only): the placeholder canvas is replaced by a real layered
 * renderer, deliberately split into a second file, assets/bpf-memorial-
 * canvas.js (window.BpfMemorialCanvasRenderer, loaded before this file —
 * see the two <script> tags in sections/bpf-memorial-designer.liquid).
 * This file gained three things, all new, nothing existing changed
 * shape: resolveVisual() — a SEPARATE resolver from resolveProduct()
 * above, same documented 3-tier precedence shape, its own independent
 * implementation (not a shared helper, and not touching resolveProduct()
 * at all, per explicit approval — that refactor is deferred until both
 * resolvers are independently proven); getCanvasState() — a read-only
 * projection combining this.state.resolvedProduct (product side, zero
 * new logic) with live DOM reads of colors/engraved name (personalization
 * side, same selectors updateReviewSummary() already uses — this.state
 * still deliberately doesn't carry personalization, unchanged from
 * Phase 2.5); updateCanvasFromState() — called once at the end of the
 * constructor and once at the end of handleChange() (the existing
 * delegated listener — no new listeners added). The renderer only ever
 * receives what getCanvasState() builds: it never queries a form field,
 * never reads this.state, never resolves a product, and never affects
 * validation/Add to Cart/line-item properties. A visual-resolution
 * failure (no matching visualAssets) can never block Add to Cart;
 * product-resolution failure (isAvailable: false) always overrides
 * whatever the canvas would otherwise show. this.canvasPlaceholder — the
 * same node updateCanvasPosition() already moves between the desktop/
 * mobile-Review/hidden-holding mounts — is untouched as the mount-move
 * target; BpfMemorialCanvasRenderer is constructed once against it in
 * this file's own constructor, so moving that node between mounts never
 * recreates or resets renderer state.
 *
 * Phase 2.5.6 (Builder Hero and Review purchase summary — presentation
 * only): this.priceDisplay/this.defaultPriceHtml (the top-of-page price
 * display that used to live alongside the removed host product title) are
 * gone, replaced by this.reviewPriceValue — a new element inside the
 * Review purchase summary (sections/bpf-memorial-designer.liquid), the
 * only place the resolved price is ever shown now. updatePriceDisplay()'s
 * call sites and the data feeding it (this.state.resolvedProduct / the
 * resolver) are completely unchanged — only where the result gets written
 * changed. The hero's own starting-price line is static, merchant-edited
 * copy (section.settings.starting_price_message) that this file never
 * touches.
 *
 * Phase 2.5.5 (remove temporary variant diagnostics): the debug panel and
 * all supporting code added across Phases 2.5.4.1-2.5.4.3 (BPF_DEBUG/
 * bpfDebugLog, the debug panel element references, initDebugPanel()/
 * renderDebugPanel()/renderDebugSubmitSnapshot()/handleCartDebugEvent(),
 * and the 'bpf:cart-debug' listener) have been removed entirely — they
 * served their purpose, tracing the reported "Cannot find variant" error
 * to the test product being set to Draft in Shopify Admin, not to
 * anything in this file. The Phase 2.5.4 production safeguards below
 * (option-position detection, validVariantIds, isMemorialStyleValid()'s
 * strengthened checks) remain, since they're genuinely valid regardless
 * of that incident's actual cause.
 *
 * Phase 2.5.4 (variant-resolution safeguards — see the note at the end of
 * this entry for the actual root cause of the reported Add-to-Cart
 * defect):
 * - Root cause hypothesis at the time: sections/bpf-memorial-designer.liquid
 *   resolved bpf_capsule_variant/bpf_vault_variant via a hardcoded
 *   'option1' assumption for the Urn Style option. The fix removes the
 *   assumption itself (verifying which option's real values actually
 *   contain 'Capsule'/'Vault' rather than guessing a position) so it
 *   self-corrects regardless of which position Urn Style actually
 *   occupies — see that file's own changelog entry for the full
 *   explanation.
 * - isMemorialStyleValid() gained three more checks beyond Phase 2.5.2's:
 *   record.available must be true (a resolved-but-sold-out record no
 *   longer counts as "Step 2 complete"), the hidden `id` input must
 *   actually be enabled (verified, not assumed), and the resolved
 *   variantId must appear in validVariantIds — parsed from a new
 *   data-valid-variant-ids script tag Liquid renders from this exact
 *   product's real, current variants at the same render as the
 *   eligibility data, so a resolver record can never point at an ID
 *   outside what Shopify's own catalog actually has for this product,
 *   without any extra network call. applyResolvedProduct() mirrors the
 *   same three checks and surfaces the existing "unavailable" UI (rather
 *   than silently leaving Add to Cart disabled with no explanation) the
 *   instant any of them fails.
 * - NOTE (Phase 2.5.5): the actual reported "Cannot find variant"
 *   incident was traced (via the temporary diagnostic panel, since
 *   removed) to the test product being set to Draft in Shopify Admin —
 *   Shopify's cart API correctly rejected every variant regardless of
 *   how it was resolved, which is why both Capsule and Vault failed
 *   identically. The fixes above were not the resolution for that
 *   incident, but remain in place as genuine, independently-valid
 *   safeguards — a hardcoded option1 assumption and an unverified
 *   variant ID are both real risks worth closing regardless.
 *
 * Phase 2.5.3 (restore Memorial Wall Pet Name for Vault — reverses one
 * piece of Phase 2.5.1's "single source of truth" design below; the rest
 * of that entry, and all of Phase 2.5.2, are still current):
 * - The Memorial Wall Pet's Name field (properties[Companion Name]) is
 *   shown/required for BOTH Vault and Capsule whenever Memorial Wall is
 *   Yes — updateMemorialWallPetNameVisibility() no longer excludes Vault.
 *   Vault customers may now hold two independently-editable values on
 *   purpose: the engraved name (properties[Pet Name], still optional,
 *   Step 3) and this Memorial Wall name (Step 5) — e.g. "MAX" engraved,
 *   "Maxwell" here. For Vault, this field still pre-fills from the
 *   engraved name, but only until the customer edits it directly —
 *   companionNameManuallyEdited (removed in Phase 2.5.1, when the field
 *   became permanently hidden for Vault) is reintroduced;
 *   syncCompanionNameFromEngraved() checks it before ever overwriting.
 *   clearInactiveStyleFields() no longer clears this field on a style
 *   switch, since it's independent of style now, not a Vault-only mirror.
 * - isCompanionNameValid() no longer exempts Vault — required for both
 *   styles whenever the field is visible, never required while hidden.
 * - Review restores two distinct rows where applicable: "Engraved Name"
 *   (Vault, shown whenever an engraving value exists, independent of
 *   Memorial Wall) and "Pet Name" (both styles, shown only while Memorial
 *   Wall is Yes) — no deduplication, since they're genuinely different
 *   concepts and may legitimately show the same or different values.
 * - Root cause of the "field won't show for Vault" report: a deliberate
 *   Phase 2.5.1 design choice (updateMemorialWallPetNameVisibility()'s
 *   `!isVault` exclusion), not a CSS/breakpoint defect — confirmed no
 *   breakpoint-specific rule ever existed for this element; the shared
 *   visibility logic behaves identically on desktop and mobile both
 *   before and after this fix.
 *
 * Phase 2.5.1 (three approved UX adjustments before Phase 3) — NOTE: the
 * first bullet below (hiding the Memorial Wall field entirely for Vault)
 * was reversed in Phase 2.5.3 above; the other two bullets are unchanged:
 * - Pet's Name single source of truth: properties[Companion Name] (same
 *   property, still submitted) relocated from Personalize into Memorial
 *   Wall. For Vault, the field stays hidden and its value is kept in sync
 *   automatically from the engraved Pet Name (updateMemorialWallPetNameVisibility()
 *   calls syncCompanionNameFromEngraved() whenever Vault is active) — the
 *   customer is never asked for the same name twice. For Capsule (no
 *   engraving field), it's the one place the name is collected, shown and
 *   required only while Memorial Wall is opted into (isCompanionNameValid()).
 *   isPersonalizeValid() no longer includes it; isBuilderComplete() composes
 *   it directly instead.
 * - Mobile-only Memorial Canvas relocation: the canvas is never shown near
 *   the top of the page on mobile at all. updateCanvasPosition() moves the
 *   one canvas placeholder node between three mounts: its original desktop
 *   position (always used at >=750px, every step), a mount inside the
 *   Review accordion above the summary (mobile, only while Review is the
 *   open step), or a permanently `hidden` holding mount (mobile, every
 *   other step — not the top of the page). Editing an earlier step while
 *   Review was showing the canvas moves it straight to the holding mount;
 *   returning to Review moves it back. Driven by every accordion toggle
 *   (initAccordion()) and the same debounced resize handler as
 *   updateStickyNavOffset(). Desktop is never touched. The mount-switching
 *   mechanism is deliberately generic (three named mounts, one condition)
 *   so a future product-page experience can choose a different mobile
 *   strategy by changing only which mount wins, not the canvas or the
 *   mechanism itself. The Phase 2.3.3 mobile compact-preview bar (tied to
 *   the canvas's position near the top of the page) is removed — its
 *   premise doesn't apply once the canvas is never there to scroll past.
 * - Memorial Style auto-selection is no longer silent: updateStyleCardVisibility()
 *   never hides the card anymore (eligibleCount === 1 used to). Instead it
 *   shows an informational message in place of the radios and disables
 *   them, so the customer still sees which style applies and can't switch
 *   to the ineligible one. Progress/numbering/Continue are unaffected,
 *   since getVisibleSteps() never excludes this card now.
 *
 * ── Architecture overview (Phase 2.5) ───────────────────────────────────
 * One-directional data flow — no step reads from further downstream than
 * the one immediately before it:
 *
 *   Eligibility Data -> Product Resolver -> Resolved Product -> Builder
 *   State -> UI (Progress, Review, Validation, Add to Cart, (Phase 3)
 *   Memorial Canvas)
 *
 * - Eligibility Data is the normalized array parsed once by
 *   parseEligibilityData() from <script type="application/json"
 *   data-eligibility-data>. It is the ONLY interface this file depends on
 *   — origin-agnostic by design. The current array (sections/bpf-memorial-
 *   designer.liquid) is hand-written TEST DATA ONLY; Phase 2.6 replaces
 *   just the Liquid source that produces it (e.g. metaobjects/real
 *   catalog) with zero JS changes, as long as the array shape is unchanged.
 * - resolveProduct() is the SINGLE authoritative place a Companion/Breed/
 *   Style combination is turned into a product. Every caller that needs a
 *   resolved product (recomputeEligibility(), applyResolvedProduct()) goes
 *   through it — no other method may independently look up or guess one —
 *   so the 3-tier precedence and completeness rules are enforced exactly
 *   once, in exactly one place.
 * - this.state (Builder State) is the authoritative record of the
 *   customer's in-progress configuration. Future features — Phase 3's
 *   Memorial Canvas included — should read from this.state rather than
 *   re-deriving the same information from the DOM.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Phase 2.5: conditional flow architecture — Companion → Breed → Memorial
 * Style resolution, one centralized builder state object.
 *
 * - Eligibility data: GENERIC_KEY-based normalized records, parsed once
 *   from a single <script type="application/json" data-eligibility-data>
 *   tag (parseEligibilityData()). This class never knows or cares whether
 *   that JSON was generated from plain Liquid variables (Phase 2.5) or
 *   real catalog data (Phase 2.6) — the interface is the array shape
 *   itself, not its Liquid source.
 * - resolveProduct(companionType, breed, style) implements the approved
 *   3-tier precedence resolver: exact breed → companion-generic breed →
 *   globally-generic. A record with enabled:false and allowFallback:true
 *   lets the resolver keep searching lower tiers; enabled:false with
 *   allowFallback:false is a hard stop (explicitly unavailable — never
 *   silently falls back to a generic product). No record at a tier at all
 *   just continues to the next tier. This is called independently for
 *   Capsule and Vault — the resulting eligible count (0/1/2) drives the
 *   "Choose Your Memorial" card's visibility exactly as approved: 2 →
 *   shown, customer picks; 1 → hidden, auto-selected; 0 → shown with an
 *   "unavailable" message replacing the radios, blocking progression.
 * - One centralized state object, this.state, covering Companion, Breed,
 *   Memorial Style (+ eligibility), the currently resolved product,
 *   Memorial Wall opt-in, and Review lock status — these are all genuinely
 *   new concerns this phase introduces, so they're state-native.
 *   Personalization/Photo are deliberately NOT part of this.state — they
 *   keep reading directly from their own DOM inputs via their existing,
 *   already-proven validators (isPetNameValid(), isCompanionNameValid(),
 *   areActiveColorsSelected(), handlePhotoChange()), the same as before
 *   this phase. isBuilderComplete() still composes their validity in, it
 *   just calls those DOM-backed validators directly rather than reading a
 *   mirrored copy — there is no this.state.personalization/photo.
 * - getVisibleSteps() (filters this.accordions by the native `hidden`
 *   attribute — no parallel visibility bookkeeping needed, since
 *   updateStyleCardVisibility() already sets `hidden` directly) is the one
 *   authority for "which accordion steps exist right now" — replacing the
 *   old assumption that this.accordions itself was always the full,
 *   correctly-ordered visible list. Used by the stepper (renumberStepper()),
 *   updateCurrentStepDisplay(), and initContinueButtons()'s "next" lookup.
 *   isBuilderComplete() is the one authority for whether Review may unlock
 *   — composes isCompanionTypeValid() + isBreedValid() + isMemorialStyleValid()
 *   + isPersonalizeValid() (all pre-existing or new field-level validators;
 *   isBuilderComplete() only orchestrates, matching how updateAddToCartState()
 *   was already structured, just centralized instead of a hardcoded chain).
 * - Review is now genuinely locked, not just visually plain: a `click`
 *   listener on its <summary> (not the non-cancelable `toggle` event)
 *   calls preventDefault() while locked — this also blocks keyboard
 *   activation, since browsers dispatch a synthetic click for Enter/Space
 *   on <summary>. Relocks (force-closes if open) the instant
 *   isBuilderComplete() goes false again.
 * - Reset cascades: handleCompanionTypeChange() clears every downstream
 *   answer (resetDownstreamOfCompanion()) except the newly selected type,
 *   showing the reset notice only from the second selection onward.
 *   handleBreedChange() re-resolves eligibility (recomputeEligibility(),
 *   which already knows to preserve a still-eligible manual style pick and
 *   its Personalization) and separately detects whether the *active*
 *   resolved style itself changed as a side effect — if so,
 *   clearInactiveStyleFields() clears only that style's own colors/
 *   engraved name (not Companion Type, Breed, Photo, Memorial Wall, Story,
 *   or State). Manual style radio clicks are deliberately NOT run through
 *   clearInactiveStyleFields() — that stays exactly the existing
 *   hide+disable-only behavior, since actively clearing values was only
 *   approved for the new invisible/automatic breed-driven case.
 * - Companion Name (properties[Companion Name]) relocates from the removed
 *   "Tell Us About Your Companion" card into "Personalize Your Memorial";
 *   Story/State (properties[Pet Story]/properties[Companion State])
 *   relocate into "Memorial Wall" as children, shown on "Yes"/hidden+
 *   disabled-but-preserved on "No". None of these fields' selectors,
 *   validation, or the existing one-directional Engraved-Name→Companion-
 *   Name sync (syncCompanionNameFromEngraved(), unchanged) needed to
 *   change — they're selector-based, not accordion-card-based.
 *
 * Phase 2.3.4.3: final desktop sticky progress structure — restored
 * desktop stickiness for the full-width nav after the actual root cause
 * (the canvas's own sticky offset never accounting for the nav's height)
 * was fixed directly in assets/bpf-memorial-designer.css. No JS changed
 * for that phase.
 *
 * Local, isolated Urn Style (Capsule / Vault) controller — the Phase 2.5
 * resolver supplements this, it doesn't replace it: currentUrnStyle is
 * still whichever radio is checked (manually or auto-checked by the
 * resolver), and applyPersonalizationVisibility()/isVaultSelected() are
 * completely unchanged, since "which radio is checked" is exactly the same
 * signal regardless of how it got checked.
 *
 * This intentionally does NOT use the theme's <variant-radios> / VariantSelects
 * component (assets/global.js) — see prior phase notes. Every Urn Style
 * radio still carries a static data-variant-id/data-available/data-price
 * attribute from Phase 1.1's matched_variant lookup, but this file no
 * longer reads them for anything — applyResolvedProduct() only ever
 * submits/displays a resolver-confirmed record (see its own comment) and
 * blocks Add to Cart rather than falling back to these attributes.
 *
 * <product-form> (assets/product-form.js) is untouched and still owns the
 * actual Add to Cart submission and cart drawer response — it submits
 * generically via `new FormData(this.form)`, so a resolved product's
 * variant ID from a different real product (Phase 2.6) submits exactly
 * like this product's own variant does today.
 */

const BPF_GENERIC_KEY = 'generic';

// Phase 7H.7 — mobile-only "Next: <step>" forward-dock CTA copy. Keyed
// by data-accordion-section (the same stable step identifier
// getVisibleSteps()/navigateToStep() already use everywhere else in
// this file), not by position, so a hidden step (Companion in breed/
// product mode, Add-ons or Memorial Wall when the product doesn't
// support them) is never a possible lookup — updateMobileNav() only
// ever looks up whatever visibleSteps[currentIndex + 1] actually
// resolves to, which already excludes hidden steps by construction.
// Deliberately short-form text distinct from each step's own full
// <summary> title (e.g. "Choose Memorial", not "Choose Your Memorial"
// — the shorter wording is required for mobile width) — a fixed
// mapping, not a live readout of the destination step's own heading.
const BPF_MOBILE_NEXT_STEP_LABELS = {
  style: 'Choose Memorial',
  personalize: 'Personalize',
  photo: 'Add-ons',
  'memorial-wall': 'Memorial Wall',
  review: 'Review',
};

// Phase 7H.7C — bounded retry window for a Builder analytics event
// whose first send attempt lands before Shopify's own GA4 tag has
// defined window.gtag (live GA4/BigQuery verification found this on
// direct breed-collection landings: the initial bpf_builder_exposure
// and initial bpf_builder_step_view never arrived, while later
// navigation events — sent once gtag existed — succeeded normally).
// 500ms x 40 = 20s, comfortably longer than the ~10s gap observed
// live, after which the retry TIMER stops (never an unbounded poll) —
// but see trackBuilderEvent()'s own comment for why a still-pending
// event isn't lost even after that: any later real Builder event
// opportunistically flushes it first.
const BPF_ANALYTICS_RETRY_INTERVAL_MS = 500;
const BPF_ANALYTICS_RETRY_MAX_ATTEMPTS = 40;

// Phase 4D.10E.2, Parts 3/4 — the ONE authoritative fallback source for
// the real Pet Memorial Picture Frame (product 9020789948614) variant
// IDs, manually verified from Shopify Admin (screenshots) on 2026-08-14
// specifically because that product is currently Draft — not published
// to the Online Store sales channel — so all_products['pet-memorial-
// picture-frame'] resolves to nil in Liquid and bpf_pf_commerce_json
// (sections/bpf-memorial-designer.liquid) can never see these variants
// through live storefront data today. This is deliberately a LAST
// RESORT, never the first choice: resolvePictureFrameVariant() below
// always tries this.pictureFrameCommerce (the live, generically-
// resolved Liquid data) FIRST, and only reaches into this table when
// that lookup finds nothing for the given color — the moment the
// product is published, live data resolves first and this table stops
// being consulted at all, with zero code changes required.
//
// priceCents: 1500 for every color is the manually-verified CURRENT
// price of all five real variants as of this phase — explicitly a
// temporary fallback, never a permanent hard-coded product-price
// architecture (Part 5). imageUrl is deliberately omitted (undefined)
// for every entry — Part 6 forbids inventing/guessing a variant image
// URL; every consumer of a fallback-resolved variant (Step 4 price,
// Review pricing, the Details popup, the real cart-line add) already
// treats a missing imageUrl exactly like "unavailable," never a
// silently wrong image.
//
// shopifyColorValue is the REAL Shopify option value for that variant
// (e.g. "Metallic Gold", not the Builder's own "Gold") — carried here
// for reference/diagnostics only; nothing in this file needs to submit
// it anywhere, since Add to Cart only ever needs the variant id itself.
const BPF_PICTURE_FRAME_VERIFIED_FALLBACK = {
  Black: { id: 46921686646982, shopifyColorValue: 'Black', priceCents: 1500, priceFormatted: '$15.00', available: true },
  White: { id: 46921686614214, shopifyColorValue: 'White', priceCents: 1500, priceFormatted: '$15.00', available: true },
  Gold: { id: 46921688711366, shopifyColorValue: 'Metallic Gold', priceCents: 1500, priceFormatted: '$15.00', available: true },
  Blue: { id: 46921688744134, shopifyColorValue: 'Metallic Blue', priceCents: 1500, priceFormatted: '$15.00', available: true },
  Pink: { id: 46921688776902, shopifyColorValue: 'Metallic Pink', priceCents: 1500, priceFormatted: '$15.00', available: true },
};
// Phase 4D.8C: bounds uploadBuilderImage()'s fetch() so a stalled network
// request can never strand the intercepted Add to Cart submission
// indefinitely — fetch() itself has no default timeout.
const BPF_BUILDER_IMAGE_UPLOAD_TIMEOUT_MS = 20000;

// Phase 5C.1P — a separate, narrower budget for LOCAL memorial canvas
// rendering/export work (window.BpfMemorialRenderer.exportMemorialPreviewPNG()
// and the early-prepared _preparedCanvasBlobPromise built from that same
// call), deliberately distinct from BPF_BUILDER_IMAGE_UPLOAD_TIMEOUT_MS
// above, which governs genuine network operations (the photo upload
// wait, uploadBuilderImage()'s own fetch). Business/UX decision: if
// memorial canvas rendering takes anywhere close to 20 seconds,
// something is already wrong — a real renderer stall should be
// recognized and abandoned well before the two 20-second network
// budgets it precedes even get a chance to run.
//
// Phase 5C.1Q — this is a single TOTAL budget for this purchase attempt's
// renderer work, not a per-attempt allowance. performPrePurchaseImageUpload()'s
// canvas branch computes ONE deadline (Date.now() + this constant) up
// front and both the prepared-Blob reuse wait and the fresh-export
// fallback (when one is permitted) share the remaining time left on that
// same deadline — a prepared Blob that actively rejects after consuming
// part of the budget lets the fresh attempt use only what's left, never
// a fresh full 10 seconds on top. A prepared Blob that TIMES OUT (as
// opposed to actively rejecting) still gets zero fresh-export attempts,
// since the deadline is already exhausted by definition when that
// happens — both rules exist to prevent this constant's intent (one
// 10-second renderer ceiling) from silently doubling back up to 20
// seconds of renderer-only delay.
const BPF_CANVAS_EXPORT_TIMEOUT_MS = 10000;

// Phase 5E.1A — mobile-only proximity-based whole-Builder viewport
// anchoring. As the customer scrolls down toward the Builder,
// [data-mobile-shell] (the existing mobile flex shell — canvas, card
// deck, and bottom action dock — already `position: sticky; top:
// var(--padding4)` at <750px, see assets/bpf-memorial-designer.css) is
// gently nudged the rest of the way to that SAME sticky top offset once
// it's already close, rather than leaving the customer to scroll the
// exact remaining pixels by hand — native CSS sticky then holds it in
// place and releases it again on its own once the customer scrolls far
// enough past it in either direction; this feature never tries to hold
// position itself, only smooths the final approach.
//
// ACTIVATION_DISTANCE_PX: the shell's current getBoundingClientRect().top
// must be within this many px ABOVE the sticky target (i.e. still
// approaching, not yet pinned) before a settle may trigger at all —
// chosen from the middle of the reasonable 80-180px range: close enough
// that the nudge reads as "finishing what the customer's own scroll
// already started," far enough that it never fires from a large,
// unrelated page scroll that merely passes near the Builder.
const BPF_VIEWPORT_ANCHOR_ACTIVATION_DISTANCE_PX = 140;
// TOLERANCE_PX: the shell is considered "anchored" once within this many
// px of the target — avoids both subpixel-exact requirements and
// jitter from repeatedly re-evaluating a settle that's already close
// enough (native sticky itself becomes exact once actually engaged).
const BPF_VIEWPORT_ANCHOR_TOLERANCE_PX = 8;
// RELEASE_DISTANCE_PX — Phase 5E.1B: real iPhone QA on 5E.1A found that
// once seated, light continued scrolling could gradually "leak" out of
// the anchor — the old release check compared each reading only to the
// immediately PRECEDING one, so a single 1px increase between two
// throttled evaluations was already enough to release, with no notion
// of how far the shell had actually drifted from its seated position.
// This is the fix: once seated (see _viewportAnchorSeated), a
// displacement is measured against the SEATED TARGET itself, not the
// previous reading — displacement up to this many px stays inside the
// groove (receiving at most one bounded recenter, never released);
// beyond it is a deliberate escape. Deliberately a separate concept
// from TOLERANCE_PX above: tolerance answers "are we seated," this
// answers "how far can we drift before we've clearly left." 32px:
// noticeably deeper than an 8px tolerance without approaching the
// 140px activation distance — a real QA-tunable starting point per
// this phase's own task spec, not a value to keep re-tuning from
// automated-harness behavior alone.
const BPF_VIEWPORT_ANCHOR_RELEASE_DISTANCE_PX = 32;
// INTERACTION_QUIET_MS: how long after the most recently observed
// touch/click/change/focus interaction anywhere in the Builder before
// magnetism is eligible again — covers the tap-then-release shape of a
// pill/select/button interaction without needing to enumerate every
// individual control (Part 13).
const BPF_VIEWPORT_ANCHOR_INTERACTION_QUIET_MS = 600;
// SETTLE_TIMEOUT_MS: safety cutoff for the geometry-tolerance completion
// check after triggering one smooth scrollTo() — never depended on
// exclusively (the tolerance check itself is what normally ends
// SETTLING), just a backstop so a browser that never quite reaches
// tolerance (e.g. reduced/clamped smooth-scroll speed) cannot leave the
// controller stuck in SETTLING indefinitely.
const BPF_VIEWPORT_ANCHOR_SETTLE_TIMEOUT_MS = 900;
// RESIZE_DEBOUNCE_MS: matches the file's own existing window resize
// debounce (see connectedCallback()'s this._handleResize) — reused here
// for visualViewport's own resize event, not a new/different cadence.
const BPF_VIEWPORT_ANCHOR_RESIZE_DEBOUNCE_MS = 150;

// Phase 4D.9B.5, Part 6: temporary, OFF-by-default timing instrumentation
// for diagnosing the Memorial Wall = Yes Add to Cart latency during live
// QA. Only active when the page URL includes ?bpf_debug_timing=1 — logs
// nothing otherwise, so this ships permanently gated rather than being
// ripped out before finalizing. Covers only this file's own pipeline
// (click -> submit interception -> Wall branch -> canvas export ->
// upload -> native resume); the remaining native /cart/add + cart
// drawer render segment lives entirely in assets/product-form.js, which
// this phase found no evidence of being defective — measure that
// segment with the browser's own Network/Performance panel instead of
// adding instrumentation to working theme cart code.
function bpfTimingEnabled() {
  try {
    return typeof window !== 'undefined' && window.location && /[?&]bpf_debug_timing=1\b/.test(window.location.search || '');
  } catch (error) {
    return false;
  }
}
function bpfTimingMark(label) {
  if (!bpfTimingEnabled()) return;
  var t = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  console.info('[bpf-timing] ' + label + ' @ ' + t.toFixed(1) + 'ms');
}

// Phase 4D.9B.9: an independent backstop bounding how long Add to Cart
// can ever wait on a given promise — never rejects, always resolves
// once EITHER the promise settles OR `ms` elapses, whichever comes
// first. Used to bound the Favorite Photo branch's await on
// _photoUploadPromise: uploadBuilderImage() already has its own
// internal AbortController timeout, but that safety net lives one
// promise-chain hop away from where Add to Cart actually waits — this
// guarantees Add to Cart itself can never be blocked indefinitely by
// the photo path even in an unanticipated failure mode, regardless of
// whether that inner timeout fires as expected. A photo that hasn't
// settled by the bound simply falls through to Priority 3 (no image),
// the exact same non-blocking outcome an outright upload failure
// already produces.
function bpfWithTimeout(promise, ms) {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve();
    }, ms);
    Promise.resolve(promise).then(
      () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      },
      () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      }
    );
  });
}

// Phase 5C.1O — a companion to bpfWithTimeout() for the two call sites
// inside performPrePurchaseImageUpload()'s canvas branch that need the
// actual resolved value (the exported Blob), which bpfWithTimeout()
// deliberately discards by design (see its own header comment). Real
// mobile QA found window.BpfMemorialRenderer.exportMemorialPreviewPNG()
// — and, transitively, the early-prepared _preparedCanvasBlobPromise
// maybePrepareMemorialCanvas() builds from that same unbounded call —
// had NO timeout anywhere in the chain: a single stalled image request
// inside the renderer (a realistic real-mobile-network condition) could
// leave performPrePurchaseImageUpload() waiting forever, which in turn
// left the purchase transaction guard, the "Preparing your memorial…"
// spinner, and the combined /cart/add.js mutation all stuck — the
// mutation was never even sent, matching the real customer symptom
// (indefinite spinner, cart empty on reload). Resolves with the real
// value if the promise settles first; otherwise REJECTS with a timeout
// Error once `ms` elapses — deliberately the SAME shape a genuinely
// failed/rejected renderer call already produces, so every existing
// catch/fallback that already treats a renderer rejection as "no image,
// proceed without it" (this function's own established, unmodified
// "canvas persistence is best-effort" contract) handles a timeout
// identically, with no second failure branch to invent or maintain.
//
// Phase 5C.1P — the timeout rejection now carries error.name ===
// 'TimeoutError', so a caller that needs to react differently to "the
// renderer is genuinely stuck" versus "the renderer actively failed"
// can tell them apart reliably (never by brittle string-matching the
// message). An ordinary rejection from the wrapped promise is passed
// through completely unchanged — its own name/identity is preserved.
function bpfWithTimeoutOrReject(promise, ms) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      const timeoutError = new Error('bpf-memorial-designer: timed out after ' + ms + 'ms');
      timeoutError.name = 'TimeoutError';
      reject(timeoutError);
    }, ms);
    Promise.resolve(promise).then(
      (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

if (!customElements.get('bpf-memorial-designer')) {
  customElements.define(
    'bpf-memorial-designer',
    class BpfMemorialDesigner extends HTMLElement {
      constructor() {
        super();

        this.urnStyleInputs = Array.from(this.querySelectorAll('.bpf-urn-style-input'));
        // Phase 2.5.1: Liquid may render a radio pre-disabled when this
        // product's own variant is genuinely out of stock (Phase 1.1's
        // matched_variant.available check) — captured here, before this
        // class ever touches .disabled itself, so "re-enable the style
        // radios" (single-eligible auto-select ending) restores this
        // original state instead of blindly clearing a real stock flag.
        this._urnStyleOriginalDisabled = new Map(this.urnStyleInputs.map((input) => [input, input.disabled]));
        this.colorGroups = Array.from(this.querySelectorAll('.bpf-color-group'));
        // Phase 4D.9A — Capsule Type (Memorial Urn vs Memorial Statue),
        // feature-flagged in Liquid via section.settings.
        // enable_capsule_type_choice. When that flag is off, neither
        // element exists in the DOM, so capsuleTypeInputs is simply an
        // empty array — every consumer below already guards on
        // .length/existence, so no separate "is the feature enabled"
        // check is needed anywhere in this file.
        this.capsuleTypeChildren = this.querySelector('[data-capsule-type-children]');
        this.capsuleTypeInputs = Array.from(this.querySelectorAll('.bpf-capsule-type-input'));
        // Phase 5C.1G, Parts 1-5 — the SAME single, always-together
        // "Memorial Urn: ... / Memorial Statue: ..." explanatory <p>
        // that already exists (never swapped per-selection — both
        // options are always explained together) — this phase only
        // brings it into view on mobile, never duplicates or swaps its
        // content.
        this.capsuleTypeHelpText = this.querySelector('[data-capsule-type-help]');

        // Phase 4D.9B.3, Parts 4-6 — Memorial Statue Add to Cart
        // confirmation. The <modal-dialog> itself always relocates to
        // document.body on its own connectedCallback() (assets/global.js),
        // so — same as the Additional Photos gallery modal — it's queried
        // via document.querySelector() rather than this.querySelector(),
        // and its markup lives as a sibling in the section, not nested
        // inside this element. A single unscoped [data-*] selector is
        // safe here: this Builder is a single-instance-per-page component
        // (see e.g. the body-class toggle in connectedCallback()).
        this.statueConfirmModal = document.querySelector('[data-bpf-statue-confirm-modal]');
        this.statueConfirmProceedBtn = this.statueConfirmModal
          ? this.statueConfirmModal.querySelector('[data-bpf-statue-confirm-proceed]')
          : null;
        this.statueConfirmBackBtn = this.statueConfirmModal
          ? this.statueConfirmModal.querySelector('[data-bpf-statue-confirm-back]')
          : null;
        // Narrow, single-use continuation flag — see handleSubmitClick()
        // and confirmStatueAndProceed() for why this must never become a
        // durable "the customer already confirmed Statue" record.
        this._statueConfirmBypass = false;
        // Phase 4D.9B.11: the same narrow, single-use shape as
        // _statueConfirmBypass above, for the Wall = Yes + Favorite
        // Photo direct-click convergence fix — see handleSubmitClick().
        this._memorialPhotoBypass = false;
        this.petNameField = this.querySelector('.bpf-pet-name-field');
        this.petNameInput = this.querySelector('.bpf-pet-name-input:not(.bpf-companion-name-input)');
        this.petNameCounter = this.querySelector('.bpf-pet-name-counter');
        this.petNameError = this.querySelector('.bpf-pet-name-error');
        // Engraving Color: mirrors petNameField/petNameInput's own
        // separate-from-colorGroups pattern — visibility is capability-
        // driven (currentStyleSupportsEngraving()), not a data-applies-to
        // style match, so it's queried and handled independently here
        // too, never added to this.colorGroups.
        this.engravingColorField = this.querySelector('.bpf-engraving-color-field');
        this.engravingColorSelect = this.querySelector('.bpf-engraving-color-select');
        this.hiddenVariantInput = this.querySelector('form input[name="id"]');
        // Phase 5B.1B, Parts 14/15 — captured immediately after caching
        // (before ANY JS mutation), so this is exactly the server-
        // rendered `current_variant.id` — the real variant Shopify's
        // own product-page routing selected (a Google Shopping/variant
        // URL landing, or Shopify's own default). Used only in product
        // mode, to protect against resolveProduct()'s default-variant
        // result silently overwriting it — see
        // protectCurrentVariantInProductMode().
        this.currentVariantId = this.hiddenVariantInput ? this.hiddenVariantInput.value : null;
        this.productForm = this.querySelector('product-form');
        this.submitButton = this.querySelector('[name="add"]');

        // Phase 5A.1, Parts 2/6 — entry context. Read straight off this
        // custom element's own root attributes (data-entry-mode/data-
        // preselected-companion-type/data-preselected-breed, set by
        // sections/bpf-memorial-designer.liquid from THIS product's own
        // metafields when entry_mode is "breed" — see that file's own
        // header comment for why no second breed table exists). 'full'
        // (the Liquid default) means every one of these three fields is
        // a no-op; initBreedEntryContext() below early-returns
        // immediately in that case, so the full Builder's own
        // construction is byte-for-byte unaffected by this addition.
        this.entryMode = this.dataset.entryMode || 'full';
        this.preselectedCompanionType = this.dataset.preselectedCompanionType || null;
        this.preselectedBreed = this.dataset.preselectedBreed || null;
        // Phase 5B.1B — product mode's own preselected values. Unlike
        // breed mode, entryMode === 'product' never needs a bootstrap
        // product (the ambient `product` this section rendered on IS
        // already the real, authoritative landing product), so no
        // corresponding "bootstrap" dataset field exists here.
        this.currentProductStyle = this.dataset.currentProductStyle || null;
        this.currentProductUrl = this.dataset.currentProductUrl || null;

        // Phase 2.2 additions
        this.accordions = Array.from(this.querySelectorAll('.bpf-accordion'));
        // Phase 2.3 addition
        this.journeyItems = Array.from(this.querySelectorAll('.bpf-journey-stepper__step'));
        // Phase 5C.1B — mobile-only persistent canvas + swipe step deck.
        // this.accordionList is the deck's own positioned/clipped
        // container (position:relative + overflow:hidden at mobile only,
        // see assets/bpf-memorial-designer.css) — the SAME element the
        // existing accordion cards already live inside, never a second
        // list. The 6 rail buttons/back button/status line are static,
        // pre-rendered markup (sections/bpf-memorial-designer.liquid) —
        // never built or destroyed at runtime, mirroring how
        // this.journeyItems above is captured once and only ever
        // hidden/relabeled in place.
        this.accordionList = this.querySelector('.bpf-memorial-designer__accordion-list');
        this.mobileNavBack = this.querySelector('[data-mobile-nav-back]');
        // Phase 5C.1D, Part 1/2/4 — the persistent mobile action-dock
        // forward control. Deliberately NOT a second Continue/Add to
        // Cart implementation — see initMobileNav()'s own comment.
        this.mobileNavForward = this.querySelector('[data-mobile-nav-forward]');
        this.mobileNavForwardLabel = this.querySelector('[data-mobile-nav-forward-label]');
        this.mobileNavStatus = this.querySelector('[data-mobile-nav-status]');
        this.mobileNavPoints = Array.from(this.querySelectorAll('[data-mobile-nav-item]'));
        // Phase 5C.1D, Parts 12-14 — mobile-only compact color <select>
        // proxies. Each entry pairs the select with the exact same
        // stepSelector applyLiveColorVariant()/getCanvasState() already
        // use for that color group — never a new/second notion of which
        // DOM step owns which color.
        this.mobileColorSelects = [
          { select: this.querySelector('[data-mobile-color-select="capsule-color"]'), summary: this.querySelector('[data-mobile-color-summary="capsule-color"]'), stepSelector: '[data-step="capsule-color"]' },
          { select: this.querySelector('[data-mobile-color-select="vault-color"]'), summary: this.querySelector('[data-mobile-color-summary="vault-color"]'), stepSelector: '[data-step="vault-color"]' },
          { select: this.querySelector('[data-mobile-color-select="dog-engraving-color"]'), summary: this.querySelector('[data-mobile-color-summary="dog-engraving-color"]'), stepSelector: '[data-step="dog-engraving-color"]' },
        ];
        this.companionNameInput = this.querySelector('.bpf-companion-name-input');
        this.companionNameError = this.querySelector('.bpf-companion-name-error');
        this.stateSelect = this.querySelector('.bpf-state-select');
        this.storyInput = this.querySelector('.bpf-story-input');
        this.storyCounter = this.querySelector('.bpf-story-counter');
        this.photoInput = this.querySelector('.bpf-photo-input');
        this.photoFilename = this.querySelector('.bpf-photo-filename');
        // Phase 4D.10B — Picture Frame add-on, feature-flagged in Liquid
        // via section.settings.enable_picture_frame_addon. Same
        // empty-array/null-when-off pattern as capsuleTypeChildren/
        // capsuleTypeInputs above: when the flag is off, none of this
        // markup exists, so every consumer below (all already guarded on
        // .length/existence) is a guaranteed no-op.
        this.pictureFrameOptInInput = this.querySelector('.bpf-picture-frame-optin-input');
        this.pictureFrameChildren = this.querySelector('[data-picture-frame-children]');
        this.pictureFrameOrientationInputs = Array.from(this.querySelectorAll('.bpf-picture-frame-orientation-input'));
        // Phase 4D.10C, Part 8 — Frame Color is now a single <select>, not
        // an array of option pills (the pill inputs/bindings are removed
        // entirely, not left disabled/unused in the DOM).
        this.pictureFrameColorSelect = this.querySelector('.bpf-picture-frame-color-select');
        // Phase 4D.10E, Part 6 — the Step 4 opt-in's own "(+$price)"
        // suffix, kept in sync with the currently selected Frame Color's
        // real variant price by updatePictureFrameStepPrice().
        this.pictureFramePriceSuffix = this.querySelector('[data-picture-frame-price-suffix]');
        // Phase 4D.10E, Part 31-39 — Picture Frame Details link/modal.
        // Both trigger wrappers (desktop + mobile) so
        // updatePictureFrameDetailsLinkVisibility() can toggle them
        // together; empty array (forEach no-ops) when the feature flag
        // is off, matching every other Picture Frame element cached
        // above. The trigger wrappers themselves are regular descendants
        // of this component (this.querySelectorAll is correct), but the
        // modal's OWN internal elements are not — <modal-dialog>'s
        // connectedCallback (assets/global.js) moves itself to
        // document.body, so this.querySelector would never find them
        // afterward. Queried via the global document, exactly like the
        // pre-existing this.statueConfirmModal = document.querySelector(
        // '[data-bpf-statue-confirm-modal]') a few lines above already
        // does for the same reason.
        this.pictureFrameDetailsTriggerWrappers = Array.from(this.querySelectorAll('[data-picture-frame-details-trigger-wrapper]'));
        this.pictureFrameDetailsImage = document.querySelector('[data-picture-frame-details-image]');
        this.pictureFrameDetailsImageUnavailable = document.querySelector('[data-picture-frame-details-image-unavailable]');
        this.pictureFrameDetailsPrice = document.querySelector('[data-picture-frame-details-price]');
        // Phase 4D.10C, Part 13 — Memorial Wall's own contextual Favorite
        // Photo subsection (Step 5 bottom). Both null when the feature
        // flag is off, matching every other Picture Frame element above.
        this.memorialWallPhotoExisting = this.querySelector('[data-memorial-wall-photo-existing]');
        this.memorialWallPhotoOptional = this.querySelector('[data-memorial-wall-photo-optional]');
        this.photoFilenameWall = this.querySelector('[data-photo-filename-wall]');
        this.memorialWallInputs = Array.from(this.querySelectorAll('.bpf-memorial-wall-input'));
        this.reviewSummary = this.querySelector('[data-review-summary]');

        // Phase 2.3.3 additions
        this.companionTypeInputs = Array.from(this.querySelectorAll('.bpf-companion-type-input'));
        this.companionTypeError = this.querySelector('.bpf-companion-type-error');
        // Phase 5C.1F, Parts 1/2 — mobile-only Companion progressive
        // disclosure. mobileCompanionFieldset is the SAME fieldset
        // (legend + radios) already cached implicitly via
        // companionTypeInputs above — never a second Companion control.
        this.mobileCompanionFieldset = this.querySelector('[data-mobile-companion-fieldset]');
        this.mobileCompanionSummary = this.querySelector('[data-mobile-companion-summary]');
        this.mobileCompanionSummaryLabel = this.querySelector('[data-mobile-companion-summary-label]');
        // Tap-to-re-edit override — cleared the instant the customer
        // makes a real selection again (handleChange()'s own companion-
        // type branch), so compaction resumes normally on their very
        // next pick.
        this._mobileCompanionForceExpanded = false;
        this.stickyNav = this.querySelector('[data-journey-sticky]');
        this.currentStepNumberEl = this.querySelector('[data-current-step-number]');
        this.currentStepTitleEl = this.querySelector('[data-current-step-title]');
        this.currentStepNumberCompactEl = this.querySelector('[data-current-step-number-compact]');
        this.currentStepTitleCompactEl = this.querySelector('[data-current-step-title-compact]');
        this.progressFillEl = this.querySelector('[data-progress-fill]');
        this.progressTrackEl = this.querySelector('[data-progress-track]');
        this.canvasPlaceholder = this.querySelector('[data-canvas-placeholder]');

        // Phase 2.5 additions
        this.breedField = this.querySelector('[data-breed-field]');
        this.breedSelect = this.querySelector('[data-breed-select]');
        this.breedError = this.querySelector('.bpf-breed-error');
        this.resetNotice = this.querySelector('[data-reset-notice]');
        this.styleDetails = this.accordions.find((details) => details.dataset.accordionSection === 'style') || null;
        this.styleUnavailable = this.querySelector('[data-style-unavailable]');
        this.styleOptions = this.querySelector('[data-style-options]');
        // Phase 5B.1B — product mode's own presentational Memorial Style
        // display (current style + counterpart slot). Null in every
        // other entry mode (Liquid never renders this markup outside
        // entry_mode=product), so every consumer below is a no-op there.
        this.productStyleCounterpartWrapper = this.querySelector('[data-product-style-counterpart]');
        this.productStyleCounterpartSlot = this.querySelector('[data-product-style-counterpart-slot]');
        this.memorialWallChildren = this.querySelector('[data-memorial-wall-children]');
        // Phase 2.5.6: the top-of-page price display (data-price-display)
        // was removed along with the host product title — the resolved
        // price now only ever renders here, inside the Review purchase
        // summary. See updatePriceDisplay() for the single place this is
        // written.
        this.reviewPriceValue = this.querySelector('[data-review-price]');
        // Phase 4D.10E, Part 5 — Memorial/Picture Frame Review pricing
        // breakdown rows. Unconditional markup (same as the pre-existing
        // Total row above/below them) — updateReviewPricing() itself is
        // the guard: it only ever unhides these when the feature flag is
        // on AND a frame is actually selected with a resolvable variant.
        this.reviewMemorialRow = this.querySelector('[data-review-memorial-row]');
        this.reviewMemorialPriceValue = this.querySelector('[data-review-memorial-price]');
        this.reviewFrameRow = this.querySelector('[data-review-frame-row]');
        this.reviewFramePriceValue = this.querySelector('[data-review-frame-price]');
        this.reviewDetails = this.accordions.find((details) => details.dataset.accordionSection === 'review') || null;
        this.reviewSummaryToggle = this.querySelector('[data-review-summary-toggle]');

        // Phase 2.5.1 additions
        this.companionNameField = this.querySelector('[data-companion-name-field]');
        this.styleSingleInfo = this.querySelector('[data-style-single-info]');
        this.styleSingleInfoText = this.querySelector('[data-style-single-info-text]');
        // Phase 5C.1B: [data-canvas-mobile-mount] itself is retired along
        // with the Review-specific canvas relocation it existed for — no
        // remaining reference/cache. canvasHoldingMount is untouched;
        // updateProductDetailsPosition()/
        // updateAdditionalPhotosMobileTriggerPosition() still depend on it.
        this.canvasHoldingMount = this.querySelector('[data-canvas-holding-mount]');
        // Cached before updateCanvasPosition() ever runs, so the canvas
        // can always be moved back to its original desktop position — see
        // that method's own comment.
        this._canvasDesktopParent = this.canvasPlaceholder ? this.canvasPlaceholder.parentElement : null;
        this._canvasDesktopNextSibling = this.canvasPlaceholder ? this.canvasPlaceholder.nextElementSibling : null;

        this.eligibilityData = this.parseEligibilityData();
        this.breedsByCompanion = this.buildBreedsByCompanion();
        this.validVariantIds = this.parseValidVariantIds();
        // Phase 3.0: a SEPARATE dataset from eligibilityData, parsed the
        // same way but never merged with it — see resolveVisual()'s own
        // comment for why product resolution and visual resolution stay
        // two independent lookups.
        this.visualMapping = this.parseVisualMapping();
        // Phase 4D.10C, Part 22 — the one authoritative Picture Frame
        // renderer config (aperture geometry + resolved asset URLs per
        // orientation/color), parsed the same defensive way as
        // parseVisualMapping() above. Empty object (never null) when the
        // feature flag is off or the script tag is otherwise absent —
        // every consumer below reads it via
        // this.pictureFrameProfiles[orientation], which is simply
        // undefined in that case, matching the no-op guards already used
        // throughout this feature.
        this.pictureFrameProfiles = this.parsePictureFrameProfiles();
        // Phase 4D.10E — live Picture Frame COMMERCE data (per-color
        // variant id/price/image), parsed the same defensive way as
        // pictureFrameProfiles above but from a SEPARATE script tag
        // (data-picture-frame-commerce) — see parsePictureFrameCommerce()
        // and sections/bpf-memorial-designer.liquid's own bpf_pf_commerce_json
        // header comment. null (never {}) when the product itself
        // doesn't resolve, matching the Liquid side's own "null" literal
        // — every consumer below (resolvePictureFrameVariant()) already
        // treats a null/missing commerce object as "unavailable."
        this.pictureFrameCommerce = this.parsePictureFrameCommerce();
        this.canvasRenderer =
          this.canvasPlaceholder && window.BpfMemorialCanvasRenderer
            ? new window.BpfMemorialCanvasRenderer(this.canvasPlaceholder)
            : null;

        // Phase 5.3.0: Vault-only live engraving preview. A sibling to
        // canvasRenderer above, not a replacement — see assets/bpf-vault-
        // engraving-layer.js's own header for why this is a separate
        // canvas/controller rather than a change to
        // BpfMemorialCanvasRenderer. Constructed here (alongside
        // canvasRenderer, at the same point in the lifecycle) so it is
        // always recreated whenever the canvas itself is.
        //
        // Phase 5C.1F: no longer "desktop only" — see
        // updateEngravingFromState()'s own comment for why the Phase
        // 5.3.1 mobile no-op gate this comment used to describe was a
        // stale assumption from before the canvas became persistent on
        // mobile (Phase 5C.1B).
        this.engravingCanvas = this.querySelector('[data-canvas-engraving]');
        this.engravingLayer =
          this.engravingCanvas && window.BpfVaultEngravingLayer
            ? new window.BpfVaultEngravingLayer(this.engravingCanvas)
            : null;
        // Phase 5C.1F — mobile's own live engraving trigger, debounced
        // (150ms, via the existing generic debounce() helper — same
        // "cancel the pending timer, only the latest call survives"
        // mechanism the resize handler already relies on) since mobile
        // hardware is typically less powerful than the desktop hardware
        // this render() path has always run undebounced/per-keystroke on
        // — desktop's own existing behavior is completely untouched (see
        // updateEngravingFromState()). Created exactly once, here, so
        // the closed-over `timeout` variable actually persists across
        // keystrokes instead of a fresh one being created (and never
        // cancelable) on every call.
        this._debouncedMobileEngravingRender = this.debounce(() => {
          if (this.engravingLayer) this.engravingLayer.render(this.getCanvasState());
        }, 150);

        // Phase 6.3: the Resolved Shopify Product catalog (id -> full
        // Shopify product object — description, images, vendor, type,
        // price range) — parsed once, alongside the other data-*
        // blobs below (eligibility, visual mapping). Kept as its own
        // lookup, never merged into this.state.resolvedProduct, which
        // continues to hold only the ELIGIBILITY resolution result
        // (which variant/product) — see getResolvedShopifyProduct()
        // and syncProductDependentDisclosures() further down for how
        // the two are joined only by productId, on demand, never
        // duplicated together into one object.
        this.shopifyProducts = this.parseShopifyProducts();

        // Phase 6.3: a sibling to engravingLayer above, same
        // reasoning — constructed here so it's always recreated
        // whenever the rest of the Builder's DOM is. Reusable disclosure
        // lifecycle (assets/bpf-disclosure-controller.js); this is the
        // only disclosure instance today, but a future Picture Frame
        // Details/Care Instructions/Shipping Details disclosure would
        // be one more line here, never a change to that file.
        this.productDetailsDisclosureEl = this.querySelector('[data-disclosure="product-details"]');
        this.productDetailsDisclosure =
          this.productDetailsDisclosureEl && window.BpfProductDetailsDisclosure
            ? new window.BpfProductDetailsDisclosure(this.productDetailsDisclosureEl)
            : null;

        // Regression fix (post-Migration, live QA — third pass): the
        // disclosure now follows the exact same mobile lifecycle as the
        // Memorial Canvas placeholder (see updateCanvasPosition()'s own
        // comment) rather than a static mount of its own — same three
        // states: desktop keeps this original position; mobile+Review
        // moves it to data-product-details-review-mount, directly after
        // the canvas inside Review; mobile+anything else moves it into
        // the SAME shared data-canvas-holding-mount the canvas itself
        // uses, never a second, dedicated holding mount. See
        // updateProductDetailsPosition() further down.
        this.productDetailsReviewMount = this.querySelector('[data-product-details-review-mount]');
        this._productDetailsDesktopParent = this.productDetailsDisclosureEl
          ? this.productDetailsDisclosureEl.parentElement
          : null;
        this._productDetailsDesktopNextSibling = this.productDetailsDisclosureEl
          ? this.productDetailsDisclosureEl.nextElementSibling
          : null;

        // Phase 4D.9B.1 — Additional Photos gallery. additionalPhotosMobileTrigger
        // is deliberately a SEPARATE node from productDetailsDisclosureEl,
        // never passed to BpfProductDetailsDisclosure or touched by
        // updateProductDetailsPosition() — see
        // updateAdditionalPhotosMobileTriggerPosition() further down for
        // why it can reuse productDetailsReviewMount/canvasHoldingMount
        // (both already selected above) without modifying that method.
        this.additionalPhotosMobileTrigger = this.querySelector('[data-bpf-additional-photos-mobile-trigger]');
        this.additionalPhotosTriggers = Array.from(this.querySelectorAll('[data-bpf-additional-photos-trigger]'));
        // Phase 4D.10E.4 QA hotfix, Part 6 — the MOBILE Picture Frame
        // Details trigger specifically (distinct from
        // pictureFrameDetailsTriggerWrappers below, which holds BOTH
        // desktop+mobile for show/hide only). Root cause of "mobile
        // Picture Frame Details link missing": this node was never
        // wired into updateAdditionalPhotosMobileTriggerPosition()'s own
        // relocation — it stayed in its static starting position (the
        // same one additionalPhotosMobileTrigger itself would sit in if
        // it were NEVER relocated), which isn't actually visible/
        // discoverable in the real mobile UX at any step; only the
        // relocated mount (productDetailsReviewMount/canvasHoldingMount)
        // is. null on desktop-only renders is impossible (this element
        // is unconditional markup, gated only by the feature flag) —
        // null only when the flag is off, matching the trigger wrappers
        // above.
        this.pictureFrameDetailsMobileTriggerWrapper = this.querySelector('.bpf-view-additional-photos-wrapper--mobile[data-picture-frame-details-trigger-wrapper]');

        this.state = {
          companion: { type: null },
          breed: { value: null },
          style: { eligibleCapsule: null, eligibleVault: null },
          resolvedProduct: null,
          memorialWall: { optIn: null },
          review: { locked: true },
          // Phase 4D.10B — Picture Frame add-on. Deliberately independent
          // of selectedPhotoFile, Memorial Wall, Vault/Capsule/Figure/
          // Engraving Color, and Capsule Type — see
          // resetDownstreamOfCompanion() (unmodified by this phase): a
          // Companion Type change resets the photo but must NOT reset
          // this. orientationWasManuallyChosen mirrors
          // engravingColorManuallyEdited's own "explicit customer choice
          // wins over auto-computed default" shape — see
          // syncPictureFrameOrientationFromPhoto().
          pictureFrame: {
            selected: false,
            orientation: 'Portrait',
            orientationWasManuallyChosen: false,
            color: 'Black',
          },
        };

        // Phase 7H.7 — native Builder journey analytics bookkeeping.
        // this._bpfAnalyticsCurrentStepDetails is the one piece of state
        // this feature adds: "which step did the LAST recorded
        // bpf_builder_step_view belong to," used only to derive
        // step_advance vs step_back direction (see
        // recordBuilderStepActive()) — never read by any non-analytics
        // code, and never influences Builder behavior. The two "fired"
        // flags give bpf_builder_exposure/bpf_builder_review_reached
        // their required once-per-instance dedupe with nothing more
        // than a boolean each.
        this._bpfAnalyticsCurrentStepDetails = null;
        this._bpfAnalyticsExposureFired = false;
        this._bpfAnalyticsReviewReachedFired = false;
        // Phase 7H.7C — events that couldn't reach window.gtag on first
        // attempt (transport not yet defined), retained here so they are
        // never silently dropped — see trackBuilderEvent()'s own comment.
        this._bpfAnalyticsPending = [];
        this._bpfAnalyticsRetryTimer = null;
        this._bpfAnalyticsRetryAttempts = 0;

        this.maxPetNameLength = this.petNameInput
          ? parseInt(this.petNameInput.getAttribute('maxlength'), 10) || 12
          : 12;
        this.maxCompanionNameLength = this.companionNameInput
          ? parseInt(this.companionNameInput.getAttribute('maxlength'), 10) || 12
          : 12;
        this.maxStoryLength = this.storyInput
          ? parseInt(this.storyInput.getAttribute('maxlength'), 10) || 500
          : 500;
        this.selectedPhotoFile = null;
        // Phase 4D.9B.7, Parts 7-11: best-effort early canvas preparation
        // cache — see maybePrepareMemorialCanvas(). _preparedCanvasSignature
        // is the exact canvas-affecting state the (possibly still in-
        // flight) _preparedCanvasBlobPromise was rendered for; both are
        // always overwritten together, synchronously, so a newer prepare
        // call always supersedes an older one before its promise ever
        // resolves — nothing ever reads a stale captured reference.
        this._preparedCanvasSignature = null;
        this._preparedCanvasBlobPromise = null;
        // Phase 4D.10D — early canvas PERSISTENCE (upload), a step beyond
        // the render-only preparation above. See maybePersistMemorialCanvas()
        // for why: the fallback canvas's actual Shopify File upload
        // (uploadBuilderImage(), a real network request) was the
        // remaining click-time cost the 4D.10C.3 prep-only fix didn't
        // address — this caches the resolved GID itself, keyed by the
        // exact signature it was uploaded for, same
        // always-overwritten-together guarantee as the prepared-Blob pair
        // above.
        this._persistedCanvasSignature = null;
        this._persistedCanvasGidPromise = null;
        // Phase 5D.1B — network-request-volume controls.
        // _activeCanvasUploadController and _activePhotoUploadController
        // each track AT MOST one currently-in-flight /apps/memorial/image
        // upload for their own operation (canvas vs. photo are
        // deliberately never shared — see maybePersistMemorialCanvas()/
        // handlePhotoChange()'s own comments): a new attempt aborts
        // whichever one it supersedes before starting, rather than
        // leaving it to complete wastefully. Still needed under 5D.1D's
        // boundary-only persistence model (see maybePersistMemorialCanvas()'s
        // own comment) — a Review-started upload can still be superseded
        // by an Add-to-Cart-time upload for a since-edited signature.
        this._activeCanvasUploadController = null;
        this._activePhotoUploadController = null;
        // Phase 5E.1A — mobile viewport-anchor state. See
        // initMobileViewportAnchor()'s own comment for the full state
        // machine; fields listed here purely so every field this
        // feature ever touches is visible in one place, same convention
        // as every other feature's state above.
        // _viewportAnchorState: 'idle' | 'released' — the only two
        // states actually stored between evaluations (SETTLING is
        // represented by _viewportAnchorSettling being true; ANCHORED
        // is simply "idle and already within tolerance," never a
        // distinct stored state — see evaluateViewportAnchor()).
        this._viewportAnchorState = 'idle';
        this._viewportAnchorSettling = false;
        this._viewportAnchorSettleTimer = null;
        this._viewportAnchorRafId = null;
        this._viewportAnchorScrollListener = null;
        this._viewportAnchorTouchStartListener = null;
        this._viewportAnchorTouchMoveListener = null;
        this._viewportAnchorWheelListener = null;
        this._viewportAnchorInteractionListener = null;
        this._viewportAnchorFocusInListener = null;
        this._viewportAnchorFocusOutListener = null;
        this._viewportAnchorResizeListener = null;
        this._viewportAnchorVisualViewportResizeListener = null;
        this._viewportAnchorFocusActive = false;
        this._viewportAnchorLastInteractionAt = 0;
        this._viewportAnchorHasSeenScroll = false;
        // Phase 5E.1B — true once the shell has genuinely reached
        // BPF_VIEWPORT_ANCHOR_TOLERANCE_PX of the target at least once
        // since the last release; gates the deepened groove behavior
        // (see evaluateViewportAnchor()'s own comment) so it only ever
        // applies after a real settle, never while merely approaching.
        this._viewportAnchorSeated = false;
        // Phase 4D.9B.7, Part 3: the Add to Cart button's own pre-
        // "Preparing your memorial…" text, captured so it can be restored
        // verbatim (whatever it currently says — "Add to Cart" or the
        // sold-out string) rather than hardcoding a value here.
        this._preparingStateOriginalText = null;
        // Phase 2.5.3: the Memorial Wall Pet's Name field is pre-filled
        // from the Vault engraved name, but only until the customer edits
        // it directly — see syncCompanionNameFromEngraved().
        this.companionNameManuallyEdited = false;
        // Engraving Color: the <select> is pre-filled with the resolved
        // effective default (visualAssets.engravingColorDefault, or the
        // selected Dog/Figure/Capsule Color), but only until the customer
        // explicitly changes it — see syncEngravingColorFromDefault().
        // Unlike companionNameManuallyEdited, this also resets on Breed
        // and Memorial Style changes, not only Companion Type — an
        // explicit engraving-color choice is scoped to one product
        // identity, never carried into a different one (see
        // resetEngravingColorOverride()).
        this.engravingColorManuallyEdited = false;

        this.addEventListener('change', this.handleChange.bind(this));
        this.addEventListener('input', this.handleInput.bind(this));

        if (this.petNameInput) {
          this.petNameInput.addEventListener('blur', this.handlePetNameBlur.bind(this));
        }
        if (this.companionNameInput) {
          this.companionNameInput.addEventListener('blur', this.handleCompanionNameBlur.bind(this));
        }
        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.handleSubmitClick.bind(this), true);
        }
        if (this.statueConfirmProceedBtn) {
          this.statueConfirmProceedBtn.addEventListener('click', this.confirmStatueAndProceed.bind(this));
        }
        if (this.statueConfirmBackBtn) {
          this.statueConfirmBackBtn.addEventListener('click', this.cancelStatueConfirm.bind(this));
        }

        // Phase 6.1: must run before initAccordion()/initStepLocks() below
        // so getVisibleSteps()/renumberStepper()'s very first pass already
        // sees the correct hidden state — see the method's own comment.
        this.applyProductDefinitionStepVisibility();
        // Phase 5A.1, Part 6 — same ordering requirement, same reason:
        // must run before initAccordion() so the very first accordion-
        // open/getVisibleSteps() pass already reflects the Companion
        // step being hidden (breed mode) or untouched (full mode).
        this.initBreedEntryContext();
        // Phase 5B.1B — same ordering requirement, same reason, for
        // product mode's own Companion-step hiding + current-style
        // lock + counterpart-link rendering.
        this.initProductEntryContext();

        this.initAccordion();
        this.initContinueButtons();
        this.initReviewLock();
        this.initStepLocks();
        this.initMobileNoCollapse();
        // Phase 5C.1B — mobile Back/rail buttons + swipe gestures. Binds
        // unconditionally (cheap listeners); every handler self-guards on
        // isDesktopViewport() internally, exactly like updateMobileNav()
        // above, so this has no desktop effect.
        this.initMobileNav();
        // Phase 5C.1D — per-stepSelector "customer explicitly changed
        // this color" flags, initialized false (never manually edited
        // yet) exactly once, mirroring engravingColorManuallyEdited's own
        // shape elsewhere in this file.
        this._mobileColorManuallyEdited = {};
        this.initMobileColorSelects();
        // Phase 5C.1F — mobile Companion summary tap-to-re-edit.
        if (this.mobileCompanionSummary) {
          this.mobileCompanionSummary.addEventListener('click', () => {
            this._mobileCompanionForceExpanded = true;
            this.updateMobileCompanionProgression();
            if (this.mobileCompanionFieldset) {
              const companionDetails = this.accordions.find((details) => details.dataset.accordionSection === 'companion');
              if (companionDetails) this.revealInCardQuestion(companionDetails, this.mobileCompanionFieldset);
            }
          });
        }

        this.updateCurrentUrnStyle();
        this.applyPersonalizationVisibility();
        this.updateCapsuleTypeVisibility();
        this.updatePictureFrameVisibility();
        // Phase 4D.10E, Part 6 — initial Step 4 price sync (defaults to
        // Black, the Color <select>'s own default) against the live
        // commerce data, ahead of any customer interaction.
        this.updatePictureFrameStepPrice();
        this.updatePetNameCounter();
        this.updateStoryCounter();
        this.updateMemorialWallChildrenVisibility();
        this.updateMemorialWallPhotoSection();
        this.updateAddToCartState();
        this.updateReviewSummary();
        this.updateJourneyNav();
        this.updateCurrentStepDisplay();
        this.renumberStepper();
        // Phase 5C.1B: initial mobile deck/rail state — self-guarded, a
        // no-op on desktop (see the method's own comment).
        this.updateMobileNav();
        // Phase 5C.1D — initial mobile color-select sync (picks up the
        // Google Shopping landed-color correction from
        // initProductEntryContext() above, if this is product mode).
        this.syncMobileColorSelects();
        // Phase 5C.1F — initial Companion compaction state (a no-op in
        // every real Full-mode load, since nothing is pre-checked; kept
        // for consistency with every other initial-state sync above).
        this.updateMobileCompanionProgression();
        // Phase 3.0: renders the correct initial (neutral) state
        // immediately — before this call the canvas would just be
        // whatever the renderer's own constructor left it as (nothing).
        this.updateCanvasFromState();
      }

      connectedCallback() {
        // Phase 2.3.3: best-effort, theme-scoped newsletter suppression
        // while this component is on the page — see the body-class rule in
        // assets/bpf-memorial-designer.css.
        document.body.classList.add('bpf-memorial-designer-active');
        // Phase 7H.5 — the same class, also on <html>, added/removed here
        // in lockstep with the body one: the Builder-scoped document
        // horizontal-lock rule (assets/bpf-memorial-designer.css) needs
        // to reach the actual document scroll container, which on some
        // engines is <html> rather than <body> — matching both covers
        // either. No separate lifecycle: this is the exact same
        // connectedCallback()/disconnectedCallback() pair the body class
        // already relies on, so it can never go stale independently of it
        // (Theme Editor section reload/unload included — the custom
        // element's disconnectedCallback() fires whenever its node leaves
        // the DOM, and always removes both classes together).
        document.documentElement.classList.add('bpf-memorial-designer-active');

        // Measuring the sticky nav's real height requires it to actually be
        // laid out, which needs the element connected to the DOM — safe to
        // do here, unlike in the constructor. Same reasoning applies to the
        // canvas's initial mobile/desktop position.
        this.updateStickyNavOffset();
        this.updateCanvasPosition();
        this.updateProductDetailsPosition();

        // Phase 5.3.1: the desktop/mobile state as of first connection —
        // used only to detect a later TRANSITION in the resize handler
        // below, never to gate a render by itself (updateEngravingFromState()
        // does that, via isDesktopViewport(), on every call regardless of
        // this value).
        this._engravingWasDesktop = this.isDesktopViewport();

        // Phase 4D.8C: bound once per connection, not in the constructor —
        // this.productForm's own inner <form> is queried fresh here, and
        // the capture-phase listener itself is idempotent-safe to attach
        // again on a reconnect since disconnectedCallback() doesn't need
        // to remove it (it's scoped to `this` and only ever acts on this
        // component's own form).
        //
        // Phase 4D.9B.3: deliberately called BEFORE any Additional Photos
        // gallery init below (moved earlier than in 4D.9B.1) and that
        // gallery init is wrapped in try/catch — this is the proven,
        // purchase-critical Add-to-Cart interception, and it must never be
        // skipped or delayed by a failure in newer, non-critical code that
        // runs later in this same method.
        this.bindPrePurchaseImageUpload();

        try {
          this.updateAdditionalPhotosMobileTriggerPosition();
          this.bindAdditionalPhotosGallery();
        } catch (error) {
          // Never let an Additional Photos gallery failure prevent Add to
          // Cart itself from working — see the module-level comment above.
        }

        // Purchase-flow simplification, Full entry mode ONLY: wires the
        // post-Add-to-Cart add-on popup's cached controls and one-time
        // pub/sub subscriptions. Same try/catch posture as the
        // Additional Photos gallery above — this is new, non-purchase-
        // critical UI; a failure here must never prevent
        // bindPrePurchaseImageUpload() above (and the native Add to
        // Cart it protects) from working. A no-op for every other entry
        // mode.
        try {
          this.bindFullModeAddOnPopup();
        } catch (error) {
          // See above.
        }

        this._handleResize = this.debounce(() => {
          this.updateStickyNavOffset();
          this.updateCanvasPosition();
          this.updateProductDetailsPosition();
          try {
            this.updateAdditionalPhotosMobileTriggerPosition();
          } catch (error) {
            // See the connectedCallback() comment above.
          }
          // Phase 5.3.0/5.3.1: a resize can move the canvas placeholder
          // between mounts (updateCanvasPosition() above) or cross the
          // desktop/mobile breakpoint — syncEngravingViewportTransition()
          // re-asserts correctness on desktop and, specifically on a
          // desktop->mobile transition, invalidates/hides any in-flight
          // engraving work rather than letting it complete and display
          // after the switch.
          this.syncEngravingViewportTransition();
          // Phase 5C.1B — a resize can cross the mobile/desktop
          // breakpoint; re-asserts the deck/rail state (self-guarded
          // no-op on desktop) so rotating a device or leaving the
          // Theme Editor's device preview mid-session never leaves the
          // mobile card-position classes stale.
          this.updateMobileNav();
        }, 150);
        window.addEventListener('resize', this._handleResize);

        // Phase 5E.1A — mobile-only, self-guarded via isDesktopViewport()
        // everywhere it matters; safe to call unconditionally here, same
        // as every other connectedCallback() init above.
        this.initMobileViewportAnchor();

        // Phase 7H.7 — fires bpf_builder_exposure + the first
        // bpf_builder_step_view once this connection is fully settled.
        // Deliberately last: every other piece of state this reads
        // (entryMode, which step is open, resolved companion/breed/
        // style) is already final by this point.
        this.initBuilderAnalytics();
      }

      disconnectedCallback() {
        document.body.classList.remove('bpf-memorial-designer-active');
        // Phase 7H.5 — see the matching connectedCallback() comment: the
        // html class is always removed together with the body one.
        document.documentElement.classList.remove('bpf-memorial-designer-active');

        if (this._handleResize) {
          window.removeEventListener('resize', this._handleResize);
          this._handleResize = null;
        }
        if (this._resetNoticeTimeout) {
          clearTimeout(this._resetNoticeTimeout);
          this._resetNoticeTimeout = null;
        }
        // Phase 7H.7C — stops the bounded analytics retry timer; any
        // still-pending events simply go away with this instance, same
        // as every other piece of in-memory-only Builder state (never
        // persisted across page loads).
        if (this._bpfAnalyticsRetryTimer) {
          clearTimeout(this._bpfAnalyticsRetryTimer);
          this._bpfAnalyticsRetryTimer = null;
        }
        if (this.canvasRenderer) {
          this.canvasRenderer.destroy();
        }
        if (this.engravingLayer) {
          this.engravingLayer.destroy();
        }
        // Phase 5D.1B — stop any NETWORK work this instance still owns
        // (local renderer promises are deliberately left alone — the
        // target here is /apps/memorial/image traffic, not rendering).
        // Without this, a Theme Editor section reload could leave a
        // just-disconnected instance's canvas/photo upload running in
        // the background while a brand-new instance starts its own
        // independent sequence — a real, source-confirmed contributing
        // factor to the 429s this phase addresses (5D.1A, Part 33/34).
        if (this._activeCanvasUploadController) {
          this._activeCanvasUploadController.abort();
          this._activeCanvasUploadController = null;
        }
        if (this._activePhotoUploadController) {
          this._activePhotoUploadController.abort();
          this._activePhotoUploadController = null;
        }
        // Phase 5E.1A — removes every listener/timer/RAF this feature
        // may have added; see the method's own comment.
        this.destroyMobileViewportAnchor();
      }

      debounce(fn, wait) {
        let timeout;
        return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => fn(...args), wait);
        };
      }

      handleChange(event) {
        const target = event.target;

        if (target.classList.contains('bpf-companion-type-input')) {
          // Phase 5C.1F — a real pill tap always resumes normal
          // compaction (clears any "customer tapped the summary to
          // re-edit" override from before).
          this._mobileCompanionForceExpanded = false;
          this.handleCompanionTypeChange(target.value);
          this.updateMobileCompanionProgression();
        }

        if (target === this.breedSelect) {
          this.handleBreedChange();
        }

        // Phase 5C.1G, Parts 1-5 — no prior branch reacted to this
        // input's own change at all (updateCapsuleTypeVisibility()
        // only ever toggles it enabled/disabled from the Vault/Capsule
        // style branch below). Mobile-only, self-guarded reveal — never
        // collapses/hides the pills themselves (Part 3).
        if (target.classList.contains('bpf-capsule-type-input')) {
          this.revealCapsuleTypeHelp();
        }

        if (target.classList.contains('bpf-urn-style-input')) {
          // Engraving Color: an explicit override is scoped to one
          // product identity — a manual Vault<->Capsule switch (both
          // eligible, customer clicks the other radio) starts the newly-
          // active style from its own correct default, exactly like a
          // Companion Type or Breed change already does below. Cleared
          // BEFORE applyPersonalizationVisibility() so the sync it
          // triggers reflects the new style's default immediately, not
          // the just-cleared stale override.
          this.resetEngravingColorOverride();
          this.onUrnStyleChange(event);
          this.updateCurrentUrnStyle();
          this.applyPersonalizationVisibility();
          this.updateCapsuleTypeVisibility();
        }

        if (target === this.engravingColorSelect) {
          this.engravingColorManuallyEdited = true;
        }

        // Regression fix (post-Migration, live QA): the real Vault product
        // has TWO commerce-relevant options — "Color" (Dog & Engraving
        // Color) and "Vault Color" — both participate in resolving the
        // real Shopify variant (confirmed against the live catalog; Dog &
        // Engraving Color is not personalization-only for Vault the way
        // it was under the prototype). Capsule has only "Color". So every
        // color group that can affect commerce for the active style must
        // re-trigger resolution — Capsule Color, Vault Color, AND Dog &
        // Engraving Color. Re-running applyResolvedProduct() for the
        // active style is deliberate rather than a smaller targeted
        // update: it re-resolves the SAME eligibility record (cheap,
        // idempotent) and then re-applies the live variant match against
        // whatever is now checked, so this single call site is correct
        // regardless of whether it fires from a color click or (via
        // applyResolvedProduct itself) a style change — see
        // applyLiveColorVariant()'s own comment for why the eligibility
        // record itself is never mutated. Dog & Engraving Color's
        // properties[Dog & Engraving Color] submission and its effect on
        // the canvas/engraving preview are unchanged by this — it now
        // ALSO affects commerce, it hasn't stopped being personalization.
        if (
          this.currentUrnStyle &&
          (target.closest('[data-step="vault-color"]') ||
            target.closest('[data-step="capsule-color"]') ||
            target.closest('[data-step="dog-engraving-color"]'))
        ) {
          this.applyResolvedProduct(this.currentUrnStyle);
        }

        // Global Text/Engraving Color defaulting rule: whenever the
        // customer actually changes their Pet/Animal Figurine Color —
        // "Choose Pet Color" (dog-engraving-color) for Vault, or Capsule
        // Color for a capsule-surface engraving product — the Engraving
        // Color override (if any) is cleared, so the very next
        // syncEngravingColorFromDefault() call below re-defaults it to
        // match the NEW figure/capsule color rather than leaving a stale
        // manual pick in place. This is the one piece
        // getEffectiveEngravingColorDefault()/syncEngravingColorFromDefault()
        // didn't already do: they compute and apply the correct default,
        // but nothing previously told them the customer's earlier
        // override no longer applies once the figurine color itself has
        // moved. Deliberately excludes Vault Color (a separate, unrelated
        // real Shopify option — the vault CONTAINER's color, never the
        // figurine) — a Vault Color change must never touch Engraving
        // Color. Breed/Style/Companion-Type changes already call
        // resetEngravingColorOverride() themselves (a new product
        // identity's own default), so this is additive, never a
        // duplicate reset.
        if (target.closest('[data-step="capsule-color"]') || target.closest('[data-step="dog-engraving-color"]')) {
          this.resetEngravingColorOverride();
        }

        if (target.classList.contains('bpf-memorial-wall-input')) {
          this.updateMemorialWallChildrenVisibility();
        }

        if (target === this.photoInput) {
          this.handlePhotoChange();
        }

        // Phase 4D.10B — Picture Frame add-on. All three no-op safely
        // when the feature flag is off (the relevant cached element is
        // null/absent, so target can never match/contain it).
        if (target === this.pictureFrameOptInInput) {
          this.state.pictureFrame.selected = Boolean(this.pictureFrameOptInInput.checked);
          this.updatePictureFrameVisibility();
          // Phase 4D.10E, Part 5/6 — Review combined total and the Step 4
          // add-on price both depend on pictureFrame.selected; Orientation
          // deliberately does NOT trigger this (Part 9 — presentation
          // only, never priced).
          this.updateReviewPricing();
        }

        if (target.classList.contains('bpf-picture-frame-orientation-input')) {
          // An explicit tap always wins over auto-detection from here on,
          // exactly like engravingColorManuallyEdited — see
          // syncPictureFrameOrientationFromPhoto().
          this.setPictureFrameOrientation(target.value, { manual: true });
        }

        if (target === this.pictureFrameColorSelect) {
          this.state.pictureFrame.color = target.value;
          // Phase 4D.10E, Part 6/16 — a Frame Color change resolves a
          // different (or no) real variant; Review/Step 4 pricing must
          // reflect the NEWLY selected color immediately, never the
          // previous color's stale price.
          this.updateReviewPricing();
          this.updatePictureFrameStepPrice();
          this.updatePictureFrameDetailsModalContent();
        }

        // Re-syncs the Engraving Color <select> to the current effective
        // default whenever anything that could change it fires a change
        // event (figure/capsule color, style, breed, companion type) —
        // a no-op once the customer has explicitly picked a color
        // themselves (engravingColorManuallyEdited), or while the field
        // isn't currently visible.
        this.syncEngravingColorFromDefault();
        // Phase 5C.1D — keeps the mobile color <select> proxies (and
        // their progressive-disclosure summaries) mirroring whatever
        // just changed the authoritative radios — a real customer pill
        // tap, a style/breed change resetting a color, or the select's
        // own proxy dispatching this exact event.
        this.syncMobileColorSelects();
        this.updateAddToCartState();
        this.updateReviewSummary();
        this.updateCanvasFromState();
        // Phase 4D.10C.3 QA hotfix (Defect 2 — Add to Cart latency
        // regression): maybePrepareMemorialCanvas() used to be triggered
        // ONLY when Review opens, so any canvas-affecting change made
        // AFTER Review had already been opened once (Orientation, Frame
        // Color, or any pre-existing field like Vault Color) left the
        // early-prepared Blob's signature stale — performPrePurchase
        // ImageUpload()'s own signature check correctly detects that
        // mismatch and falls through to a fresh, synchronous-feeling
        // export at CLICK time instead of reusing a cheap cached one.
        // This was always technically possible, but Picture Frame made
        // the fresh-render fallback noticeably slower (now also loading
        // the frame + photo/placeholder images) than it used to be. This
        // call is unconditional here — matching updateCanvasFromState()'s
        // own unconditional call on this exact same line — because the
        // method's own guards (Wall !== Yes, or a customer photo already
        // exists — Priority 1, canvas never used) make it a cheap,
        // immediate no-op for every case that doesn't actually need a
        // prepared fallback canvas. Never introduces a NEW persistence
        // requirement (Part 9/10 — Wall No and customer-photo paths stay
        // exactly as fast as before); it only keeps the ALREADY-existing
        // Wall Yes + no-photo fallback continuously warm instead of only
        // once, at Review-open.
        // Phase 5D.1D — deliberately local-only: this keeps the fallback
        // canvas Blob warm in memory for every qualifying change (zero
        // network cost — see maybePrepareMemorialCanvas()'s own
        // comment), but no longer also triggers a remote
        // /apps/memorial/image upload here. 5D.1B's per-change debounced
        // background upload was proven (5D.1C) to still legitimately
        // exceed the backend's 5-requests/60-minutes-per-IP limiter
        // under ordinary product-comparison behavior — a customer
        // pausing more than the old 750ms debounce between each of
        // several distinct choices produced one real upload per choice,
        // regardless of how short the debounce was. Remote persistence
        // now happens ONLY at the two meaningful boundaries where it's
        // actually needed — Review-open and Add to Cart (see
        // maybePersistMemorialCanvas()'s own comment) — never on every
        // intermediate edit.
        this.maybePrepareMemorialCanvas();
      }

      handleInput(event) {
        const target = event.target;

        if (target === this.petNameInput) {
          this.updatePetNameCounter();
          this.syncCompanionNameFromEngraved();
          // Phase 5.3.0: live engraving preview, engraving layer only
          // (not the full updateCanvasFromState()) — Pet Name changes
          // never affect the base/figure/capsule image layers.
          this.updateEngravingFromState();
        }

        if (target === this.companionNameInput) {
          this.companionNameManuallyEdited = true;
        }

        if (target === this.storyInput) {
          this.updateStoryCounter();
        }

        this.updateAddToCartState();
        this.updateReviewSummary();
      }

      handlePetNameBlur() {
        if (!this.petNameInput) return;
        const trimmed = this.petNameInput.value.trim();
        if (trimmed !== this.petNameInput.value) {
          this.petNameInput.value = trimmed;
          this.updatePetNameCounter();
          this.syncCompanionNameFromEngraved();
        }
        this.updateAddToCartState({ showPetNameError: true });
      }

      handleCompanionNameBlur() {
        if (!this.companionNameInput) return;
        const trimmed = this.companionNameInput.value.trim();
        if (trimmed !== this.companionNameInput.value) {
          this.companionNameInput.value = trimmed;
        }
        this.updateAddToCartState({ showCompanionNameError: true });
      }

      handleSubmitClick(event) {
        if (!this.submitButton) return;
        bpfTimingMark('T0 Add to Cart click');

        // Phase 4D.9B.3, Parts 4-6: unconditionally consumed at the very
        // top of every click, regardless of what happens below — this is
        // the one and only place _statueConfirmBypass is ever read, and
        // it is cleared in the same tick it's read. It is set to true by
        // confirmStatueAndProceed() immediately before that method
        // synthesizes exactly one re-click of this same button to resume
        // the real submission; capturing it into a local here guarantees
        // it can never leak into gating some later, unrelated Add to
        // Cart attempt even if this click turns out to be blocked below
        // for an unrelated reason.
        const isStatueConfirmBypass = this._statueConfirmBypass;
        this._statueConfirmBypass = false;
        // Phase 4D.9B.11: same one-shot, consumed-unconditionally pattern
        // as isStatueConfirmBypass above — see the new branch below for
        // why this exists and what sets it.
        const isMemorialPhotoBypass = this._memorialPhotoBypass;
        this._memorialPhotoBypass = false;

        // Phase 4D.9B.11, Part 6: booleans/state labels only — no
        // filename, photo content, or customer data. Gated the same as
        // every other bpfTimingMark() call; the string is only built
        // when timing is actually enabled.
        if (bpfTimingEnabled()) {
          const photoGidField = this.querySelector('[data-bpf-memorial-photo-gid-field]');
          bpfTimingMark(
            'click state: style=' + (this.currentUrnStyle || 'none') +
              ' capsuleType=' + (typeof this.getCheckedCapsuleType === 'function' ? this.getCheckedCapsuleType() || 'none' : 'n/a') +
              ' statueBypass=' + isStatueConfirmBypass +
              ' photoBypass=' + isMemorialPhotoBypass +
              ' hasPhoto=' + Boolean(this.selectedPhotoFile) +
              ' hasPhotoPromise=' + Boolean(this._photoUploadPromise) +
              ' photoGidPopulated=' + Boolean(photoGidField && photoGidField.value) +
              ' submitDisabled=' + Boolean(this.submitButton.disabled) +
              ' processingActive=' + Boolean(this.submitButton.classList && this.submitButton.classList.contains('loading'))
          );
        }

        // Phase 2.5.2: engraving is optional, but a whitespace-only value
        // (typed but never blurred, e.g. the customer tabs straight to
        // Add to Cart) must never reach FormData — handlePetNameBlur()
        // already does this on blur; this is the same trim applied once
        // more immediately before submission, regardless of whether blur
        // ever fired.
        if (this.petNameInput) {
          const trimmed = this.petNameInput.value.trim();
          if (trimmed !== this.petNameInput.value) {
            this.petNameInput.value = trimmed;
            this.updatePetNameCounter();
            this.syncCompanionNameFromEngraved();
          }
        }

        // Phase 2.5.3: the Memorial Wall Pet's Name field is now required
        // (when visible) rather than optional, but the same trailing/
        // leading-whitespace cleanup applies here too, immediately before
        // submission — handleCompanionNameBlur() already does this on
        // blur.
        if (this.companionNameInput) {
          const trimmedCompanion = this.companionNameInput.value.trim();
          if (trimmedCompanion !== this.companionNameInput.value) {
            this.companionNameInput.value = trimmedCompanion;
          }
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          event.stopPropagation();
          this.updateAddToCartState({
            showPetNameError: true,
            showCompanionNameError: true,
            showCompanionTypeError: true,
            showBreedError: true,
          });

          if (!this.isCompanionTypeValid() && this.companionTypeInputs[0]) {
            this.companionTypeInputs[0].focus();
          } else if (!this.isBreedValid() && this.breedSelect) {
            this.breedSelect.focus();
          } else if (!this.isMemorialStyleValid() && this.getEligibleStyleCount() === 2 && this.urnStyleInputs[0]) {
            this.urnStyleInputs[0].focus();
          } else if (this.currentStyleSupportsEngraving() && this.petNameInput && !this.isPetNameValid()) {
            this.petNameInput.focus();
          } else if (this.companionNameInput && !this.isCompanionNameValid()) {
            this.companionNameInput.focus();
          }
          return;
        }

        // Phase 4D.9B.3, Parts 4-6: the form is valid at this point (the
        // aria-disabled branch above already returned otherwise). If this
        // click is the synthesized resume from confirmStatueAndProceed(),
        // isStatueConfirmBypass is true and the click is left completely
        // alone here — it falls through to the button's own native
        // submit behavior exactly like an ordinary Urn/Vault Add to Cart
        // click, reaching bindPrePurchaseImageUpload()'s submit
        // interception exactly once, through its one normal entry point.
        if (!isStatueConfirmBypass && this.requiresStatueConfirmation()) {
          event.preventDefault();
          event.stopPropagation();
          this.openStatueConfirmModal();
          return;
        }

        // Phase 4D.9B.11: live QA isolated a Wall = Yes + Favorite Photo
        // differential — Capsule Statue (which always resumes via a
        // programmatic submitButton.click() issued from a separate task,
        // after the customer interacts with the confirmation modal)
        // reliably reaches native submission, while a DIRECT real user
        // click for Vault/Urn with the identical Wall = Yes + photo
        // combination does not. No exact browser-level mechanism could
        // be confirmed even with a faithful capture/bubble propagation
        // simulation using real async timing — every reproduction
        // attempted behaves identically for both paths. Rather than
        // continue guessing at an unreproducible live-only mechanism,
        // this converges the direct-click Wall = Yes + photo path onto
        // the exact same "click -> prevent -> one deferred programmatic
        // re-click -> resume" shape already proven reliable for Statue.
        // Statue's own resume already sets isStatueConfirmBypass and
        // skips this branch entirely, so it can never double up with
        // this one. Deliberately scoped to selectedPhotoFile only —
        // Wall = Yes + no photo (canvas) and Wall = No are both already
        // confirmed working via the direct click and are left untouched.
        if (!isStatueConfirmBypass && !isMemorialPhotoBypass && this.selectedPhotoFile && this.requiresMemorialImagePersistence()) {
          event.preventDefault();
          event.stopPropagation();
          this._memorialPhotoBypass = true;
          // A macrotask deferral (not a synchronous nested click) so the
          // re-click happens in a genuinely separate task from this one
          // — mirroring Statue's own resume, which only ever happens
          // after a full task/user-interaction gap, rather than a
          // re-entrant click nested inside this click's own call stack.
          setTimeout(() => {
            if (this.submitButton) this.submitButton.click();
          }, 0);
          return;
        }

        bpfTimingMark('T1 Builder validation complete, native submit proceeding');
      }

      // Phase 4D.9B.3, Part 7 (enable_capsule_type_choice) note: reads
      // this.capsuleTypeInputs.length first, so this is a guaranteed
      // false whenever the feature flag is off (capsuleTypeInputs is
      // empty in that case) — no separate flag check needed, matching
      // every other Capsule Type consumer in this file.
      requiresStatueConfirmation() {
        if (!this.capsuleTypeInputs.length) return false;
        return this.currentUrnStyle === 'Capsule' && this.getCheckedCapsuleType() === 'Memorial Statue';
      }

      openStatueConfirmModal() {
        if (!this.statueConfirmModal) return;
        this.statueConfirmModal.show(this.submitButton);
      }

      // "Confirm Memorial Statue" — closes the modal, arms the narrow
      // one-shot bypass, then synthesizes exactly one click on the real
      // Add to Cart button to resume the exact same submission path a
      // customer who never saw this modal would have taken. Never calls
      // form.requestSubmit()/performPrePurchaseImageUpload() directly —
      // going back through submitButton.click() is what guarantees this
      // reaches bindPrePurchaseImageUpload()'s existing interception
      // exactly once, with Builder/Capsule Type/Memorial Wall state
      // completely untouched.
      confirmStatueAndProceed() {
        if (this.statueConfirmModal) this.statueConfirmModal.hide();
        this._statueConfirmBypass = true;
        if (this.submitButton) this.submitButton.click();
      }

      // "Go Back" — closes the modal and does nothing else. No submit,
      // no bypass flag, no Builder/Capsule Type/Memorial Wall state
      // change, no image upload — the customer is left exactly where
      // they were, free to change Capsule Type (or anything else)
      // before attempting Add to Cart again.
      cancelStatueConfirm() {
        if (this.statueConfirmModal) this.statueConfirmModal.hide();
      }

      // ----- Phase 2.5: eligibility data -----

      parseEligibilityData() {
        const script = this.querySelector('[data-eligibility-data]');
        if (!script) return [];
        try {
          const parsed = JSON.parse(script.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      // Phase 2.5.4: every currently-real variant ID for this product,
      // straight from the same server render as the eligibility data —
      // used by isMemorialStyleValid() to confirm a resolved record's
      // variantId "belongs to a real variant in the current Shopify
      // catalog" without an extra network call. A Set of strings, so
      // comparisons never depend on number/string coercion matching.
      parseValidVariantIds() {
        const script = this.querySelector('[data-valid-variant-ids]');
        if (!script) return null;
        try {
          const parsed = JSON.parse(script.textContent);
          return Array.isArray(parsed) ? new Set(parsed.map((id) => String(id))) : null;
        } catch (error) {
          return null;
        }
      }

      // Phase 6.3: the Resolved Shopify Product catalog — id (as a
      // string, matching JSON object key semantics) -> full Shopify
      // product object. See sections/bpf-memorial-designer.liquid's
      // own comment on bpf_shopify_products_json for exactly what's in
      // each entry and why eligibility (parseEligibilityData() above)
      // never carries this data itself.
      parseShopifyProducts() {
        const script = this.querySelector('[data-shopify-products]');
        if (!script) return {};
        try {
          const parsed = JSON.parse(script.textContent);
          return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch (error) {
          return {};
        }
      }

      // The one place eligibility's resolved productId is joined with
      // the Resolved Shopify Product catalog — never stored together,
      // always looked up fresh. Returns null (not throws, not a
      // partial object) for any productId with no matching catalog
      // entry — e.g. an eligibility test record whose productId isn't
      // mirrored by a real Product Definition yet — so a disclosure
      // fails safe to "no content" exactly like every other missing-
      // data case in this file already does.
      getResolvedShopifyProduct(productId) {
        if (!productId) return null;
        return this.shopifyProducts[String(productId)] || null;
      }

      buildBreedsByCompanion() {
        const map = {};
        this.eligibilityData.forEach((record) => {
          if (!record || !record.breed || record.breed === BPF_GENERIC_KEY) return;
          if (!record.companionType || record.companionType === BPF_GENERIC_KEY) return;
          if (!map[record.companionType]) map[record.companionType] = [];
          if (map[record.companionType].indexOf(record.breed) === -1) {
            map[record.companionType].push(record.breed);
          }
        });
        return map;
      }

      findEligibilityRecord(companionType, breed, style) {
        return (
          this.eligibilityData.find(
            (record) =>
              record.companionType === companionType && record.breed === breed && record.memorialStyle === style
          ) || null
        );
      }

      // SINGLE authoritative product-resolution path (see the file header's
      // architecture overview) — every caller that needs a resolved
      // product goes through this method, never a parallel lookup.
      //
      // Approved 3-tier precedence: exact breed → companion-generic breed →
      // globally-generic. A disabled record with allowFallback:true lets
      // the search continue; disabled with allowFallback:false (or absent)
      // is a hard stop — never silently falls through to a generic product.
      // An ENABLED record that's missing a field required for safe cart
      // submission/display (isRecordComplete()) is ALSO a hard stop, not a
      // silent fall-through — presenting a different (possibly wrong)
      // generic product when the intended specific mapping is broken would
      // mask a real catalog data problem rather than surface it.
      resolveProduct(companionType, breed, style) {
        if (!companionType || !style) return null;
        const effectiveBreed = breed || BPF_GENERIC_KEY;
        const tiers = [
          { companionType, breed: effectiveBreed },
          { companionType, breed: BPF_GENERIC_KEY },
          { companionType: BPF_GENERIC_KEY, breed: BPF_GENERIC_KEY },
        ];

        for (let i = 0; i < tiers.length; i += 1) {
          const record = this.findEligibilityRecord(tiers[i].companionType, tiers[i].breed, style);
          if (!record) continue;
          if (record.enabled) return this.isRecordComplete(record) ? record : null;
          if (record.allowFallback) continue;
          return null;
        }
        return null;
      }

      // A record is only safe to resolve to if every field required for
      // accurate cart submission and customer-facing display is present —
      // otherwise it must never be treated as a valid resolution, per the
      // "never submit a partial or stale selection" requirement.
      isRecordComplete(record) {
        return Boolean(
          record &&
            record.productId &&
            record.variantId &&
            record.price &&
            typeof record.available === 'boolean'
        );
      }

      getEligibleStyleCount() {
        return (this.state.style.eligibleCapsule ? 1 : 0) + (this.state.style.eligibleVault ? 1 : 0);
      }

      // Restores each radio's original (Liquid-rendered) disabled state
      // rather than unconditionally clearing it — see the Map built in the
      // constructor. Used wherever the single-eligible auto-select's own
      // forced-disable (updateStyleCardVisibility()) needs to be undone.
      restoreUrnStyleOriginalDisabled() {
        this.urnStyleInputs.forEach((input) => {
          input.disabled = this._urnStyleOriginalDisabled.get(input) || false;
        });
      }

      // ----- Phase 2.5: Companion Type -----

      handleCompanionTypeChange(newType) {
        const previousType = this.state.companion.type;
        const isGenuineChange = previousType !== null && previousType !== newType;

        this.state.companion.type = newType;

        if (isGenuineChange) {
          this.resetDownstreamOfCompanion();
        }

        this.updateBreedVisibility();
        this.state.breed.value = null;

        this.recomputeEligibility();

        if (isGenuineChange) {
          this.showResetNotice();
        }
      }

      resetDownstreamOfCompanion() {
        this.clearStyleChecked();
        this.state.resolvedProduct = null;
        this.updatePriceDisplay(null);
        this.syncProductDependentDisclosures(null);
        if (this.styleDetails) {
          this.styleDetails.open = false;
          this.styleDetails.hidden = false;
          this.styleDetails.classList.remove('is-visited');
        }
        if (this.styleUnavailable) this.styleUnavailable.hidden = true;
        if (this.styleOptions) this.styleOptions.hidden = false;
        if (this.styleSingleInfo) this.styleSingleInfo.hidden = true;
        this.restoreUrnStyleOriginalDisabled();

        this.colorGroups.forEach((group) => {
          const whiteInput = group.querySelector('input[value="White"]');
          group.querySelectorAll('input').forEach((input) => {
            input.checked = false;
          });
          if (whiteInput) whiteInput.checked = true;
        });
        if (this.petNameInput) this.petNameInput.value = '';
        if (this.companionNameInput) {
          this.companionNameInput.value = '';
          this.companionNameManuallyEdited = false;
        }
        this.resetEngravingColorOverride();
        this.setAccordionUnvisited('personalize');

        if (this.photoInput) this.photoInput.value = '';
        this.selectedPhotoFile = null;
        // Phase 4D.9B.9, Part 12: a Companion Type change clears the
        // selected photo, but was leaving _photoUploadPromise pointing at
        // whatever upload (if any) was previously in flight — harmless
        // today only because performPrePurchaseImageUpload() gates on
        // selectedPhotoFile first, but a stale reference here is still
        // the wrong invariant (handlePhotoChange()'s own "no file"
        // branch already clears both together). Cleared here too so the
        // two fields can never disagree about "is there an eager photo
        // upload relevant to the current selection."
        this._photoUploadPromise = null;
        this.setBuilderImageGidField('photo', '');
        if (this.photoFilename) this.photoFilename.textContent = 'No photo selected';
        // Phase 4D.10C: keeps Step 5's own filename echo and its
        // existing-vs-optional contextual messaging in sync with the
        // photo reset above — this.selectedPhotoFile is already null by
        // this point, so updateMemorialWallPhotoSection() correctly
        // switches back to the "Optional" case. The Picture Frame add-on's
        // own opt-in/orientation/color state is deliberately NOT touched
        // anywhere in this method — see the state object's own comment at
        // its declaration (near this.state's initialization) for why.
        if (this.photoFilenameWall) this.photoFilenameWall.textContent = 'No photo selected';
        this.updateMemorialWallPhotoSection();
        this.setAccordionUnvisited('photo');

        this.memorialWallInputs.forEach((input) => {
          input.checked = input.value === 'Yes';
        });
        if (this.storyInput) this.storyInput.value = '';
        if (this.stateSelect) this.stateSelect.value = '';
        this.updateMemorialWallChildrenVisibility();
        this.setAccordionUnvisited('memorial-wall');

        this.setAccordionUnvisited('review');

        this.updatePetNameCounter();
        this.updateStoryCounter();
      }

      setAccordionUnvisited(key) {
        const details = this.accordions.find((item) => item.dataset.accordionSection === key);
        if (!details) return;
        details.open = false;
        details.classList.remove('is-visited');
      }

      showResetNotice() {
        if (!this.resetNotice) return;
        this.resetNotice.hidden = false;
        if (this._resetNoticeTimeout) clearTimeout(this._resetNoticeTimeout);
        this._resetNoticeTimeout = setTimeout(() => {
          if (this.resetNotice) this.resetNotice.hidden = true;
        }, 6000);
      }

      // ----- Phase 2.5: Breed (child question) -----

      updateBreedVisibility() {
        if (!this.breedField || !this.breedSelect) return;
        const companionType = this.state.companion.type;
        const breeds = companionType ? this.breedsByCompanion[companionType] : null;
        const isVisible = Boolean(breeds && breeds.length);

        if (isVisible) {
          this.renderBreedOptions(breeds);
        }

        this.breedField.hidden = !isVisible;
        this.breedSelect.disabled = !isVisible;
      }

      renderBreedOptions(breeds) {
        if (!this.breedSelect) return;
        this.breedSelect.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select a breed';
        this.breedSelect.appendChild(placeholder);

        breeds
          .slice()
          .sort((a, b) => a.localeCompare(b))
          .forEach((breed) => {
            const option = document.createElement('option');
            option.value = breed;
            option.textContent = breed;
            this.breedSelect.appendChild(option);
          });

        this.breedSelect.value = '';
      }

      isBreedRequired() {
        return Boolean(this.breedField && !this.breedField.hidden);
      }

      isBreedValid() {
        if (!this.isBreedRequired()) return true;
        return Boolean(this.breedSelect && this.breedSelect.value);
      }

      handleBreedChange() {
        this.state.breed.value = (this.breedSelect && this.breedSelect.value) || null;
        // Engraving Color: a new breed is a new product identity with its
        // own correct default (e.g. Pitbull Vault's Pink override must
        // not persist onto Dog Bone Vault, which defaults to Black) —
        // cleared before recomputeEligibility() so the
        // applyPersonalizationVisibility() it triggers syncs the new
        // breed's own default immediately.
        this.resetEngravingColorOverride();

        const styleBefore = this.getCheckedStyle();
        this.recomputeEligibility();
        const styleAfter = this.getCheckedStyle();

        if (styleBefore && styleAfter && styleBefore !== styleAfter) {
          this.clearInactiveStyleFields(styleBefore);
        }
      }

      // ----- Phase 2.5: Memorial Style resolution -----

      getCheckedStyle() {
        const checked = this.urnStyleInputs.find((input) => input.checked);
        return checked ? checked.value : null;
      }

      setStyleChecked(style) {
        this.urnStyleInputs.forEach((input) => {
          input.checked = input.value === style;
        });
      }

      clearStyleChecked() {
        this.urnStyleInputs.forEach((input) => {
          input.checked = false;
        });
      }

      recomputeEligibility() {
        const companionType = this.state.companion.type;
        const breedRequired = this.isBreedRequired();
        // A required-but-unanswered Breed must NOT fall through to the
        // generic-breed tier — that would let e.g. a Dog selection with no
        // Breed chosen yet silently auto-resolve (and auto-unlock Review)
        // off a generic Dog product before the customer has actually
        // answered a field the Companion card's own Continue gate already
        // requires. Only resolve once Breed is genuinely answered (or was
        // never required in the first place, e.g. Other Pet).
        const breedAnswered = !breedRequired || Boolean(this.state.breed.value);
        const breed = breedRequired ? this.state.breed.value : null;

        const capsule = companionType && breedAnswered ? this.resolveProduct(companionType, breed, 'Capsule') : null;
        const vault = companionType && breedAnswered ? this.resolveProduct(companionType, breed, 'Vault') : null;

        this.state.style.eligibleCapsule = capsule;
        this.state.style.eligibleVault = vault;

        const eligible = [];
        if (capsule) eligible.push('Capsule');
        if (vault) eligible.push('Vault');

        if (eligible.length === 1) {
          this.setStyleChecked(eligible[0]);
          this.applyResolvedProduct(eligible[0]);
        } else if (eligible.length === 2) {
          const currentlyChecked = this.getCheckedStyle();
          if (currentlyChecked && eligible.indexOf(currentlyChecked) !== -1) {
            this.applyResolvedProduct(currentlyChecked);
          } else if (this.entryMode !== 'product') {
            // Phase 7H.1 Part C — Vault-first default. Only reached when
            // the Builder is resolving style itself (Full/Breed mode):
            // Product mode always pre-checks its landed style BEFORE
            // recomputeEligibility() ever runs (initProductEntryContext()
            // sets a real radio's .checked, then calls handleBreedChange()
            // -> recomputeEligibility()), so it always hits the branch
            // above instead — this branch is structurally unreachable in
            // Product mode, and the explicit entryMode guard here is
            // belt-and-suspenders, matching the same guard
            // initProductEntryContext() itself uses, per this phase's own
            // explicit "Product Mode remains authoritative" requirement.
            // Runs through the exact same real state-transition path a
            // manual click uses (setStyleChecked() + applyResolvedProduct()
            // — the identical pair the eligible.length===1 branch above
            // already uses for its own single-style auto-select), so
            // selected style/product/variant/resolver state/canvas/
            // Review/Add to Cart properties are all updated correctly,
            // not just a CSS class toggle. Vault is always chosen here
            // over Capsule since this branch is eligible.length===2 —
            // both are eligible, and Vault is priority 1.
            this.setStyleChecked('Vault');
            this.applyResolvedProduct('Vault');
          } else {
            this.clearStyleChecked();
            this.state.resolvedProduct = null;
            this.updatePriceDisplay(null);
            this.syncProductDependentDisclosures(null);
          }
        } else {
          this.clearStyleChecked();
          this.state.resolvedProduct = null;
          this.updatePriceDisplay(null);
          this.syncProductDependentDisclosures(null);
        }

        this.updateStyleCardVisibility(eligible.length);
        this.updateCurrentUrnStyle();
        this.applyPersonalizationVisibility();
        this.updateCapsuleTypeVisibility();
        this.renumberStepper();
        // Phase 5C.1B: eligibility recompute can change which step is
        // "next" (Style auto-skip) without any accordion toggle firing —
        // same reasoning as renumberStepper() itself needing this call
        // site.
        this.updateMobileNav();
        this.updateAddToCartState();
        this.updateReviewSummary();
      }

      // Phase 2.5.1: the card itself is never hidden anymore (eligibleCount
      // === 1 used to hide it entirely) — it always stays step 2 of 6, so
      // getVisibleSteps()/renumberStepper() never need to special-case it.
      // Only which of the three mutually-exclusive bodies is shown changes:
      // the free-choice radios (2 eligible), an informational message with
      // the radios disabled (1 eligible, auto-selected), or the
      // "unavailable" message (0 eligible).
      updateStyleCardVisibility(eligibleCount) {
        if (!this.styleDetails) return;
        this.styleDetails.hidden = false;

        // Phase 5B.1B — product mode has its own dedicated presentation
        // (data-product-style-display, rendered by renderProductStyle
        // Counterpart()) that already communicates "current style +
        // counterpart availability" in product-mode-specific language.
        // The breed-flavored single-info/unavailable copy below
        // ("This companion is currently available in...") doesn't fit a
        // product landing page and would show alongside/instead of it,
        // so both are suppressed here — the underlying eligibility
        // state (state.style.eligibleVault/eligibleCapsule) driving
        // them is completely unaffected.
        const suppressStyleMessaging = this.entryMode === 'product';

        if (eligibleCount === 1) {
          if (this.styleUnavailable) this.styleUnavailable.hidden = true;
          if (this.styleOptions) this.styleOptions.hidden = true;
          if (this.styleSingleInfo && !suppressStyleMessaging) {
            const styleName = this.getCheckedStyle();
            if (this.styleSingleInfoText) {
              this.styleSingleInfoText.textContent = `This companion is currently available in ${styleName}.`;
            }
            this.styleSingleInfo.hidden = false;
          } else if (this.styleSingleInfo) {
            this.styleSingleInfo.hidden = true;
          }
          this.urnStyleInputs.forEach((input) => {
            input.disabled = true;
          });
        } else if (eligibleCount === 0) {
          if (this.styleUnavailable) this.styleUnavailable.hidden = suppressStyleMessaging ? true : false;
          if (this.styleOptions) this.styleOptions.hidden = true;
          if (this.styleSingleInfo) this.styleSingleInfo.hidden = true;
        } else {
          if (this.styleUnavailable) this.styleUnavailable.hidden = true;
          if (this.styleOptions) this.styleOptions.hidden = false;
          if (this.styleSingleInfo) this.styleSingleInfo.hidden = true;
          this.restoreUrnStyleOriginalDisabled();
        }

        // Phase 2.5.2: recompute here too (not just from
        // applyPersonalizationVisibility()) — this is the earliest point a
        // style resolution (including the single-eligible auto-select) can
        // change whether Vault is active, so the Memorial Wall Pet's Name
        // field's visibility is never stale even for one tick.
        this.updateMemorialWallPetNameVisibility();
      }

      // Phase 2.5.2: completeness now requires more than "a radio is
      // checked" — Step 2 (and everything gated behind it: Steps 3-6, via
      // updateStepLocks()/Review's own lock) must never be reachable with
      // an unresolved or mismatched variant. A style radio being checked
      // with no corresponding complete this.state.resolvedProduct, or a
      // resolved product whose variantId doesn't match what's actually in
      // the hidden `id` input, both fail this check — "Companion complete"
      // isn't checked separately here because it's already implied:
      // resolveProduct() can't return anything without a companion type,
      // so getEligibleStyleCount() would already be 0 without one.
      // Phase 2.5.4: three more checks close the "Cannot find variant"
      // failure class specifically — a resolved-but-out-of-stock record
      // (record.available === false) no longer counts as complete; the
      // hidden input must actually be enabled (product-form.js normally
      // guarantees this, but it's now verified rather than assumed); and
      // the resolved variantId must appear in validVariantIds — a list
      // built from this exact product's real variants at the same render
      // as the eligibility data, so a resolver record can never point at
      // an ID Shopify's own cart API wouldn't recognize as belonging to
      // this product.
      isMemorialStyleValid() {
        if (!this.styleDetails) return true; // product.has_only_default_variant — no style step at all
        if (this.getEligibleStyleCount() === 0) return false;
        if (!this.getCheckedStyle()) return false;

        const record = this.state.resolvedProduct;
        if (!record || !this.isRecordComplete(record)) return false;
        if (record.available !== true) return false;
        if (!this.hiddenVariantInput || this.hiddenVariantInput.disabled) return false;
        if (this.hiddenVariantInput.value !== String(record.variantId)) return false;
        if (this.validVariantIds && !this.validVariantIds.has(String(record.variantId))) return false;

        return true;
      }

      // Applies the resolver's record for the given style to the DOM:
      // hidden variant id (cart submission target), URL param, price
      // display, and availability.
      //
      // In normal operation `record` is always present and complete here —
      // this is only ever called (from recomputeEligibility() or a manual
      // style click) for a style that was just confirmed resolvable, and
      // resolveProduct() only returns complete records (see
      // isRecordComplete()). The `if (!record)` branch below is a
      // defensive guard for that "should be unreachable" case, and
      // deliberately does NOT fall back to the style radio's own static
      // data-variant-id/data-available attributes — silently substituting
      // that data would mean displaying/submitting a variant the resolver
      // itself couldn't confirm, exactly the "stale/partial selection"
      // failure mode this phase's safety review flagged.
      // Phase 2.5.2: this branch now also surfaces the existing
      // "unavailable" UI immediately (rather than only disabling Add to
      // Cart with no visible explanation) and explicitly clears the hidden
      // variant input rather than ever leaving a stale value from a
      // previous resolution sitting there — belt-and-suspenders, since
      // isMemorialStyleValid() already independently blocks progression
      // via `record`/hidden-input checks regardless of what this input
      // holds.
      // Phase 2.5.4: the same "unavailable" treatment now also applies
      // when a record IS complete but not actually available, or its
      // variantId isn't present in validVariantIds (a real record
      // pointing at a variant Shopify's own catalog wouldn't recognize) —
      // see the isMemorialStyleValid() checks this mirrors.
      applyResolvedProduct(styleName) {
        const companionType = this.state.companion.type;
        const breed = this.isBreedRequired() ? this.state.breed.value : null;
        const baseRecord = this.resolveProduct(companionType, breed, styleName);
        const record = this.applyLiveColorVariant(baseRecord, styleName);

        this.state.resolvedProduct = record;

        const variantIdString = record ? String(record.variantId) : null;
        const knownValid = !this.validVariantIds || (Boolean(variantIdString) && this.validVariantIds.has(variantIdString));

        // Phase 2.5.4: a record that's resolved and "complete" (all fields
        // present) but not actually available, or whose variantId isn't in
        // the server-verified validVariantIds list (see
        // parseValidVariantIds()), is treated exactly like an unresolved
        // record — never submit or display an ID that isn't a genuine,
        // currently-available variant of this product.
        if (!record || record.available !== true || !knownValid) {
          this.setHiddenVariantId('');
          this.updateAddToCartAvailability(false);
          this.updatePriceDisplay(null);
          this.syncProductDependentDisclosures(null);
          this.updateStyleCardVisibility(0);
          return;
        }

        this.setHiddenVariantId(record.variantId);
        this.updateUrlVariantParam(record.variantId);
        this.clearFormError();
        this.updateAddToCartAvailability(record.available);
        this.updatePriceDisplay(record);
        this.syncProductDependentDisclosures(record);
      }

      // Finds a live Shopify option's real position (1/2/3) by its exact
      // name, from the Resolved Shopify Product catalog's own embedded
      // options (name/position/values — see bpf_vault_options_json/
      // bpf_capsule_options_json in sections/bpf-memorial-designer.liquid).
      // Never assumes option1/option2 — the two real products don't share
      // the same option layout (Capsule has one option, "Color"; Vault has
      // two, "Color" and "Vault Color", in whichever order Shopify
      // actually returns them), and hardcoding a position was exactly the
      // bug the live QA regression traced back to.
      findOptionPosition(shopifyProduct, optionName) {
        if (!shopifyProduct || !Array.isArray(shopifyProduct.options)) return null;
        const option = shopifyProduct.options.find((o) => o.name === optionName);
        return option ? option.position : null;
      }

      // Reads a variant's value at a given live option position (1/2/3).
      getVariantValueAtPosition(variant, position) {
        if (!position) return undefined;
        return variant['option' + position];
      }

      // Regression fix (post-Migration, live QA): given an eligibility
      // record and the style it belongs to, re-resolves variantId/price/
      // available against the LIVE colors currently selected for that
      // style, using the Resolved Shopify Product catalog's own real
      // variant list and real option positions — eligibility's own
      // baked-in value (the Liquid-resolved default) is only ever the
      // starting point before a customer has made a color choice yet.
      //
      // Capsule is single-axis: one real option, "Color", one customer
      // input (Capsule Color) — the two already coincide.
      //
      // Vault is two-axis: the live catalog confirmed TWO real options,
      // "Color" (mapped from the Dog & Engraving Color control) and
      // "Vault Color" (mapped from the Vault Color control) — both
      // customer selections are required together to resolve one exact
      // variant. Dog & Engraving Color continues to also drive its
      // existing properties[Dog & Engraving Color] submission and the
      // canvas/engraving preview, unchanged — this method only adds a
      // second consumer of that same selection, it doesn't take anything
      // away from the first.
      //
      // Every path requires EXACTLY one matching variant. Zero matches or
      // more than one are both treated as unavailable — never the first
      // match, never a guess, never a stale previous variant, and the
      // product/category "Color" metafield is never consulted here at
      // all — only the live variants' own option values are authoritative
      // for what gets purchased.
      //
      // Returns a NEW object; record itself is a live reference into
      // this.eligibilityData (see findEligibilityRecord()) and must never
      // be mutated — doing so would corrupt future resolutions of the same
      // companion/breed/style with whatever colors happened to be picked
      // last. Records with no live Shopify product/variant data (every
      // eligibility row except the two real Pitbull ones today) pass
      // through completely unchanged — this only ever narrows an
      // already-resolved record, never widens or invents one.
      //
      // styleName is passed explicitly rather than read from
      // this.currentUrnStyle because this method runs from inside
      // applyResolvedProduct(), which can be called before
      // updateCurrentUrnStyle() has run for a just-changed style radio —
      // reading the instance property here could see the previous style.
      applyLiveColorVariant(record, styleName) {
        if (!record) return record;

        const shopifyProduct = this.getResolvedShopifyProduct(record.productId);
        if (!shopifyProduct || !Array.isArray(shopifyProduct.variants)) return record;

        let matches;

        if (styleName === 'Capsule') {
          const colorPosition = this.findOptionPosition(shopifyProduct, 'Color');
          const chosenColor = this.getCheckedValue(this.querySelector('[data-step="capsule-color"] input:checked'));
          if (!colorPosition || !chosenColor) return record;

          matches = shopifyProduct.variants.filter(
            (v) => this.getVariantValueAtPosition(v, colorPosition) === chosenColor
          );
        } else if (styleName === 'Vault') {
          const dogColorPosition = this.findOptionPosition(shopifyProduct, 'Color');
          const vaultColorPosition = this.findOptionPosition(shopifyProduct, 'Vault Color');
          const chosenDogColor = this.getCheckedValue(
            this.querySelector('[data-step="dog-engraving-color"] input:checked')
          );
          const chosenVaultColor = this.getCheckedValue(this.querySelector('[data-step="vault-color"] input:checked'));
          if (!dogColorPosition || !vaultColorPosition || !chosenDogColor || !chosenVaultColor) return record;

          matches = shopifyProduct.variants.filter(
            (v) =>
              this.getVariantValueAtPosition(v, dogColorPosition) === chosenDogColor &&
              this.getVariantValueAtPosition(v, vaultColorPosition) === chosenVaultColor
          );
        } else {
          return record;
        }

        // Zero matches (missing color/spelling mismatch) or more than one
        // (a catalog integrity problem — two variants sharing the same
        // option values) are both unavailable, never a guess and never
        // the previous record's stale variant.
        if (matches.length !== 1) {
          return { ...record, variantId: null, price: null, priceCents: null, available: false };
        }

        const variant = matches[0];
        return {
          ...record,
          variantId: variant.id,
          price: variant.priceFormatted,
          // Phase 4D.10E: additive — priceCents (an Integer, from the
          // embedded catalog JSON's own `| times: 100 | round`) feeds the
          // new Review combined-total math; `price` (the pre-formatted
          // display string) is completely unchanged and still the only
          // thing updatePriceDisplay() shows on its own.
          priceCents: typeof variant.priceCents === 'number' ? variant.priceCents : null,
          available: variant.available,
        };
      }

      // Clears only the given (now-inactive) style's own colors/engraved
      // name — used exclusively for the breed-driven automatic style
      // change case (see handleBreedChange()), never for a manual radio
      // click, which keeps its existing hide+disable-only behavior.
      clearInactiveStyleFields(inactiveStyle) {
        const key = inactiveStyle.toLowerCase();
        this.colorGroups.forEach((group) => {
          if (group.dataset.appliesTo !== key) return;
          const whiteInput = group.querySelector('input[value="White"]');
          group.querySelectorAll('input').forEach((input) => {
            input.checked = false;
          });
          if (whiteInput) whiteInput.checked = true;
        });
        if (key === 'vault') {
          if (this.petNameInput) {
            this.petNameInput.value = '';
            this.updatePetNameCounter();
          }
          // Phase 2.5.3: the Memorial Wall Pet's Name field is no longer
          // cleared here — it's an independent value the customer may
          // have deliberately typed (or edited away from the engraved
          // name) on the Memorial Wall card, unrelated to which style is
          // currently active. Only the engraved name itself, which is
          // genuinely Vault-only, clears when Vault becomes inactive.
        }
      }

      // Phase 2.5.6: writes the resolved price into the Review purchase
      // summary only — this is the one place in the builder the exact
      // resolved price is ever shown. The em dash fallback covers the
      // "should be unreachable" case (Review is locked until a complete,
      // valid record exists — see isMemorialStyleValid()) without ever
      // falling back to the hero's static starting-price copy or any host
      // product price.
      updatePriceDisplay(record) {
        if (!this.reviewPriceValue) return;
        this.reviewPriceValue.textContent = record && record.price ? record.price : '—';
        // Phase 4D.10E, Part 5 — reuses this method's own existing call
        // sites (the complete, already-authoritative set of every place
        // this.state.resolvedProduct is mutated — same "one generic
        // hook" reasoning syncProductDependentDisclosures() documents
        // above) rather than introducing a second notion of "the
        // resolved product changed." updateReviewPricing() re-reads
        // this.state.resolvedProduct itself rather than trusting
        // `record`, since it also needs to react to Picture Frame state
        // changes that never pass through here at all (see the
        // pictureFrameOptInInput/pictureFrameColorSelect change
        // handlers, which call it directly).
        this.updateReviewPricing();
      }

      // Phase 4D.10E, Parts 5/6/17 — the ONE place the Review purchase
      // summary's Memorial/Picture Frame breakdown and combined Total
      // are computed. Reads this.state.resolvedProduct (Memorial) and
      // this.state.pictureFrame (frame selection) fresh every call —
      // never trusts a stale snapshot. Three outcomes, exactly mirroring
      // the "graceful, never a fake number" contract every other
      // resolved-price display in this file already follows:
      //
      //   1. Feature off, or frame not selected: both breakdown rows
      //      stay/become hidden, data-review-price is left completely
      //      alone (updatePriceDisplay() above already just set it to
      //      the Memorial-only price) — byte-identical to pre-4D.10E
      //      behavior for every case that doesn't involve a frame.
      //   2. Frame selected but its variant/price can't be resolved
      //      (unresolvable product, unmapped color, missing priceCents)
      //      — same as (1): never show a wrong/guessed combined total.
      //   3. Frame selected AND resolvable: both rows show, Memorial row
      //      gets the plain resolved Memorial price, Picture Frame row
      //      gets "+" + the resolved variant's own priceFormatted, and
      //      data-review-price is OVERWRITTEN with the combined total —
      //      integer-cents addition (never string/float parsing) fed
      //      through formatMoney() against the shop's real money_format.
      updateReviewPricing() {
        if (!this.reviewMemorialRow || !this.reviewFrameRow) return;

        const record = this.state.resolvedProduct;
        const memorialCents = record && typeof record.priceCents === 'number' ? record.priceCents : null;
        const frameSelected = Boolean(this.pictureFrameOptInInput && this.state.pictureFrame && this.state.pictureFrame.selected);
        const frameVariant = frameSelected ? this.resolvePictureFrameVariant(this.state.pictureFrame.color) : null;

        const canShowBreakdown =
          frameSelected &&
          frameVariant &&
          typeof frameVariant.priceCents === 'number' &&
          memorialCents !== null &&
          record &&
          record.price;

        if (!canShowBreakdown) {
          this.reviewMemorialRow.hidden = true;
          this.reviewFrameRow.hidden = true;
          return;
        }

        if (this.reviewMemorialPriceValue) this.reviewMemorialPriceValue.textContent = record.price;
        if (this.reviewFramePriceValue) this.reviewFramePriceValue.textContent = '+' + frameVariant.priceFormatted;
        this.reviewMemorialRow.hidden = false;
        this.reviewFrameRow.hidden = false;

        const combinedFormatted = this.formatMoney(memorialCents + frameVariant.priceCents);
        if (combinedFormatted && this.reviewPriceValue) {
          this.reviewPriceValue.textContent = combinedFormatted;
        }
      }

      // Phase 6.3: the one generic hook every product-dependent disclosure
      // syncs through. Reuses updatePriceDisplay()'s own call sites — the
      // already-complete, already-authoritative set of every place
      // this.state.resolvedProduct is mutated, for any reason — rather
      // than introducing a second, parallel notion of "the resolved
      // product changed." Looks up the Resolved Shopify Product (Shopify's
      // own id/handle/title/description/vendor/type/images/price fields,
      // parsed from data-shopify-products) by record.productId and hands
      // the whole object to each disclosure; a disclosure never receives
      // a bare field, only the resolved product itself.
      syncProductDependentDisclosures(record) {
        const shopifyProduct = record ? this.getResolvedShopifyProduct(record.productId) : null;
        if (this.productDetailsDisclosure) this.productDetailsDisclosure.sync(shopifyProduct);
      }

      onUrnStyleChange(event) {
        const input = event.target;
        this.applyResolvedProduct(input.value);
      }

      setHiddenVariantId(variantId) {
        if (!this.hiddenVariantInput) return;
        this.hiddenVariantInput.value = variantId;
        this.hiddenVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateUrlVariantParam(variantId) {
        if (this.closest('quick-add-modal')) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }

      clearFormError() {
        if (this.productForm && typeof this.productForm.handleErrorMessage === 'function') {
          this.productForm.handleErrorMessage();
        }
      }

      updateAddToCartAvailability(available) {
        if (!this.submitButton) return;
        const addButtonText = this.submitButton.querySelector('span');

        if (!available) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (addButtonText && window.variantStrings) {
            addButtonText.textContent = window.variantStrings.soldOut;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          if (addButtonText && window.variantStrings) {
            addButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      // ----- Phase 6.1: Product-Definition-owned step visibility -----
      // Consumes the two CPD facts the Phase 6.0 audit found the Builder
      // was hardcoding an assumption about instead of reading:
      // memorialWallSupported (Product Definition) and personalization-
      // policy.standard.photoUpload.capability (Policy Profile). Both are
      // resolved server-side in sections/bpf-memorial-designer.liquid via
      // the same generated snippet the five policy claims already use,
      // and handed here as one data-* attribute each on the relevant
      // <details> — this method reads them once and sets `hidden`
      // directly, the exact same native-attribute mechanism
      // getVisibleSteps() already treats as authoritative for "which
      // accordion steps exist right now" (see that method's own comment)
      // for the one other step that could already be conditionally
      // skipped. No change to getVisibleSteps()/renumberStepper()/
      // updateJourneyNav() themselves — they already handle any hidden
      // accordion generically. Called once, in the constructor, before
      // initAccordion()/initStepLocks() ever run, since this is a static
      // product-level fact that cannot change during a session (unlike
      // e.g. eligibility, which is recomputed live as Companion/Breed
      // change).
      applyProductDefinitionStepVisibility() {
        const memorialWallDetails = this.accordions.find((details) => details.dataset.accordionSection === 'memorial-wall');
        if (memorialWallDetails) {
          // Purchase-flow simplification, Full entry mode ONLY: Memorial
          // Wall moves to the post-Add-to-Cart add-on popup (see below
          // Review) and is never offered pre-cart for the Full Builder,
          // regardless of memorialWallSupported. Breed/Collection and
          // Product modes are byte-identical to before this change —
          // the pre-existing memorialWallSupported check is still the
          // only thing that gates them.
          memorialWallDetails.hidden = this.entryMode === 'full' || memorialWallDetails.dataset.memorialWallSupported !== 'true';
        }

        const photoDetails = this.accordions.find((details) => details.dataset.accordionSection === 'photo');
        if (photoDetails) {
          // Phase 4D.10C.1: photoUploadCapability is a Favorite-Photo-era
          // policy concept (Phase 6.1) — correct for gating a step whose
          // ENTIRE purpose was Favorite Photo, but Step 4 is now
          // primarily the Picture Frame gateway whenever the feature
          // flag is on (Favorite Photo becomes a child of it — see
          // sections/bpf-memorial-designer.liquid's own Section 4
          // comment). this.pictureFrameEnabled is already cached by this
          // point in the constructor (element caching runs before this
          // method is called). Flag off: byte-identical historical
          // behavior, unchanged — only the flag-on branch is new.
          // Purchase-flow simplification, Full entry mode ONLY: Step
          // 4's pre-cart photo picker is hidden for this mode too —
          // real end-to-end testing plus a direct code audit (no
          // validator anywhere conditions photo-requiredness on frame
          // or Wall, and the frame's own cart item carries no photo
          // property) confirmed the uploaded photo's only actual
          // destination was ever the Memorial Wall display. Full mode
          // never reaches performPrePurchaseImageUpload() at all now
          // (requiresMemorialImagePersistence() returns false for it),
          // so a photo picked here would silently upload nowhere and
          // never attach to anything — worse than not offering it.
          // Collected instead inside the post-Add-to-Cart popup's own
          // Memorial Wall section (sections/bpf-memorial-designer.liquid),
          // only when the customer actually opts into the Wall.
          // Breed/Collection and Product are byte-identical to before
          // this change — the pre-existing pictureFrameEnabled/
          // photoUploadCapability logic is still their only gate.
          photoDetails.hidden =
            this.entryMode === 'full' || (this.pictureFrameEnabled ? false : photoDetails.dataset.photoUploadCapability !== 'preview-only');
        }
      }

      // ----- Phase 5A.1, Parts 6-13: entry context (breed pages) -----
      // The SAME shared Builder, entered a different way — NOT a second
      // state machine, NOT a forked resolveProduct() path. This method's
      // entire job is to populate the EXACT SAME internal fields
      // (this.state.companion.type, this.state.breed.value) and drive
      // them through the EXACT SAME public methods (handleCompanionTypeChange(),
      // handleBreedChange()) a real two-click user flow already calls —
      // downstream code (recomputeEligibility(), resolveProduct(),
      // isMemorialStyleValid(), the canvas, Picture Frame, Memorial
      // Wall, Review pricing, Add to Cart) has no idea, and needs no
      // idea, whether these values came from a click or from page
      // context (Part 7).
      //
      // Called once, in the constructor, before ANY real interaction —
      // handleCompanionTypeChange()'s own isGenuineChange check
      // (previousType !== null && previousType !== newType) is false the
      // first time this.state.companion.type is ever set (it starts
      // null), so this deliberately reuses that EXISTING guard rather
      // than adding a new one: no reset cascade, no "reset notice" toast,
      // fires from this call — exactly like the very first click of a
      // real Builder session never resets anything either.
      //
      // Part 12 (locked breed context): after this runs, the Companion
      // step is hidden (native `hidden`, not `disabled` — a hidden,
      // still-technically-focusable-if-scripted <details> has no click/
      // keyboard path a real customer can use to reach the radios/select
      // inside it), so handleCompanionTypeChange()/handleBreedChange()
      // — the only two places that ever WRITE these fields — can never
      // fire again for the rest of the session. No new "locked" flag is
      // needed; the absence of any visible control IS the lock.
      initBreedEntryContext() {
        if (this.entryMode !== 'breed') return;
        if (!this.preselectedCompanionType || !this.preselectedBreed) return;
        if (!this.companionTypeInputs.length || !this.breedSelect) return;

        // Part 8: Companion Type derivation never uses fragile string
        // matching — the value itself already came straight from
        // metafields.custom.animal_type via the eligibility resolver's
        // own bpf-canonical-lookup/bpf-title-case normalization (see
        // sections/bpf-memorial-designer.liquid), so it's already the
        // same normalized display value (e.g. "Dog", "Cat") every
        // companionTypeInputs radio's own value already uses — a plain
        // exact match is all that's needed here.
        const matchingInput = this.companionTypeInputs.find((input) => input.value === this.preselectedCompanionType);
        if (!matchingInput) return; // unknown companion type — never guess, leave the full Builder behavior in place

        this.companionTypeInputs.forEach((input) => {
          input.checked = input === matchingInput;
        });
        // Mirrors handleChange()'s own call shape exactly (the browser
        // already set .checked for a real click before this handler
        // ever runs — done explicitly above for the same effect).
        this.handleCompanionTypeChange(this.preselectedCompanionType);

        // handleCompanionTypeChange() -> updateBreedVisibility() has now
        // populated this.breedSelect's <option>s for this companion
        // type (renderBreedOptions()) — only now can the preselected
        // breed actually be found/selected among them.
        this.breedSelect.value = this.preselectedBreed;
        if (this.breedSelect.value !== this.preselectedBreed) {
          // Graceful degradation (never a guess): the preselected breed
          // isn't among this companion type's real options — e.g. a
          // page assigned to the wrong product, or eligibility data
          // that changed since. state.breed.value stays whatever
          // handleBreedChange() below resolves it to (null, since the
          // select's own value didn't take) — recomputeEligibility()
          // already handles a null/unanswered breed safely (no eligible
          // styles), the same as an ordinary Dog/Cat selection with no
          // breed chosen yet.
        }
        this.handleBreedChange();

        // Part 10: hide the Companion step entirely — getVisibleSteps()/
        // renumberStepper() (native `hidden` filtering, already fully
        // generic — see that method's own comment) automatically
        // renumber every remaining step with zero further code. The
        // step that was open by default (Companion) must hand off
        // `open` to whichever step is now first, or no step would be
        // open on page load.
        const companionDetails = this.accordions.find((details) => details.dataset.accordionSection === 'companion');
        if (companionDetails) {
          companionDetails.open = false;
          companionDetails.hidden = true;
          companionDetails.classList.add('is-visited');
        }
        const firstVisible = this.getVisibleSteps()[0];
        if (firstVisible) firstVisible.open = true;
      }

      // ----- Phase 5B.1B: entry context (product pages) -----
      // Third entry mode, same shared engine — reuses initBreedEntryContext()'s
      // exact shape (state fields, existing public handlers, the
      // Companion-step hide, "hand off `open` to the first visible
      // step" finish) with one structural difference: the CURRENT
      // Memorial Style is locked (pre-checked before recomputeEligibility()
      // runs, so its own existing "keep an already-checked eligible
      // style selected" branch — unmodified — naturally preserves it
      // instead of a fresh 2-eligible free choice), and the counterpart
      // style is rendered as navigation, never as an in-page selection.
      initProductEntryContext() {
        if (this.entryMode !== 'product') return;
        if (!this.preselectedCompanionType || !this.currentProductStyle) return;
        if (!this.companionTypeInputs.length) return;

        const matchingInput = this.companionTypeInputs.find((input) => input.value === this.preselectedCompanionType);
        if (!matchingInput) return; // unknown companion type — never guess

        this.companionTypeInputs.forEach((input) => {
          input.checked = input === matchingInput;
        });
        this.handleCompanionTypeChange(this.preselectedCompanionType);

        // Breed is NOT required in product mode (a companion-generic
        // Designer product has no breed metafield at all — Part 5/6) —
        // only set it when the current product actually has one.
        if (this.preselectedBreed && this.breedSelect) {
          this.breedSelect.value = this.preselectedBreed;
          // Graceful degradation, same as breed mode: an unmatched
          // value is silently ignored by the real <select>, never a
          // guessed substitute.
        }

        // Lock the current style BEFORE handleBreedChange() triggers
        // recomputeEligibility() below — Part 17: resolveProduct()/
        // recomputeEligibility() themselves are untouched; this only
        // pre-seeds which radio is already checked, which their
        // EXISTING 2-eligible branch already respects (`if
        // (currentlyChecked && eligible.indexOf(currentlyChecked) !==
        // -1) { this.applyResolvedProduct(currentlyChecked); }`).
        const currentStyleInput = this.urnStyleInputs.find((input) => input.value === this.currentProductStyle);
        if (currentStyleInput) {
          this.urnStyleInputs.forEach((input) => {
            input.checked = input === currentStyleInput;
          });
        }

        this.handleBreedChange();

        // Parts 14/15 — current_variant protection, scoped entirely to
        // product-mode initialization (never touches applyResolvedProduct()/
        // resolveProduct() themselves).
        this.protectCurrentVariantInProductMode();

        // Part 11 — counterpart navigation UI, built from the SAME
        // state.style.eligibleVault/eligibleCapsule recomputeEligibility()
        // just populated above — no second resolver call.
        this.renderProductStyleCounterpart();

        const companionDetails = this.accordions.find((details) => details.dataset.accordionSection === 'companion');
        if (companionDetails) {
          companionDetails.open = false;
          companionDetails.hidden = true;
          companionDetails.classList.add('is-visited');
        }
        const firstVisible = this.getVisibleSteps()[0];
        if (firstVisible) firstVisible.open = true;
      }

      // Part 14/15/17 — a real Google Shopping / Shopify variant-URL
      // landing selects a SPECIFIC current_variant (e.g. a Black
      // Vault), but resolveProduct()'s returned eligibility record
      // always points at that product's own DEFAULT variant selection
      // (documented elsewhere in this file as "prefer White, else the
      // product's first variant"). Left unpatched, applyResolvedProduct()
      // (called from recomputeEligibility() inside initProductEntryContext()
      // above) would silently overwrite the hidden variant input with
      // that default the moment product mode initializes — exactly the
      // acceptance risk Phase 5B.1B's own audit flagged. This method
      // runs ONCE, only from product-mode init, and only patches the
      // ALREADY-resolved record's variant-specific fields back to the
      // real current_variant — it never re-runs resolveProduct(), never
      // touches companionType/breed/style matching, and is a pure no-op
      // for every other entry mode (never called there at all).
      //
      // The replacement variant's own real price/availability are read
      // from the SAME already-parsed Shopify product catalog
      // getResolvedShopifyProduct()/applyLiveColorVariant() already use
      // — never guessed, never left as the (possibly different-priced
      // or differently-available) default variant's own values.
      //
      // Phase 5B.1D: patching resolvedProduct's own fields (below) is
      // NOT sufficient on its own — every color-driven consumer in this
      // file (applyLiveColorVariant() on a later customer color change,
      // getCanvasState(), updateReviewSummary()) reads the LIVE checked
      // color radio from the DOM, never resolvedProduct, and those
      // radios are server-rendered checked=White regardless of entry
      // mode (see the {% if color == 'White' %}checked{% endif %} markup
      // in sections/bpf-memorial-designer.liquid). Left unpatched, the
      // hidden variant ID would correctly point at the landed variant
      // while the visible color/canvas/Review still showed White — the
      // exact real-QA defect this phase fixes. See
      // syncColorSelectionToActualVariant() below.
      protectCurrentVariantInProductMode() {
        if (!this.currentVariantId || !this.state.resolvedProduct) return;
        if (String(this.state.resolvedProduct.variantId) === String(this.currentVariantId)) return;

        const shopifyProduct = this.getResolvedShopifyProduct(this.state.resolvedProduct.productId);
        const variants = shopifyProduct && Array.isArray(shopifyProduct.variants) ? shopifyProduct.variants : [];
        const actualVariant = variants.find((v) => String(v.id) === String(this.currentVariantId));
        // Can't safely protect without the real catalog data for this
        // exact variant — leave the resolver's own (still internally
        // consistent) result in place rather than writing an ID with
        // no matching price/availability data behind it.
        if (!actualVariant) return;

        this.syncColorSelectionToActualVariant(shopifyProduct, actualVariant);

        this.state.resolvedProduct = {
          ...this.state.resolvedProduct,
          variantId: actualVariant.id,
          price: actualVariant.priceFormatted,
          priceCents: typeof actualVariant.priceCents === 'number' ? actualVariant.priceCents : null,
          available: actualVariant.available,
        };
        this.setHiddenVariantId(actualVariant.id);
        this.updatePriceDisplay(this.state.resolvedProduct);
      }

      // Phase 5B.1D, Parts 1/3/5 — maps the real current_variant's own
      // option values back onto the SAME builder color controls
      // applyLiveColorVariant() itself reads live from the DOM, using
      // the EXACT SAME option-matching helpers (findOptionPosition(),
      // getVariantValueAtPosition()) that method already uses — no new
      // resolver, no hardcoded "Color" assumption beyond what those
      // helpers already encode, no title/handle inference. Runs only
      // from inside protectCurrentVariantInProductMode() above, so it
      // shares that method's single init-time call site and early-return
      // guard: a landed variant that already matches the resolver
      // default (e.g. a genuinely White current_variant) never reaches
      // here, leaving the server-rendered White radios untouched — Part
      // 7's "White remains selected" case.
      //
      // This only sets .checked on the existing radios (native browser
      // behavior then unchecks any sibling sharing the same `name`) — it
      // never disables/locks them, so a later deliberate customer color
      // change (Part 4) continues through handleChange() ->
      // applyResolvedProduct() -> applyLiveColorVariant() completely
      // unmodified.
      syncColorSelectionToActualVariant(shopifyProduct, actualVariant) {
        const setChecked = (stepSelector, value) => {
          if (!value) return;
          const input = Array.from(this.querySelectorAll(stepSelector + ' input')).find(
            (candidate) => candidate.value === value
          );
          if (input) input.checked = true;
        };

        if (this.currentProductStyle === 'Capsule') {
          const colorPosition = this.findOptionPosition(shopifyProduct, 'Color');
          setChecked('[data-step="capsule-color"]', this.getVariantValueAtPosition(actualVariant, colorPosition));
        } else if (this.currentProductStyle === 'Vault') {
          const dogColorPosition = this.findOptionPosition(shopifyProduct, 'Color');
          const vaultColorPosition = this.findOptionPosition(shopifyProduct, 'Vault Color');
          setChecked(
            '[data-step="dog-engraving-color"]',
            this.getVariantValueAtPosition(actualVariant, dogColorPosition)
          );
          setChecked('[data-step="vault-color"]', this.getVariantValueAtPosition(actualVariant, vaultColorPosition));
        }

        // Keeps the separate Engraving Color <select> consistent with the
        // just-corrected Dog & Engraving/Capsule Color radio — a no-op
        // once the customer has made an explicit choice
        // (engravingColorManuallyEdited), which is never true this early
        // in initialization.
        this.syncEngravingColorFromDefault();
      }

      // Part 9/11 — the counterpart is discovered via the EXACT SAME
      // state.style.eligibleVault/eligibleCapsule recomputeEligibility()
      // (unmodified) already populated via resolveProduct() (unmodified)
      // — never a second lookup. Part 8: only rendered as a real,
      // clickable navigation link when the counterpart record is a
      // complete, available, AND Designer Enabled match; otherwise a
      // plain unavailable label — never a broken link, never a guess.
      renderProductStyleCounterpart() {
        if (!this.productStyleCounterpartWrapper || !this.productStyleCounterpartSlot) return;

        const counterpartStyle = this.currentProductStyle === 'Vault' ? 'Capsule' : 'Vault';
        const record = counterpartStyle === 'Vault' ? this.state.style.eligibleVault : this.state.style.eligibleCapsule;

        const slot = this.productStyleCounterpartSlot;
        slot.innerHTML = '';
        slot.className = 'bpf-option-pill__label';

        if (record && record.available === true && record.designerEnabled === true && record.productUrl) {
          slot.classList.add('bpf-option-pill__label--counterpart');
          const link = document.createElement('a');
          link.href = record.productUrl;
          const span = document.createElement('span');
          span.textContent = `View ${counterpartStyle}`;
          link.appendChild(span);
          slot.appendChild(link);
        } else {
          slot.classList.add('bpf-option-pill__label--unavailable');
          const span = document.createElement('span');
          span.textContent = counterpartStyle;
          slot.appendChild(span);
        }

        this.productStyleCounterpartWrapper.hidden = false;
      }

      // ----- Phase 2: personalization (line-item properties only) -----

      updateCurrentUrnStyle() {
        const checked = this.urnStyleInputs.find((input) => input.checked);
        this.currentUrnStyle = checked ? checked.value : null;
      }

      isVaultSelected() {
        return (this.currentUrnStyle || '').toLowerCase() === 'vault';
      }

      // Dog Bone Capsule phase: the Engraved Pet Name field was
      // previously gated purely on isVaultSelected() everywhere it
      // mattered — correct when engraving genuinely was a Vault-only
      // concept, but Dog Bone Capsule breaks that assumption without
      // making engraving generally available to Capsule. This is the
      // one capability check every one of those call sites now shares:
      // Vault is unconditionally engraving-capable (unchanged); Capsule
      // is engraving-capable only when the resolved visual mapping
      // record itself declares engravingSurface: 'capsule' (today, only
      // Dog Bone Capsule — every other Capsule record inherits the
      // harmless 'base' default and correctly returns false here,
      // exactly as it always effectively has). Driven entirely by data
      // already flowing through resolveVisual() — no breed-name check,
      // here or anywhere else in this file.
      // Memorial Wall name-sync fix: accepts an optional explicit style
      // (falling back to the cached this.currentUrnStyle exactly as
      // before when omitted, so every existing caller — none of which
      // pass an argument — is byte-for-byte unaffected). Needed because
      // syncCompanionNameFromEngraved() deliberately reads the live
      // checked radio (getCheckedStyle()) rather than this.currentUrnStyle
      // — a Phase 2.5.2 fix for a real sequencing gap (see this file's
      // header history) that this method must not silently reintroduce
      // by only ever consulting the cached property.
      currentStyleSupportsEngraving(style) {
        const effectiveStyle = style || this.currentUrnStyle;
        const normalizedStyle = (effectiveStyle || '').toLowerCase();
        if (normalizedStyle === 'vault') return true;
        if (normalizedStyle !== 'capsule') return false;
        const record = this.resolveVisual(this.state.companion.type, this.state.breed.value, effectiveStyle);
        // Unambiguous by construction, not by convention: engravingSurface
        // is a closed 3-value enum ('base' | 'figure' | 'capsule'), and
        // 'base' is the reset-block default every ordinary Capsule record
        // still inherits — meaningless for a flattened composition, never
        // set to anything else unless a record explicitly opts in. This
        // MUST stay an exact === 'capsule' comparison, never a truthy/
        // non-default check (e.g. `!== 'base'` or `Boolean(engravingSurface)`)
        // — either of those would treat a Vault-only value like 'figure'
        // leaking onto a Capsule record as engraving-capable too. Today
        // exactly one record sets this to 'capsule' (Dog|Dog Bone|Capsule
        // in snippets/bpf-memorial-visual-mapping.liquid); every other
        // Capsule record correctly returns false here.
        return Boolean(record && record.engravingSurface === 'capsule');
      }

      applyPersonalizationVisibility() {
        const style = (this.currentUrnStyle || '').toLowerCase();

        this.colorGroups.forEach((group) => {
          const isActive = group.dataset.appliesTo === style;
          group.hidden = !isActive;
          group.querySelectorAll('input').forEach((input) => {
            input.disabled = !isActive;
          });
        });

        if (this.petNameField) {
          const supportsEngraving = this.currentStyleSupportsEngraving();
          this.petNameField.hidden = !supportsEngraving;
          if (this.petNameInput) this.petNameInput.disabled = !supportsEngraving;
        }

        if (this.engravingColorField) {
          const supportsEngraving = this.currentStyleSupportsEngraving();
          this.engravingColorField.hidden = !supportsEngraving;
          if (this.engravingColorSelect) this.engravingColorSelect.disabled = !supportsEngraving;
        }
        this.syncEngravingColorFromDefault();

        this.updateMemorialWallPetNameVisibility();
      }

      // Phase 4D.9A — Capsule Type (Memorial Urn vs Memorial Statue). A
      // no-op when the feature flag is off (capsuleTypeInputs is empty in
      // that case). Reveals beneath the Capsule choice the same way
      // Memorial Wall's children reveal beneath its Yes/No — but unlike
      // that field (and unlike the color groups in
      // applyPersonalizationVisibility() above), Capsule Type is
      // explicitly reset to its default on every hide, not merely
      // hidden+disabled-preserving: switching Capsule -> Vault clears it,
      // and switching Vault -> Capsule always reveals it back at its
      // "Memorial Urn" default — never a stale prior Statue choice. One
      // symmetric rule (reset-on-hide) satisfies both directions, since
      // whatever was last hidden is already back at Urn by the time it's
      // next shown.
      //
      // The submitted line-item property value ("Memorial Urn" / "Memorial
      // Statue") is used directly as this feature's only internal state —
      // no separate urn/statue enum — matching how this file already
      // treats Memorial Style itself (this.currentUrnStyle stores "Vault"/
      // "Capsule" directly, never a separate normalized code).
      updateCapsuleTypeVisibility() {
        if (!this.capsuleTypeChildren || !this.capsuleTypeInputs.length) return;

        const isCapsule = (this.currentUrnStyle || '').toLowerCase() === 'capsule';
        this.capsuleTypeChildren.hidden = !isCapsule;
        this.capsuleTypeInputs.forEach((input) => {
          input.disabled = !isCapsule;
        });

        if (!isCapsule) {
          this.capsuleTypeInputs.forEach((input) => {
            input.checked = input.value === 'Memorial Urn';
          });
        }
      }

      // Live-DOM read (never cached) — mirrors getCheckedStyle()'s own
      // "always read fresh" convention, since this feeds Review/summary
      // rendering that must never show a stale selection.
      getCheckedCapsuleType() {
        if (!this.capsuleTypeInputs.length) return null;
        const checked = this.capsuleTypeInputs.find((input) => input.checked);
        return checked ? checked.value : null;
      }

      // Phase 5C.1G, Parts 1-5 — mobile-only reveal for the existing
      // Memorial Urn/Statue explanatory copy. Reuses revealInCardQuestion()
      // (Phase 5C.1F) unmodified — no new scroll engine (Part 1). The
      // single <p> already explains both options together (Part 4 — no
      // per-selection description to choose between), so this never
      // touches its content or visibility beyond bringing it into view;
      // the pills themselves are never hidden/compacted (Part 3).
      revealCapsuleTypeHelp() {
        if (this.isDesktopViewport()) return;
        if (!this.capsuleTypeHelpText) return;
        if (this.styleDetails) this.revealInCardQuestion(this.styleDetails, this.capsuleTypeHelpText);
      }

      // ----- Phase 4D.10B: Picture Frame add-on -----
      //
      // UI/state only — no renderer, no cart line, no line-item
      // properties yet. this.pictureFrameOptInInput is null when the
      // feature flag is off (Liquid never renders the block), so this is
      // the single source of truth for "is the feature enabled" — same
      // convention as capsuleTypeInputs.length elsewhere in this file.
      get pictureFrameEnabled() {
        return Boolean(this.pictureFrameOptInInput);
      }

      // Reset-on-hide, matching updateCapsuleTypeVisibility()'s own
      // pattern exactly: deselecting always returns Orientation/Frame
      // Color to their defaults (Portrait/Black) rather than merely
      // hiding+preserving (unlike Memorial Wall's Story/State children) —
      // so re-opting-in always starts from a clean, valid, defaulted
      // state, never a stale prior choice. Disabling the child inputs
      // while hidden is a UX/accessibility measure (no hidden-but-
      // focusable controls); it is not what keeps them out of the cart,
      // since none of these inputs have a name/form attribute at all in
      // this phase (see the Liquid comment above this block).
      updatePictureFrameVisibility() {
        if (!this.pictureFrameChildren || !this.pictureFrameOptInInput) return;

        const selected = this.state.pictureFrame.selected === true;
        this.pictureFrameChildren.hidden = !selected;
        this.pictureFrameOrientationInputs.forEach((input) => {
          input.disabled = !selected;
        });
        // Phase 4D.10C, Part 8 — Frame Color is a single <select>.
        if (this.pictureFrameColorSelect) this.pictureFrameColorSelect.disabled = !selected;

        if (!selected) {
          this.state.pictureFrame.color = 'Black';
          if (this.pictureFrameColorSelect) this.pictureFrameColorSelect.value = 'Black';
          // orientationWasManuallyChosen resets here too (explicitly —
          // setPictureFrameOrientation's manual:false never clears an
          // already-true flag on its own, it only ever sets it true) — a
          // customer who manually picked Landscape, deselected, and later
          // re-opts in starts fresh (auto-follows the current photo
          // again), matching "reselect -> valid state restored/defaulted."
          this.state.pictureFrame.orientationWasManuallyChosen = false;
          this.setPictureFrameOrientation('Portrait', { manual: false });
        } else {
          // Opting in (freshly, or after a reset-by-deselect above) —
          // Part 6's default: auto-select from the CURRENT photo if one
          // already exists, otherwise Portrait. evenIfNoPhoto: true is
          // what makes this differ from the photo-replacement call site
          // in handlePhotoChange() below, which must never move
          // orientation off of a photo REMOVAL.
          this.syncPictureFrameOrientationFromPhoto({ evenIfNoPhoto: true });
        }

        // Phase 4D.10E, Part 31/40 — the Details link's visibility
        // tracks pictureFrame.selected exactly like every other
        // Picture-Frame-selected-dependent UI in this method; the
        // modal's own content is refreshed here too so it's never
        // stale the first time it's opened after opting in (and reset
        // back to Black's own data on deselect, matching the color
        // reset above).
        this.updatePictureFrameDetailsLinkVisibility();
        this.updatePictureFrameDetailsModalContent();
      }

      // Phase 4D.10E, Part 31/32/40 — shows/hides BOTH the desktop and
      // mobile "Picture Frame Details" triggers together, so they can
      // never diverge. No-op (both null) when the feature flag is off.
      updatePictureFrameDetailsLinkVisibility() {
        const selected = Boolean(this.state.pictureFrame && this.state.pictureFrame.selected);
        (this.pictureFrameDetailsTriggerWrappers || []).forEach((wrapper) => {
          wrapper.hidden = !selected;
        });
      }

      // Phase 4D.10E, Parts 34-36 — keeps the Details modal's image and
      // price in sync with the CURRENTLY SELECTED Frame Color. Title/
      // description never change here (Liquid-static, Part 38 — neither
      // varies by color). Called on opt-in/deselect and on every Frame
      // Color change, so the modal is always current the moment it's
      // opened — never a stale cached variant (Part 36).
      //
      // Part 35 — no silent wrong-color fallback: a resolved variant
      // with no imageUrl of its own shows the explicit "Image
      // unavailable" state instead of the <img>, never a different
      // color's image and never the product's own generic featured
      // image (not yet approved).
      // Phase 5C.1G, Part 7 root-cause fix: real QA found this modal
      // simultaneously showing a valid frame image (the SEPARATE,
      // already-working persistent builder canvas preview — Part 8 —
      // sitting behind/beside this modal) alongside this modal's OWN
      // "Image unavailable for this color." text. Auditing this method
      // (unchanged below) confirmed its own image/unavailable toggle was
      // already a correct, mutually-exclusive if/else — the contradiction
      // was never possible from THIS element alone. The real gap: the
      // ONLY image source consulted was resolvePictureFrameVariant()'s
      // PER-VARIANT imageUrl (Part 6/34), which is null whenever a
      // merchant hasn't set an image on that specific color variant (or,
      // separately, whenever the live product itself doesn't resolve —
      // see BPF_PICTURE_FRAME_VERIFIED_FALLBACK's own header) — a real,
      // common gap distinct from the product itself having no image at
      // all. Now follows the exact Part 7 priority: (1) the resolved
      // variant's own imageUrl, (2) the product's own real featured
      // image (this.pictureFrameCommerce.productFeaturedImageUrl —
      // Liquid-sourced, never a guessed/invented URL, null when the
      // product genuinely has none), (3) the unavailable message only
      // when NEITHER resolves. Never both an image and the message at
      // once, exactly as before — only the source list grew.
      updatePictureFrameDetailsModalContent() {
        if (!this.pictureFrameDetailsImage) return;
        const color = (this.state.pictureFrame && this.state.pictureFrame.color) || 'Black';
        const variant = this.resolvePictureFrameVariant(color);
        const imageUrl =
          (variant && variant.imageUrl) ||
          (this.pictureFrameCommerce && this.pictureFrameCommerce.productFeaturedImageUrl) ||
          null;

        if (imageUrl) {
          this.pictureFrameDetailsImage.src = imageUrl;
          this.pictureFrameDetailsImage.hidden = false;
          if (this.pictureFrameDetailsImageUnavailable) this.pictureFrameDetailsImageUnavailable.hidden = true;
        } else {
          this.pictureFrameDetailsImage.hidden = true;
          if (this.pictureFrameDetailsImageUnavailable) this.pictureFrameDetailsImageUnavailable.hidden = false;
        }

        if (this.pictureFrameDetailsPrice) {
          this.pictureFrameDetailsPrice.textContent = variant && variant.priceFormatted ? variant.priceFormatted : '—';
        }
      }

      // Single write path for orientation — keeps this.state.pictureFrame
      // and the radio pills' checked state from ever disagreeing.
      setPictureFrameOrientation(value, options) {
        var manual = Boolean(options && options.manual);
        this.state.pictureFrame.orientation = value;
        if (manual) this.state.pictureFrame.orientationWasManuallyChosen = true;
        this.pictureFrameOrientationInputs.forEach((input) => {
          input.checked = input.value === value;
        });
      }

      // Auto/manual orientation — Phase 4D.10B, Part 8. A no-op unless
      // the frame is selected and the customer hasn't manually chosen an
      // orientation yet (mirrors engravingColorManuallyEdited's own
      // "explicit choice always wins" shape). evenIfNoPhoto is only ever
      // passed by updatePictureFrameVisibility() at opt-in time (Part 6:
      // "no photo exists -> default Portrait"); the handlePhotoChange()
      // call site omits it deliberately, so a photo REMOVAL (file made
      // null) leaves the current orientation state untouched (Part 8:
      // "if photo is removed, keep current orientation state") rather
      // than resetting to Portrait.
      syncPictureFrameOrientationFromPhoto(options) {
        if (!this.pictureFrameEnabled) return;
        if (!this.state.pictureFrame.selected) return;
        if (this.state.pictureFrame.orientationWasManuallyChosen) return;

        var evenIfNoPhoto = Boolean(options && options.evenIfNoPhoto);
        var file = this.selectedPhotoFile;

        if (!file) {
          if (evenIfNoPhoto) this.setPictureFrameOrientation('Portrait', { manual: false });
          return;
        }

        var requestFile = file;
        this.readImageDimensionsFromFile(file).then((dimensions) => {
          // Stale-response guard, same shape as handlePhotoChange()'s own
          // "if (this.selectedPhotoFile !== file) return" — a slower
          // dimension read must never overwrite a newer photo selection's
          // (or a since-made manual choice's) result.
          if (this.selectedPhotoFile !== requestFile) return;
          if (this.state.pictureFrame.orientationWasManuallyChosen) return;
          this.setPictureFrameOrientation(this.orientationFromDimensions(dimensions), { manual: false });
          this.updateReviewSummary();
        });
      }

      // width > height -> Landscape; height >= width -> Portrait; no
      // reliable dimensions -> Portrait (safe fallback, Part 6/7 — never
      // blocks the customer).
      orientationFromDimensions(dimensions) {
        if (!dimensions || !dimensions.width || !dimensions.height) return 'Portrait';
        return dimensions.width > dimensions.height ? 'Landscape' : 'Portrait';
      }

      // Local-only browser helper (Part 7): object URL -> Image ->
      // naturalWidth/naturalHeight -> revoke. Never re-uploads, never
      // touches the File itself, never alters the existing eager-upload
      // path in handlePhotoChange(). Always RESOLVES (never rejects),
      // with null on any failure — so callers never need a .catch() to
      // stay safe, matching Part 7's "if dimension detection fails,
      // safely fall back to Portrait, do not block the customer."
      readImageDimensionsFromFile(file) {
        if (
          !file ||
          typeof URL === 'undefined' ||
          typeof URL.createObjectURL !== 'function' ||
          typeof Image === 'undefined'
        ) {
          return Promise.resolve(null);
        }

        return new Promise((resolve) => {
          var objectUrl;
          try {
            objectUrl = URL.createObjectURL(file);
          } catch (error) {
            resolve(null);
            return;
          }

          var cleanup = function () {
            try {
              URL.revokeObjectURL(objectUrl);
            } catch (error) {
              // ignore — best-effort cleanup only
            }
          };

          var img = new Image();
          img.onload = function () {
            var dimensions =
              img.naturalWidth && img.naturalHeight ? { width: img.naturalWidth, height: img.naturalHeight } : null;
            cleanup();
            resolve(dimensions);
          };
          img.onerror = function () {
            cleanup();
            resolve(null);
          };
          img.src = objectUrl;
        });
      }

      // Shared by buildStyleSummaryLines() (closed-card summary) and
      // updateReviewSummary() (Review step) so both compact presentations
      // of "Memorial Style" stay in sync automatically. Feature-off or
      // Vault-selected: unchanged single-value behavior ("Vault"/
      // "Capsule"). Feature-on + Capsule + a type chosen: combined
      // "Capsule — Memorial Statue" form, matching the Review step's
      // existing "one compact row per field" convention rather than
      // adding a whole separate labeled row.
      getStyleDisplayValue() {
        const style = this.currentUrnStyle;
        if (!style) return null;
        if (style.toLowerCase() === 'capsule') {
          const capsuleType = this.getCheckedCapsuleType();
          if (capsuleType) return `${style} — ${capsuleType}`;
        }
        return style;
      }

      updatePetNameCounter() {
        if (!this.petNameInput || !this.petNameCounter) return;
        this.petNameCounter.textContent = `${this.petNameInput.value.length} / ${this.maxPetNameLength}`;
      }

      // Phase 2.5.2: engraving is optional — blank is valid. Native
      // maxlength="12" already prevents typing past the limit, so this is
      // effectively always true in normal use; kept as a real check (not
      // deleted) in case a future non-typing value assignment ever bypasses
      // maxlength.
      isPetNameValid() {
        if (!this.petNameInput) return true;
        return this.petNameInput.value.trim().length <= this.maxPetNameLength;
      }

      // Phase 2.5.3: the Memorial Wall Pet's Name field is required for
      // BOTH Vault and Capsule whenever Memorial Wall is opted into (and
      // never required, regardless of style, while hidden+disabled on
      // "No") — see updateMemorialWallPetNameVisibility(). This is
      // independent from Vault's own engraving requirement
      // (isPetNameValid(), which stays optional).
      isCompanionNameValid() {
        if (!this.companionNameInput) return true;
        // Purchase-flow simplification, Full entry mode ONLY: Memorial
        // Wall's own accordion is forced hidden pre-cart for Full
        // (applyProductDefinitionStepVisibility()) — its "Yes" radio
        // still defaults checked underneath that hidden accordion, so
        // without this exemption this gate would require a Pet's Name
        // the customer can never see or fill in, permanently blocking
        // Add to Cart. Breed/Collection and Product are byte-identical
        // to before this change.
        if (this.entryMode === 'full') return true;
        if (this.state.memorialWall.optIn !== true) return true;
        const trimmed = this.companionNameInput.value.trim();
        return trimmed.length > 0 && trimmed.length <= this.maxCompanionNameLength;
      }

      isCompanionTypeValid() {
        if (!this.companionTypeInputs.length) return true;
        return this.companionTypeInputs.some((input) => input.checked);
      }

      areActiveColorsSelected() {
        const activeGroups = this.colorGroups.filter((group) => !group.hidden);
        return activeGroups.every((group) => group.querySelector('input:checked'));
      }

      // Phase 2.5.1: Pet's Name (properties[Companion Name]) is no longer a
      // Personalize concern — it relocated to Memorial Wall, so this only
      // composes product personalization now. See isCompanionNameValid(),
      // composed directly into isBuilderComplete() instead.
      isPersonalizeValid() {
        const supportsEngraving = this.currentStyleSupportsEngraving();
        const colorsValid = this.areActiveColorsSelected();
        const petNameValid = supportsEngraving ? this.isPetNameValid() : true;
        return colorsValid && petNameValid;
      }

      // ----- Phase 2.5: centralized required-completion authority -----
      // The one method that decides whether Review may unlock. Composes
      // the field-level validators above rather than duplicating their
      // logic — Style's own isMemorialStyleValid() already accounts for
      // the 0/1/2-eligible cases (auto-selected counts as complete; zero
      // eligible never does), so it needs no special-casing here.

      isBuilderComplete() {
        return (
          this.isCompanionTypeValid() &&
          this.isBreedValid() &&
          this.isMemorialStyleValid() &&
          this.isPersonalizeValid() &&
          this.isCompanionNameValid()
        );
      }

      // ----- Phase 2.5.2: Step 1 / Step 2 completeness (feeds the step
      // locks below) -----

      isStep1Complete() {
        return this.isCompanionTypeValid() && this.isBreedValid();
      }

      isStep2Complete() {
        return this.isMemorialStyleValid();
      }

      updateAddToCartState(options = {}) {
        const supportsEngraving = this.currentStyleSupportsEngraving();
        const petNameValid = supportsEngraving ? this.isPetNameValid() : true;
        const companionNameValid = this.isCompanionNameValid();
        const companionTypeValid = this.isCompanionTypeValid();
        const breedValid = this.isBreedValid();
        const isValid = this.isBuilderComplete();

        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', (!isValid).toString());
          this.submitButton.classList.toggle('bpf-memorial-designer__add-to-cart--blocked', !isValid);
        }

        // Bugfix (Dog Bone Capsule regression): this function's own
        // `isVault` local was renamed to `supportsEngraving` above when
        // Vault-only engraving gating became capability-driven (Dog Bone
        // Capsule is the first Capsule that also needs this), but these
        // two uses were missed — `isVault` was never declared anywhere
        // in this function's scope, so both threw ReferenceError on
        // every call, unconditionally, for every product (this.petNameInput
        // always exists in the DOM, just conditionally hidden/disabled).
        // Since updateAddToCartState() runs before updateReviewSummary()/
        // updateCanvasFromState() in handleChange(), the uncaught throw
        // silently aborted both of those on every change event — the
        // memorial canvas never got a chance to re-render after the
        // initial (unresolved, stage-only) page-load state. Using
        // supportsEngraving here is not just a rename to fix the crash;
        // it's the semantically correct replacement — Pet Name validity
        // must only gate aria-invalid/the error message for whichever
        // style actually requires it (every Vault, and now Dog Bone
        // Capsule), exactly matching petNameValid's own derivation above.
        if (this.petNameInput) {
          this.petNameInput.setAttribute('aria-invalid', (supportsEngraving && !petNameValid).toString());
        }

        if (this.petNameError) {
          const showError = Boolean(options.showPetNameError) && supportsEngraving && !petNameValid;
          this.petNameError.hidden = !showError;
        }

        if (this.companionNameInput) {
          this.companionNameInput.setAttribute('aria-invalid', (!companionNameValid).toString());
        }

        if (this.companionNameError) {
          const showCompanionError = Boolean(options.showCompanionNameError) && !companionNameValid;
          this.companionNameError.hidden = !showCompanionError;
        }

        if (this.companionTypeError) {
          const showCompanionTypeError = Boolean(options.showCompanionTypeError) && !companionTypeValid;
          this.companionTypeError.hidden = !showCompanionTypeError;
        }

        if (this.breedError) {
          const showBreedError = Boolean(options.showBreedError) && !breedValid;
          this.breedError.hidden = !showBreedError;
        }

        this.updateStepLocks();
        this.updateReviewLockState();
      }

      // ----- Phase 2.2: "Personalize Your Memorial" / Photo / Memorial Wall -----
      // Phase 2.5.1: Companion Name (properties[Companion Name]) is no
      // longer unconditionally required — see isCompanionNameValid() above
      // and the "memorial-wall" section's Continue gate in
      // initContinueButtons() below. Companion State and Pet Story remain
      // fully optional; Photo and Remember on Memorial Wall's own Yes/No
      // choice also never affect Add to Cart validity.

      // Phase 2.5.3: one-directional, pre-fill-only sync. Populates the
      // Memorial Wall Pet's Name field (properties[Companion Name]) from
      // the Vault engraved name (properties[Pet Name]) only while Vault is
      // active AND the customer hasn't directly edited the Memorial Wall
      // field yet (companionNameManuallyEdited, set by handleInput()). Once
      // they've typed their own value there, this stops overwriting it —
      // the two properties are allowed to diverge on purpose (e.g. "MAX"
      // engraved, "Maxwell" on the Memorial Wall). Reads the checked radio
      // directly rather than the cached currentUrnStyle — see
      // updateMemorialWallPetNameVisibility()'s own comment for why.
      // Bugfix (Dog Bone Capsule): was hardcoded to Vault only
      // (this.getCheckedStyle() !== 'Vault'), so Dog Bone Capsule's own
      // Engraved Pet Name never pre-filled the Memorial Wall field even
      // though it's the same shared input, correctly required, and
      // already engraving-capable. Now uses the same capability concept
      // as everywhere else in this file ("does this product already
      // collect an engraved Pet Name?") via currentStyleSupportsEngraving()
      // — still reads the LIVE checked radio (getCheckedStyle()), not the
      // cached this.currentUrnStyle, preserving the exact freshness this
      // method has always required (see the class comment on why that
      // matters). Standard Vault and Dog Bone Vault both still return
      // true unconditionally, byte-for-byte unchanged; every other
      // Capsule still returns false and this sync never fires for them.
      syncCompanionNameFromEngraved() {
        if (!this.companionNameInput || !this.petNameInput) return;
        if (this.companionNameManuallyEdited) return;
        if (!this.currentStyleSupportsEngraving(this.getCheckedStyle())) return;
        this.companionNameInput.value = this.petNameInput.value;
      }

      // Engraving Color: mirrors the exact same fallback precedence
      // assets/bpf-vault-engraving-layer.js's render() applies for its
      // second and third tiers (visualAssets.engravingColorDefault, then
      // the surface-appropriate customer color selection) — the first
      // tier, an explicit customer override, is what
      // syncEngravingColorFromDefault() below decides whether to apply
      // AHEAD of this computed value. Kept as a small, self-contained
      // mirror here (that file is a different class with no shared
      // instance) rather than a cross-file call; must stay consistent
      // with that precedence if it's ever changed.
      getEffectiveEngravingColorDefault() {
        const visualAssets = this.resolveVisual(this.state.companion.type, this.state.breed.value, this.currentUrnStyle);
        const engravingSurface = (visualAssets && visualAssets.engravingSurface) || 'base';
        const engravingColorDefault = (visualAssets && visualAssets.engravingColorDefault) || '';
        if (engravingColorDefault) return engravingColorDefault;
        if (engravingSurface === 'capsule') {
          return this.getCheckedValue(this.querySelector('[data-step="capsule-color"] input:checked'));
        }
        return this.getCheckedValue(this.querySelector('[data-step="dog-engraving-color"] input:checked'));
      }

      // One-directional, pre-fill-only sync — same "until manually
      // edited" pattern as syncCompanionNameFromEngraved(). Keeps the
      // <select>'s own displayed value equal to the current effective
      // default for as long as the customer hasn't explicitly picked a
      // color themselves; once they have
      // (engravingColorManuallyEdited), this stops overwriting it
      // entirely, even when figure/capsule color changes afterward.
      syncEngravingColorFromDefault() {
        if (!this.engravingColorSelect) return;
        if (this.engravingColorManuallyEdited) return;
        if (!this.currentStyleSupportsEngraving(this.getCheckedStyle())) return;
        const effectiveDefault = this.getEffectiveEngravingColorDefault();
        if (effectiveDefault) this.engravingColorSelect.value = effectiveDefault;
      }

      // An explicit engraving-color override is scoped to one product
      // identity — switching Companion Type, Breed, or Memorial Style
      // must never carry a previously-chosen color into a different
      // product's own default (e.g. a Pink override on a Pitbull Vault
      // must not persist once the customer switches to Dog Bone Vault,
      // which itself defaults to Black). Only clears the flag; the
      // <select>'s own displayed value is corrected by the very next
      // syncEngravingColorFromDefault() call, which every caller of
      // this method already triggers immediately afterward (via
      // applyPersonalizationVisibility()/recomputeEligibility()).
      resetEngravingColorOverride() {
        this.engravingColorManuallyEdited = false;
      }

      updateStoryCounter() {
        if (!this.storyInput || !this.storyCounter) return;
        this.storyCounter.textContent = `${this.storyInput.value.length} / ${this.maxStoryLength}`;
      }

      handlePhotoChange() {
        if (!this.photoInput || !this.photoFilename) return;
        const file = this.photoInput.files && this.photoInput.files[0];

        // Phase 5D.1B — at most one active photo upload per instance
        // (deliberately separate controller from canvas persistence —
        // see maybePersistMemorialCanvas()'s own comment on why these
        // never share cancellation state, they represent different
        // operations). Whatever the PREVIOUS selection was still
        // uploading — if anything — is aborted here, before this new
        // selection (or removal) is processed at all, so a customer who
        // replaces their photo choice never leaves the old upload
        // running to completion wastefully.
        if (this._activePhotoUploadController) {
          this._activePhotoUploadController.abort();
          this._activePhotoUploadController = null;
        }

        // Kept in memory for the summary card text AND (Phase 4D.8C) as
        // the Priority 1 signal performPrePurchaseImageUpload() checks at
        // Add to Cart time — the raw File itself is still never attached
        // to the cart/order form directly (this.photoInput still has no
        // name/form attribute); only the GID this upload resolves to ever
        // reaches a line-item property.
        this.selectedPhotoFile = file || null;
        this.photoFilename.textContent = file ? file.name : 'No photo selected';
        // Phase 4D.10C, Part 11/14 — the SAME canonical <input> (see its
        // own comment in sections/bpf-memorial-designer.liquid) is now
        // triggerable from both Step 4 and Step 5, so this ONE handler
        // keeps BOTH filename displays in sync regardless of which
        // card's "Choose a Photo" trigger the customer actually used.
        // Null-safe: this.photoFilenameWall only exists when the feature
        // flag is on.
        if (this.photoFilenameWall) this.photoFilenameWall.textContent = file ? file.name : 'No photo selected';
        this.setBuilderImageGidField('photo', '');

        // Phase 4D.10C, Part 13/15 — Memorial Wall's contextual photo
        // subsection reacts to the SAME canonical selectedPhotoFile, so a
        // photo chosen from EITHER Step 4 or Step 5 immediately updates
        // it. No-op (both cached elements null) when the feature flag is
        // off.
        this.updateMemorialWallPhotoSection();

        // Phase 4D.10B — Picture Frame add-on: recomputes orientation
        // from the newly selected photo's aspect ratio, but only when a
        // real file was chosen/replaced (default omits evenIfNoPhoto) —
        // a photo REMOVAL must keep the current orientation state as-is
        // (this phase does not implement a stock-photo fallback), so this
        // is deliberately a no-op when file is null. No-ops entirely
        // whenever the frame isn't selected or the flag is off, or the
        // customer already manually chose an orientation (all checked
        // inside the method itself).
        this.syncPictureFrameOrientationFromPhoto();

        if (!file) {
          this._photoUploadPromise = null;
          return;
        }

        // Phase 4D.9B.9, Part 13: no filename/size/content — event labels
        // only, matching every other bpfTimingMark() call site.
        bpfTimingMark('Favorite Photo selected, eager upload started');

        // Phase 5D.1B — a fresh controller for THIS selection, passed to
        // uploadBuilderImage() as an external abort signal so a LATER
        // photo change (or component disconnect) can cancel this exact
        // attempt without waiting for it to finish.
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        this._activePhotoUploadController = controller;

        // Stored so performPrePurchaseImageUpload() can await this exact
        // upload at Add to Cart time — a customer who clicks Add to Cart
        // before this settles must not have their photo silently dropped
        // just because the eager upload hadn't finished yet.
        this._photoUploadPromise = this.uploadBuilderImage(file, file.name, controller ? { signal: controller.signal } : undefined)
          .then((gid) => {
            // Stale-completion protection (Part 13/17): a superseded
            // attempt (a newer photo already took over
            // _activePhotoUploadController) is discarded silently here —
            // never treated as authoritative, regardless of arrival
            // order.
            if (this._activePhotoUploadController !== controller) return undefined;
            this._activePhotoUploadController = null;
            bpfTimingMark('Favorite Photo eager upload resolved');
            // Stale-response guard: if the customer already picked a
            // different (or no) photo by the time this resolves, this
            // result is no longer current — never overwrite a newer
            // selection with an older upload's GID. Kept in addition to
            // the controller-identity check above (belt-and-suspenders —
            // this one predates 5D.1B and already covers the case where
            // selectedPhotoFile changed some other way).
            if (this.selectedPhotoFile !== file) return undefined;
            this.setBuilderImageGidField('photo', gid);
            bpfTimingMark('Favorite Photo GID populated');
            return undefined;
          })
          .catch(() => {
            // Phase 5D.1B — an intentional supersession abort (a newer
            // photo selection, or component disconnect) lands here too,
            // since fetch() rejects with AbortError either way. The
            // controller-identity check distinguishes "this is expected,
            // stay silent, the newer selection's own attempt is already
            // handling things" from "this attempt is still current and
            // genuinely failed" — both cases already shared the same
            // non-blocking, GID-stays-empty outcome, so no new failure
            // branch is introduced, only this one extra check.
            if (this._activePhotoUploadController !== controller) return;
            this._activePhotoUploadController = null;
            bpfTimingMark('Favorite Photo eager upload rejected (non-blocking)');
            // Upload failure: the GID field stays empty. The post-purchase
            // processor's Priority 3 fallback (DRAFT Memorial, no image)
            // already handles a missing photo GID safely — the customer
            // is never blocked from continuing the Builder or checkout
            // because of a transient upload problem.
          });
      }

      getCheckedValue(input) {
        return input ? input.value : '';
      }

      // ----- Phase 2.5: Memorial Wall children (Story/State) -----
      // Hidden+disabled (not cleared) on "No", matching the existing
      // Capsule/Vault color-group pattern — both fields are optional with
      // no validation gate either way, so preserving typed content on an
      // accidental Yes→No→Yes toggle costs nothing and avoids losing a
      // customer's story.

      updateMemorialWallChildrenVisibility() {
        if (!this.memorialWallChildren) return;
        const checked = this.memorialWallInputs.find((input) => input.checked);
        const optIn = Boolean(checked && checked.value === 'Yes');
        this.state.memorialWall.optIn = optIn;

        this.memorialWallChildren.hidden = !optIn;
        if (this.storyInput) this.storyInput.disabled = !optIn;
        if (this.stateSelect) this.stateSelect.disabled = !optIn;

        this.updateMemorialWallPetNameVisibility();
      }

      // Phase 2.5.3: Memorial Wall's own Pet's Name field
      // (properties[Companion Name]) — visible and required for BOTH
      // Vault and Capsule whenever Memorial Wall is opted into;
      // hidden+disabled (value preserved, not cleared) otherwise, the same
      // pattern Story/State already use. This is deliberately independent
      // from style — unlike Phase 2.5.1/2.5.2's "single source of truth"
      // approach (which hid this field entirely for Vault), Vault
      // customers may now hold two distinct values on purpose: the
      // engraved name (properties[Pet Name], physical engraving) and this
      // Memorial Wall name (e.g. "MAX" engraved, "Maxwell" here). For
      // Vault, this field pre-fills from the engraved name — see
      // syncCompanionNameFromEngraved() for the "until manually edited"
      // rule that keeps them independently editable afterward.
      updateMemorialWallPetNameVisibility() {
        if (!this.companionNameField) return;
        const optedIn = this.state.memorialWall.optIn === true;

        this.companionNameField.hidden = !optedIn;
        if (this.companionNameInput) this.companionNameInput.disabled = !optedIn;

        this.syncCompanionNameFromEngraved();
      }

      // Phase 4D.10C, Parts 13-17 — Memorial Wall's contextual Favorite
      // Photo subsection. A no-op when the feature flag is off (both
      // cached elements null, matching the empty-array/null convention
      // every other Picture Frame element uses). Exactly one of the two
      // blocks is ever shown, driven purely by whether the ONE canonical
      // selectedPhotoFile currently exists — completely independent of
      // which step (4 or 5) it was chosen from, and independent of
      // whether Picture Frame itself is selected (Scenario 2: no frame,
      // no Step 4 photo opportunity, Memorial Wall still offers one).
      updateMemorialWallPhotoSection() {
        if (!this.memorialWallPhotoExisting || !this.memorialWallPhotoOptional) return;

        const hasPhoto = Boolean(this.selectedPhotoFile);
        this.memorialWallPhotoExisting.hidden = !hasPhoto;
        this.memorialWallPhotoOptional.hidden = hasPhoto;
      }

      // ----- Phase 2.2: guided-journey accordion -----

      initAccordion() {
        this.accordions.forEach((details) => {
          details.addEventListener('toggle', () => {
            if (!details.open) {
              details.classList.add('is-visited');
              this.updateSectionSummary(details);
            }
            // Phase 7H.7 — native Builder journey analytics. Every
            // section that just BECAME open is a genuine navigation:
            // Continue, the mobile forward-dock's real-button relay,
            // the mobile Back control, progress-rail taps
            // (navigateToStep()'s own `.open` assignments), AND
            // desktop's native direct-summary-click all funnel through
            // this SAME toggle event — there is no second navigation
            // path this file exposes. A validation-blocked Continue tap
            // never reaches here at all (isStepValid() gates BEFORE
            // navigateToStep() is ever called, so `.open` never
            // changes and no toggle ever fires) — see
            // recordBuilderStepActive()'s own comment for the rest of
            // the semantics.
            if (details.open) {
              this.recordBuilderStepActive(details);
            }
            if (details.dataset.accordionSection === 'review' && details.open) {
              this.updateReviewSummary();
              // Phase 4D.9B.7, Parts 7-11: best-effort early canvas
              // preparation trigger — see maybePrepareMemorialCanvas()'s
              // own comment for why Review-open (a single, low-frequency,
              // near-purchase-intent signal) was chosen over debouncing
              // every canvas-affecting input. Never lets a preparation
              // failure interrupt Review from opening normally.
              try {
                this.maybePrepareMemorialCanvas();
                // Phase 4D.10D: also starts the early UPLOAD, not just
                // the render — see maybePersistMemorialCanvas()'s own
                // comment for why this is what actually removes the
                // Wall=Yes/no-photo click-time delay.
                // Phase 5D.1D — Review-open is one of exactly two
                // moments (the other is Add to Cart) that ever trigger a
                // remote /apps/memorial/image upload at all — see
                // maybePersistMemorialCanvas()'s own comment.
                this.maybePersistMemorialCanvas();
              } catch (error) {
                // Best-effort only — Add to Cart always still renders
                // fresh if this failed for any reason.
              }
              // Phase 5.4: mobile-only Review-time engraving render — see
              // renderEngravingForMobileReview()'s own comment. Desktop is
              // excluded here deliberately: it already renders
              // continuously via updateEngravingFromState() on every Pet
              // Name/color change, so an extra render on Review's own
              // toggle would be redundant work, not a behavior change,
              // but skipping it keeps desktop's trigger set exactly as
              // approved in Phase 5.3.x — untouched.
              if (!this.isDesktopViewport()) {
                this.renderEngravingForMobileReview();
              }
            }
            // Every toggle (opening OR closing, whichever section) can change
            // which item is "current" — e.g. clicking Edit on an earlier
            // summary card closes whatever was open elsewhere via the native
            // exclusive <details name> grouping, firing a toggle on that
            // section too. Recomputing from scratch each time keeps this
            // trivially correct regardless of navigation path.
            this.updateJourneyNav();
            this.updateCurrentStepDisplay();
            this.renumberStepper();
            // Phase 5C.1B — every open/close is exactly the "which card
            // is current" signal the mobile deck/rail needs too;
            // self-guarded no-op on desktop.
            this.updateMobileNav();
            // Whether Review just opened or closed (or another section
            // opened, implicitly closing Review via the native exclusive
            // <details name> grouping) — this is the one signal
            // updateCanvasPosition() needs on mobile. See its own comment.
            this.updateCanvasPosition();
            // Bug fix (second mobile Product Details attempt): this call
            // site was missing entirely — updateProductDetailsPosition()
            // was only wired into connectedCallback() and the resize
            // handler, mirroring updateCanvasPosition()'s OTHER two call
            // sites but missing this third one. Toggling the Shopify
            // Theme Editor's device-preview switcher does not reliably
            // dispatch a real `resize` event inside this custom
            // element's own window, so resize alone was never a
            // sufficient signal — every accordion open/close (which is
            // how a customer actually interacts with the guided flow, on
            // mobile or in preview) is what actually re-evaluates
            // correctly, since matchMedia() is read fresh on every call,
            // never cached from connection time.
            this.updateProductDetailsPosition();
            try {
              this.updateAdditionalPhotosMobileTriggerPosition();
            } catch (error) {
              // See the connectedCallback() comment near
              // bindPrePurchaseImageUpload() for why this is isolated.
            }
          });
        });
      }

      // ----- Phase 2.5: Review lock -----
      // <details>'s native `toggle` event is not cancelable, so the lock
      // has to intercept `click` on the <summary> itself, before the
      // native open/close behavior applies. This also blocks keyboard
      // activation (Enter/Space on a focused <summary> dispatches a
      // synthetic click first) — a single guard covers both input modes.

      initReviewLock() {
        if (!this.reviewSummaryToggle) return;
        this.reviewSummaryToggle.addEventListener('click', (event) => {
          if (this.state.review.locked) {
            event.preventDefault();
          }
        });
      }

      updateReviewLockState() {
        if (!this.reviewDetails) return;
        const shouldLock = !this.isBuilderComplete();
        this.state.review.locked = shouldLock;
        this.reviewDetails.classList.toggle('is-locked', shouldLock);

        if (shouldLock && this.reviewDetails.open) {
          this.reviewDetails.open = false;
        }
      }

      // ----- Phase 2.5.2: Step 1 / Step 2 gating -----
      // Reuses the exact mechanism already proven for Review's own lock
      // above: a `click` guard on the <summary> (not the non-cancelable
      // `toggle` event — the same guard blocks keyboard activation too,
      // since Enter/Space on a focused <summary> dispatch a synthetic
      // click first). Style is locked until Step 1 is complete;
      // Personalize/Photo/Memorial Wall are locked until Step 1 AND Step 2
      // are both complete. Review keeps its own, separate,
      // already-correct full-completion lock above — untouched.

      initStepLocks() {
        ['style', 'personalize', 'photo', 'memorial-wall'].forEach((key) => {
          const details = this.accordions.find((item) => item.dataset.accordionSection === key);
          if (!details) return;
          const summary = details.querySelector('.bpf-accordion-summary');
          if (!summary) return;
          summary.addEventListener('click', (event) => {
            if (details.classList.contains('is-locked')) {
              event.preventDefault();
            }
          });
        });
      }

      updateStepLocks() {
        const step1Complete = this.isStep1Complete();
        const step2Complete = this.isStep2Complete();

        this.applyStepLock('style', !step1Complete, 'Complete Step 1 to continue.');

        const laterLocked = !step1Complete || !step2Complete;
        const laterMessage = !step1Complete ? 'Complete Step 1 to continue.' : 'Complete Step 2 to continue.';
        ['personalize', 'photo', 'memorial-wall'].forEach((key) => {
          this.applyStepLock(key, laterLocked, laterMessage);
        });

        // A force-close inside applyStepLock() fires the native `toggle`
        // listener synchronously, which reads is-visited to update the
        // stepper nav BEFORE applyStepLock() removes it — updateJourneyNav()
        // needs one more pass here once every card's final is-visited/
        // is-locked state has actually settled, or the nav dot could be
        // left showing "completed" for a step that just got relocked.
        this.updateJourneyNav();
      }

      // A card that was previously visited (showing its "✓ completed"
      // summary — see .bpf-accordion.is-visited:not([open]) in the CSS)
      // and later becomes relocked would otherwise hide the lock message
      // entirely, since that same rule hides the heading block the message
      // lives in. Removing is-visited here keeps the plain heading
      // (dimmed title + message) visible instead of a misleadingly
      // "completed-looking" but secretly-unclickable card — it also
      // correctly reverts that step's stepper-nav dot from "completed"
      // back to "upcoming" (updateJourneyNav() reads the same class).
      applyStepLock(key, shouldLock, message) {
        const details = this.accordions.find((item) => item.dataset.accordionSection === key);
        if (!details) return;

        // Order matters: closing an open <details> fires a native `toggle`
        // synchronously, and the existing initAccordion() listener
        // unconditionally re-adds `is-visited` whenever a section closes
        // (that's how the normal "✓ completed" card state gets set). If
        // is-visited were removed BEFORE that close, the toggle listener
        // would immediately re-add it, undoing the removal. Closing first
        // means the removal below is the last word.
        if (shouldLock && details.open) details.open = false;

        details.classList.toggle('is-locked', shouldLock);

        if (shouldLock) {
          details.classList.remove('is-visited');
        }

        const messageEl = details.querySelector('[data-step-locked-message]');
        if (messageEl) messageEl.textContent = shouldLock ? message : '';
      }

      // ----- Phase 7G.1: mobile — the active step can't be collapsed -----
      // Real-device QA: every accordion section shares one exclusive
      // `name` attribute (native <details name>), so tapping the only
      // currently-open section's own <summary> just closes it — with no
      // other section auto-opening to replace it, leaving the whole
      // mobile Builder blank. Mobile already has dedicated Back/
      // Continue/Add to Cart controls for navigation, so collapsing the
      // active step serves no purpose there. Reuses the exact proven
      // technique initStepLocks()/initReviewLock() already use above: a
      // `click` guard on <summary> (not the non-cancelable `toggle`
      // event), which also blocks keyboard activation for the same
      // reason documented there. isDesktopViewport() is read fresh
      // inside the handler (not cached at init time), matching every
      // other live breakpoint check in this file, so this correctly
      // reflects orientation changes/Theme Editor device-preview
      // switching, not just the viewport at page-load time. Only ever
      // prevents the default action when the clicked summary's OWN
      // details is already open — clicking a DIFFERENT (closed)
      // section's summary is completely unaffected, so jumping directly
      // to another unlocked step by tapping its heading still works
      // exactly as before; the native exclusive-group swap (closing the
      // old open section, opening the new one) still always leaves
      // exactly one section open, never zero. Desktop is untouched: its
      // own summary click always proceeds to the native toggle, so any
      // intentional desktop collapse/accordion behavior is preserved.
      initMobileNoCollapse() {
        this.accordions.forEach((details) => {
          const summary = details.querySelector('.bpf-accordion-summary');
          if (!summary) return;
          summary.addEventListener('click', (event) => {
            if (!this.isDesktopViewport() && details.open) {
              event.preventDefault();
            }
          });
        });
      }

      // ----- Phase 2.5: visible-step authority + dynamic stepper -----
      // getVisibleSteps() filters by the native `hidden` attribute — no
      // parallel visibility bookkeeping needed, since
      // updateStyleCardVisibility() already sets `hidden` directly on the
      // one step that can ever be skipped.

      getVisibleSteps() {
        return this.accordions.filter((details) => !details.hidden);
      }

      renumberStepper() {
        const visibleSteps = this.getVisibleSteps();

        this.journeyItems.forEach((item) => {
          const key = item.dataset.journeyItem;
          const stepIndex = visibleSteps.findIndex((details) => details.dataset.accordionSection === key);
          const isVisible = stepIndex !== -1;
          item.hidden = !isVisible;
          if (isVisible) {
            const circle = item.querySelector('.bpf-journey-stepper__circle');
            if (circle) circle.textContent = String(stepIndex + 1);
          }
        });
      }

      // ----- Phase 7H.7: native Builder journey analytics -----
      //
      // Five first-party GA4 events (bpf_builder_exposure/step_view/
      // step_advance/step_back/review_reached — deliberately distinct
      // from the pre-existing, analytically separate
      // confirmed_builder_start), fired through whatever GA4 tag this
      // storefront already has loaded. This repository has no gtag.js/
      // dataLayer/Web Pixel code of its own anywhere (audited before
      // writing this) — that is expected, not a gap: Shopify stores
      // typically get GA4's gtag.js injected storefront-wide by the
      // admin-side "Google & YouTube" sales channel (or an equivalent
      // Web Pixel extension), entirely outside theme source, which is
      // exactly the ambient `window.gtag` trackBuilderEvent() below
      // reuses. This is deliberately NOT a second analytics library, no
      // Measurement ID is hard-coded anywhere in this file, and no
      // consent logic is touched — an ordinary gtag('event', ...) call
      // is already subject to whatever consent state gtag.js itself is
      // enforcing, the same as any other event on this storefront.
      //
      // Non-PII only (Part 15 of the phase spec): every payload below is
      // built from entry_mode/animal_type/breed/memorial_style/step
      // identifiers only — never Pet Name, engraving text, uploaded
      // photo data, or any other free-form customer input.

      // The one non-obvious piece of state this feature adds:
      // "which step did the last recorded step_view belong to," so a
      // later step_view can be classified as an advance or a back
      // transition purely by comparing visible-step indexes — see
      // recordBuilderStepActive() below, the single call site that
      // both reads and writes it.
      getBuilderAnalyticsContext() {
        return {
          entry_mode: this.entryMode || 'full',
          animal_type: (this.state.companion && this.state.companion.type) || null,
          breed: (this.state.breed && this.state.breed.value) || null,
          memorial_style: this.currentUrnStyle || null,
        };
      }

      // step_name deliberately reads the step's own real, currently-
      // rendered <summary> title (the exact same
      // `.bpf-accordion-summary__title` text updateMobileNav()'s status
      // line already reads) rather than the short mobile CTA label above
      // — that text is dynamic per product (e.g. Add-ons reads "Add a
      // Memorial Picture Frame" or "Share a Favorite Photo" depending on
      // this product's own photo-upload capability) and is the more
      // useful, still entirely non-PII, analytics dimension.
      getStepAnalyticsMeta(details, index) {
        const titleEl = details.querySelector('.bpf-accordion-summary__title');
        return {
          id: details.dataset.accordionSection || '',
          index,
          name: titleEl ? titleEl.textContent.trim() : '',
        };
      }

      // Fire-and-forget, defensive by construction (Part 29 of the phase
      // spec: tracking must never block or fail the Builder). Strips
      // null/undefined/empty-string fields rather than sending them —
      // GA4 event params are meant to be omitted, not sent empty, when a
      // value hasn't resolved yet (Part 14: "do not fabricate unknown
      // values"). Returns true only when window.gtag was actually
      // called without throwing — never merely "an attempt was made"
      // (Phase 7H.7C, Part 8 of that phase's spec).
      dispatchBuilderEvent(eventName, payload) {
        try {
          if (typeof window.gtag !== 'function') return false;
          const cleanPayload = {};
          Object.keys(payload || {}).forEach((key) => {
            const value = payload[key];
            if (value !== null && value !== undefined && value !== '') cleanPayload[key] = value;
          });
          window.gtag('event', eventName, cleanPayload);
          return true;
        } catch (error) {
          // Analytics must never block or break the Builder itself.
          return false;
        }
      }

      // Phase 7H.7C — the real fix for the breed-collection defect: a
      // fresh direct landing can reach connectedCallback() (and this
      // method, via initBuilderAnalytics()) before Shopify's own GA4 tag
      // has defined window.gtag yet. The OLD behavior called what is
      // now dispatchBuilderEvent() directly and treated a silent
      // false/no-op exactly like success — the caller's dedupe flags
      // (_bpfAnalyticsExposureFired, etc.) were already committed, so
      // the event was gone forever the instant gtag wasn't ready yet.
      //
      // This method never lets that happen: a failed dispatch is queued
      // in this._bpfAnalyticsPending instead of discarded, and BEFORE
      // sending whatever event was just requested, it first flushes any
      // already-queued ones if the transport is ready now — so once
      // gtag genuinely exists, delivery order always matches logical
      // order (e.g. the queued exposure + initial step_view arrive
      // before whatever later navigation event triggered the flush —
      // Part 6 of the phase spec's own expected sequence). A bounded
      // timer (scheduleBuilderAnalyticsRetry()) also polls readiness on
      // its own, so a pending event still arrives even if the customer
      // never triggers another event at all. Callers never need to
      // change: this has the exact same signature/call shape as the
      // dispatchBuilderEvent() it replaces as the analytics entry point.
      trackBuilderEvent(eventName, payload) {
        if (typeof window.gtag === 'function' && this._bpfAnalyticsPending.length) {
          this.flushPendingBuilderAnalytics();
        }
        const delivered = this.dispatchBuilderEvent(eventName, payload);
        if (!delivered) {
          this._bpfAnalyticsPending.push({ eventName, payload });
          this.scheduleBuilderAnalyticsRetry();
        }
        return delivered;
      }

      // Attempts every still-pending event, in the order it was
      // originally requested (a plain array push/iterate is already
      // FIFO — exposure before the initial step_view, matching Part 6's
      // expected sequence with no extra bookkeeping). Whatever still
      // fails (gtag disappearing again mid-flush — not expected in
      // practice) stays queued for the next opportunity rather than
      // being lost.
      flushPendingBuilderAnalytics() {
        if (!this._bpfAnalyticsPending.length) return;
        const queued = this._bpfAnalyticsPending;
        this._bpfAnalyticsPending = [];
        queued.forEach(({ eventName, payload }) => {
          const delivered = this.dispatchBuilderEvent(eventName, payload);
          if (!delivered) this._bpfAnalyticsPending.push({ eventName, payload });
        });
      }

      // Bounded, self-terminating retry (Part 5 of the phase spec: "do
      // not create an unbounded polling loop") — at most
      // BPF_ANALYTICS_RETRY_MAX_ATTEMPTS checks, BPF_ANALYTICS_RETRY_INTERVAL_MS
      // apart, and only ever one timer in flight per instance (the
      // guard below no-ops a call while one is already scheduled, so
      // queuing several events in the same tick — exposure AND the
      // initial step_view, e.g. — schedules exactly one timer, not one
      // per event). Once the attempt budget is exhausted the timer
      // simply stops rescheduling itself; anything still in
      // _bpfAnalyticsPending is NOT discarded — trackBuilderEvent()'s
      // own opportunistic flush on the next real Builder event remains
      // a second, timer-free chance for it to reach gtag, exactly like
      // the live breed session where the fix's flush-on-next-event path
      // alone would already have been enough (gtag was ready roughly
      // 10 seconds before the customer's own first navigation).
      scheduleBuilderAnalyticsRetry() {
        if (this._bpfAnalyticsRetryTimer) return;
        if (this._bpfAnalyticsRetryAttempts >= BPF_ANALYTICS_RETRY_MAX_ATTEMPTS) return;
        this._bpfAnalyticsRetryTimer = setTimeout(() => {
          this._bpfAnalyticsRetryTimer = null;
          this._bpfAnalyticsRetryAttempts += 1;
          if (typeof window.gtag === 'function') {
            this.flushPendingBuilderAnalytics();
          }
          if (this._bpfAnalyticsPending.length) {
            this.scheduleBuilderAnalyticsRetry();
          }
        }, BPF_ANALYTICS_RETRY_INTERVAL_MS);
      }

      // The single call site for bpf_builder_step_view/step_advance/
      // step_back/review_reached — called from exactly two places:
      // initBuilderAnalytics() once for whichever step is open at
      // startup (no "from," so never an advance/back — just the first
      // view), and initAccordion()'s own toggle listener for every
      // subsequent genuine navigation (see that listener's own comment
      // for why it alone is sufficient coverage for Continue, mobile
      // Back/forward-dock, progress-rail taps, AND desktop's native
      // direct-summary-click, uniformly, with no per-control duplicate
      // logic). Because it only ever runs when a step's `open` state
      // has genuinely just become true, a validation-blocked Continue
      // tap can never reach this method at all (Part 21 of the phase
      // spec) — there is nothing here that re-checks validity, because
      // there is nothing left to check by the time this runs.
      //
      // Phase 7H.7E — those "exactly two places" can both legitimately
      // fire for the SAME initial step in breed/product mode. Unlike
      // Full mode (the first card is already `open` via the static
      // HTML attribute — no script ever assigns `.open` to it, so no
      // `toggle` event is ever generated for it at all), breed/product
      // mode's initBreedEntryContext()/initProductEntryContext() (both
      // run BEFORE initAccordion() attaches its listeners — see their
      // own ordering comment) explicitly set
      // `firstVisible.open = true;` via script. Per the HTML `<details>`
      // spec, a script-driven open-state change queues its `toggle`
      // event rather than firing it synchronously — so that event
      // still fires later, AFTER initAccordion() has since attached its
      // listener, landing on the exact same details element
      // initBuilderAnalytics()'s own explicit call already recorded
      // moments earlier during connectedCallback(). Live GA4/BigQuery
      // verification confirmed the resulting duplicate: an identical
      // bpf_builder_step_view(style, index 0) firing twice in a row on
      // fresh breed-collection landings, with exposure and later
      // navigation both unaffected (neither has an equivalent
      // dual-trigger path). The guard below is the single authoritative
      // fix, at the one shared call site both paths already funnel
      // through — see its own comment for why it can never suppress a
      // legitimate revisit.
      recordBuilderStepActive(details) {
        const visibleSteps = this.getVisibleSteps();
        const toIndex = visibleSteps.indexOf(details);
        if (toIndex === -1) return;

        // The exact same details element recorded as current with NO
        // intervening change is a duplicate signal, not a second
        // logical transition (Part 5 of the phase spec: dedupe by
        // active-step TRANSITION, never a permanent "already seen this
        // step" flag). A genuine revisit is unaffected: by the time a
        // customer navigates Back into `details`, this field holds
        // whatever step they most recently left — never `details`
        // itself — so this only ever blocks an exact immediate repeat
        // of the step already on record, never a later, real return to
        // it.
        if (details === this._bpfAnalyticsCurrentStepDetails) return;

        const toMeta = this.getStepAnalyticsMeta(details, toIndex);
        const context = this.getBuilderAnalyticsContext();
        const fromDetails = this._bpfAnalyticsCurrentStepDetails;

        if (fromDetails && fromDetails !== details) {
          const fromIndex = visibleSteps.indexOf(fromDetails);
          if (fromIndex !== -1 && fromIndex !== toIndex) {
            const fromMeta = this.getStepAnalyticsMeta(fromDetails, fromIndex);
            const transitionPayload = Object.assign({}, context, {
              from_step_id: fromMeta.id,
              from_step_index: fromMeta.index,
              from_step_name: fromMeta.name,
              to_step_id: toMeta.id,
              to_step_index: toMeta.index,
              to_step_name: toMeta.name,
            });
            this.trackBuilderEvent(toIndex > fromIndex ? 'bpf_builder_step_advance' : 'bpf_builder_step_back', transitionPayload);
          }
        }

        this._bpfAnalyticsCurrentStepDetails = details;

        this.trackBuilderEvent('bpf_builder_step_view', Object.assign({}, context, {
          step_id: toMeta.id,
          step_index: toMeta.index,
          step_name: toMeta.name,
        }));

        // Once per instance (Part 13 of the phase spec) — separate from
        // Add to Cart/checkout/purchase, which are not this file's
        // concern.
        if (details.dataset.accordionSection === 'review' && !this._bpfAnalyticsReviewReachedFired) {
          this._bpfAnalyticsReviewReachedFired = true;
          this.trackBuilderEvent('bpf_builder_review_reached', Object.assign({}, context, {
            step_id: toMeta.id,
            step_index: toMeta.index,
            step_name: toMeta.name,
          }));
        }
      }

      // Called once from connectedCallback(), after every other init
      // above has settled — exposure means "the actual Builder UI has
      // initialized/rendered and is available to the visitor" (Part 9 of
      // the phase spec), which is equally true whether this instance is
      // the normal Memorial Builder page, a breed collection page, or a
      // product-mode render: all three are this exact same custom
      // element, distinguished only by entryMode (already included in
      // getBuilderAnalyticsContext()) — no URL pattern matching needed
      // or used. Deliberately does NOT refire on option/color/canvas
      // changes or resize (Part 9/24) — nothing outside connectedCallback()
      // ever calls this.
      initBuilderAnalytics() {
        if (this._bpfAnalyticsExposureFired) return;
        this._bpfAnalyticsExposureFired = true;

        this.trackBuilderEvent('bpf_builder_exposure', this.getBuilderAnalyticsContext());

        const visibleSteps = this.getVisibleSteps();
        const currentDetails = visibleSteps.find((details) => details.open);
        if (currentDetails) {
          this.recordBuilderStepActive(currentDetails);
        }
      }

      // Phase 5C.1B — mobile-only step deck + bottom nav rail. A pure
      // read-out, exactly like renumberStepper()/updateJourneyNav() above
      // (getVisibleSteps() + `.open`/`.is-visited` are the only inputs;
      // no new state is tracked). Desktop-safe no-op via the same
      // isDesktopViewport() guard renderEngravingForMobileReview()/
      // syncEngravingViewportTransition() already use elsewhere in this
      // file — never runs any of the logic below at >=750px, so it can be
      // called unconditionally from every place renumberStepper() already
      // is without any desktop risk.
      updateMobileNav() {
        if (this.isDesktopViewport()) return;

        const visibleSteps = this.getVisibleSteps();
        const currentDetails = visibleSteps.find((details) => details.open);
        const currentIndex = currentDetails ? visibleSteps.indexOf(currentDetails) : -1;

        // 1. Which card is current. Phase 7H.4: these three classes no
        // longer drive any horizontal transform/translateX geometry —
        // real-device iOS Safari QA (Phase 7H.3) traced a live document-
        // panning defect to the off-screen swipe-deck cards that
        // geometry depended on, so mobile navigation no longer keeps
        // neighboring steps positioned outside the viewport at all. The
        // mobile-only CSS now simply hides .bpf-mobile-card--before/
        // --after entirely (display: none — no box, no geometry, not
        // focusable, contributes nothing to scrollable overflow) and
        // shows only .bpf-mobile-card--current, in its normal inset:0
        // position with no transform. Still computed here (not left to
        // native <details>/<summary> alone) because "before" (already
        // completed) vs "after" (not yet reached) remains a meaningful
        // distinction this classification already provides for free —
        // removing it would need re-deriving the same information
        // elsewhere for no benefit.
        visibleSteps.forEach((details, index) => {
          details.classList.remove('bpf-mobile-card--before', 'bpf-mobile-card--current', 'bpf-mobile-card--after');
          if (index === currentIndex) {
            details.classList.add('bpf-mobile-card--current');
          } else if (index < currentIndex) {
            details.classList.add('bpf-mobile-card--before');
          } else {
            details.classList.add('bpf-mobile-card--after');
          }
        });

        // 2. Current-step status line ("2 of 5 · Personalize").
        if (this.mobileNavStatus && currentDetails) {
          const titleEl = currentDetails.querySelector('.bpf-accordion-summary__title');
          const title = titleEl ? titleEl.textContent.trim() : '';
          this.mobileNavStatus.textContent = `${currentIndex + 1} of ${visibleSteps.length} · ${title}`;
        }

        // 3. Back control — Part 13: hidden/disabled on the first
        // reachable visible step, otherwise navigates to whichever
        // visible step immediately precedes the current one (always
        // `.is-visited` by construction — a step can't be current without
        // every step before it having been visited at least once).
        if (this.mobileNavBack) {
          this.mobileNavBack.disabled = currentIndex <= 0;
        }

        // 3b. Forward control (Part 1/4/25) — label switches to "Add to
        // Cart" only on Review; the button itself stays enabled/tappable
        // on every OTHER step regardless of current validity (Part 25 —
        // disabling it here would silently swallow the customer's tap
        // instead of letting the real [data-continue] control's own
        // click handler show its existing error/focus feedback, which
        // is exactly what initMobileNav()'s forward click handler
        // forwards to). On Review, it mirrors the real Add to Cart
        // button's own disabled state (e.g. sold out) — never a second,
        // independent notion of availability.
        if (this.mobileNavForward) {
          const isReviewCurrent = Boolean(currentDetails && currentDetails.dataset.accordionSection === 'review');
          // Phase 7H.7 — mobile-only forward CTA describes the actual
          // next visible step ("Next: Personalize") rather than a
          // generic "Continue", using the SAME visibleSteps/currentIndex
          // this method already computed above — never a second,
          // possibly-divergent notion of what step comes next. Falls
          // back to plain "Continue" only if there's no eligible next
          // step to name (defensive; not expected to be reachable in
          // steady state, since isReviewCurrent already covers the real
          // last-step case). Desktop is completely unaffected: this
          // whole method returns early on desktop (see the guard at the
          // top), and the per-card [data-continue] buttons this label
          // never touches keep their own static "Continue" text.
          let forwardLabel = 'Continue';
          if (isReviewCurrent) {
            forwardLabel = 'Add to Cart';
          } else {
            const nextDetails = visibleSteps[currentIndex + 1];
            const nextKey = nextDetails ? nextDetails.dataset.accordionSection : null;
            const nextShortLabel = nextKey ? BPF_MOBILE_NEXT_STEP_LABELS[nextKey] : null;
            if (nextShortLabel) forwardLabel = `Next: ${nextShortLabel}`;
          }
          if (this.mobileNavForwardLabel) this.mobileNavForwardLabel.textContent = forwardLabel;
          this.mobileNavForward.setAttribute('aria-label', isReviewCurrent ? 'Add to Cart' : 'Continue to next step');
          this.mobileNavForward.disabled = isReviewCurrent ? Boolean(this.submitButton && this.submitButton.disabled) : false;
        }

        // 4. Progress rail — Part 23: reached (current or `.is-visited`)
        // visible steps are enabled/tappable; unreached ones stay
        // disabled; hidden accordion sections stay hidden here too
        // (mirrors renumberStepper()'s own journeyItems.hidden pattern
        // above).
        this.mobileNavPoints.forEach((point) => {
          const key = point.dataset.mobileNavItem;
          const stepIndex = visibleSteps.findIndex((details) => details.dataset.accordionSection === key);
          const isVisible = stepIndex !== -1;
          point.hidden = !isVisible;
          if (!isVisible) return;

          const details = visibleSteps[stepIndex];
          const isCurrent = stepIndex === currentIndex;
          const isReached = isCurrent || details.classList.contains('is-visited');

          point.classList.toggle('is-current', isCurrent);
          point.classList.toggle('is-completed', !isCurrent && isReached);
          point.disabled = !isReached;
          if (isCurrent) {
            point.setAttribute('aria-current', 'step');
          } else {
            point.removeAttribute('aria-current');
          }
        });
      }

      // Phase 5C.1B, Part 11 — the pure TRUE/FALSE decision extracted from
      // initContinueButtons()'s own inline checks below, so mobile
      // swipe-left forward progression can gate on the EXACT SAME
      // decision Continue already uses, with zero duplicated validator
      // logic (isCompanionTypeValid()/isBreedValid()/etc. below are the
      // single source of truth either way — this only centralizes the
      // per-step key -> validator(s) mapping, not the validators
      // themselves). Deliberately has no side effects (no focus, no
      // error messaging) — those stay exactly where they already are, in
      // the Continue click handler below, since swipe failing to advance
      // is a silent no-op (Part 15: "do not advance"), not a second place
      // that shows the same error UI.
      isStepValid(details) {
        const key = details.dataset.accordionSection;
        if (key === 'companion') return this.isCompanionTypeValid() && this.isBreedValid();
        if (key === 'style') return this.isMemorialStyleValid();
        if (key === 'personalize') return this.isPersonalizeValid();
        if (key === 'memorial-wall') return this.isCompanionNameValid();
        return true;
      }

      // Phase 5C.1B, Part 10 — the ONE shared navigation primitive for
      // Continue/Next, mobile swipe forward/backward, the explicit mobile
      // Back control, and completed-progress-point taps. Pure navigation
      // only — it never validates (callers decide: Continue/swipe-left
      // both call isStepValid() first; backward callers rely on
      // `.is-visited` below) and never rebuilds/reparents any card, so
      // existing input values/listeners are completely undisturbed —
      // setting `.open` on a native <details name="..."> is the ONLY
      // state change here, exactly like the pre-existing Continue flow
      // already did. Firing the native `toggle` event is what already
      // drives every downstream update (updateJourneyNav(),
      // updateCurrentStepDisplay(), renumberStepper(),
      // updateCanvasPosition(), updateProductDetailsPosition(),
      // updateMobileNav()) — see initAccordion()'s own toggle listener —
      // so this method doesn't need to call any of them itself.
      //
      // Forward is intentionally restricted to exactly one step at a time
      // (targetIndex === currentIndex + 1) — matching Continue's own
      // existing behavior — so nothing can ever jump forward through a
      // required step. Backward may target ANY already-`.is-visited`
      // visible step (a direct multi-step jump), matching the approved
      // "tap a completed progress point" behavior.
      navigateToStep(targetDetails) {
        if (!targetDetails || targetDetails.hidden) return;

        const visibleSteps = this.getVisibleSteps();
        const currentDetails = visibleSteps.find((details) => details.open);
        if (!currentDetails || currentDetails === targetDetails) return;

        const currentIndex = visibleSteps.indexOf(currentDetails);
        const targetIndex = visibleSteps.indexOf(targetDetails);
        if (targetIndex === -1) return;

        if (targetIndex > currentIndex) {
          if (targetIndex !== currentIndex + 1) return;
        } else if (!targetDetails.classList.contains('is-visited')) {
          return;
        }

        currentDetails.open = false;
        targetDetails.open = true;
      }

      initContinueButtons() {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.accordions.forEach((details) => {
          const continueButton = details.querySelector('[data-continue]');
          if (!continueButton) return;

          continueButton.addEventListener('click', () => {
            const key = details.dataset.accordionSection;

            // Phase 5C.1B, Part 11 — isStepValid() (above) is now the
            // single shared gate; the per-key branches below are UNCHANGED
            // from before this phase and only run on the failure path, to
            // decide which error message/focus target applies (isStepValid()
            // itself has no side effects, so it's safe to have already
            // called the same underlying validators once above).
            if (!this.isStepValid(details)) {
              // Phase 5C.1F, Part 31 — every branch below keeps its
              // EXISTING desktop-and-mobile .focus() call unchanged, and
              // additionally (mobile-only — revealInCardQuestion() self-
              // guards) brings the same invalid control into view within
              // the card, so the fixed dock's Continue button reliably
              // surfaces whatever it is the customer still needs to
              // answer, without a manual scroll.
              if (key === 'companion') {
                if (!this.isCompanionTypeValid()) {
                  this.updateAddToCartState({ showCompanionTypeError: true });
                  if (this.companionTypeInputs[0]) this.companionTypeInputs[0].focus();
                  this.revealInCardQuestion(details, this.companionTypeInputs[0]);
                  return;
                }
                if (!this.isBreedValid()) {
                  this.updateAddToCartState({ showBreedError: true });
                  if (this.breedSelect) this.breedSelect.focus();
                  this.revealInCardQuestion(details, this.breedSelect);
                  return;
                }
              }

              if (key === 'style') {
                if (this.getEligibleStyleCount() === 2 && this.urnStyleInputs[0]) {
                  this.urnStyleInputs[0].focus();
                  this.revealInCardQuestion(details, this.urnStyleInputs[0]);
                }
                return;
              }

              if (key === 'personalize') {
                this.updateAddToCartState({ showPetNameError: true });
                if (this.currentStyleSupportsEngraving() && this.petNameInput && !this.isPetNameValid()) {
                  this.petNameInput.focus();
                  this.revealInCardQuestion(details, this.petNameInput);
                }
                return;
              }

              if (key === 'memorial-wall') {
                this.updateAddToCartState({ showCompanionNameError: true });
                if (this.companionNameInput) this.companionNameInput.focus();
                this.revealInCardQuestion(details, this.companionNameInput);
                return;
              }

              return;
            }

            const visibleSteps = this.getVisibleSteps();
            const currentIndex = visibleSteps.indexOf(details);
            const next = visibleSteps[currentIndex + 1];
            if (!next) return;
            this.navigateToStep(next);
            // Phase 5C.1B: on mobile the cards no longer live in normal
            // vertically-stacked document flow (they're absolutely
            // positioned inside the deck — see the mobile-only CSS), so
            // scrolling the page to the next card's own position no
            // longer means anything there — and the deck's own CSS
            // transition already shows the transition. Desktop is
            // completely unaffected: normal document flow, unchanged
            // scrollIntoView behavior.
            if (this.isDesktopViewport()) {
              next.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
            }
          });
        });
      }

      // Phase 7H.4 — EMERGENCY MOBILE STABILIZATION: horizontal swipe-
      // to-navigate between Builder steps has been removed entirely.
      // Repeated real-device iOS Safari QA (Phase 7H.3) found the
      // horizontally-transformed off-screen swipe-deck cards this
      // gesture depended on were the underlying cause of a live
      // document-panning defect that survived multiple successive
      // containment fixes — rather than continuing to layer more
      // overflow/clipping patches around that architecture, mobile
      // navigation is now driven exclusively by the existing Back/
      // Continue/Add to Cart/progress-rail controls below, none of
      // which ever depended on touch/swipe state to begin with. The
      // former touchstart/touchmove/touchend/touchcancel listeners on
      // this.accordionList (Phase 5C.1B, Parts 15-18) are gone — see
      // updateMobileNav()'s own comment for how inactive cards are now
      // hidden without any transform/translateX geometry.
      initMobileNav() {
        if (this.mobileNavBack) {
          this.mobileNavBack.addEventListener('click', () => {
            const visibleSteps = this.getVisibleSteps();
            const current = visibleSteps.find((details) => details.open);
            if (!current) return;
            const currentIndex = visibleSteps.indexOf(current);
            const previous = visibleSteps[currentIndex - 1];
            if (!previous) return;
            this.navigateToStep(previous);
          });
        }

        // Phase 5C.1D, Part 1/2/4 — the dock's forward control is a pure
        // relay, never a second Continue/Add to Cart implementation: it
        // finds the current card's own REAL [data-continue] button (or,
        // on Review, the form's own REAL submit button) and calls
        // .click() on it — the exact same trusted click event a manual
        // tap on that (now mobile-hidden but still fully functional,
        // never removed — Part 3) control would dispatch, so every
        // existing validation/error/focus/sold-out check applies
        // completely unmodified. A disabled real button (e.g. sold out)
        // simply no-ops .click(), matching native behavior exactly.
        if (this.mobileNavForward) {
          this.mobileNavForward.addEventListener('click', () => {
            const visibleSteps = this.getVisibleSteps();
            const current = visibleSteps.find((details) => details.open);
            if (!current) return;

            if (current.dataset.accordionSection === 'review') {
              if (this.submitButton) this.submitButton.click();
              return;
            }

            const continueButton = current.querySelector('[data-continue]');
            if (continueButton) continueButton.click();
          });
        }

        this.mobileNavPoints.forEach((point) => {
          point.addEventListener('click', () => {
            const key = point.dataset.mobileNavItem;
            const target = this.accordions.find((details) => details.dataset.accordionSection === key);
            if (target) this.navigateToStep(target);
          });
        });
      }

      // ===== Phase 5E.1A (extended by 5E.1B) — mobile-only proximity-
      // based whole-Builder viewport anchoring. See BPF_VIEWPORT_ANCHOR_*'s
      // own header comments for the constants and the overall "why."
      //
      // State machine:
      //   IDLE          — default resting state; the shell is either far
      //                    from the target, or not yet SEATED and not
      //                    within the activation window.
      //   SETTLING      — this._viewportAnchorSettling is true: one
      //                    smooth scroll is in flight (either the
      //                    original approach-phase nudge, or 5E.1B's
      //                    bounded in-groove recenter — same guard,
      //                    same method), guarded against a second
      //                    concurrent one.
      //   SEATED        — this._viewportAnchorSeated is true: the shell
      //                    has reached BPF_VIEWPORT_ANCHOR_TOLERANCE_PX
      //                    of the target at least once since the last
      //                    release (native CSS sticky, not this
      //                    controller, is what actually holds it there).
      //                    While seated, light drift up to
      //                    BPF_VIEWPORT_ANCHOR_RELEASE_DISTANCE_PX stays
      //                    inside the groove (Part 8/9 — at most one
      //                    bounded recenter, never released); beyond
      //                    that distance is a deliberate escape.
      //   RELEASED      — this._viewportAnchorState === 'released': the
      //                    customer deliberately left an anchored
      //                    position — either a still-unseated approach
      //                    settle interrupted by real touch/wheel
      //                    contact, a seated shell displaced beyond the
      //                    groove depth, or an in-flight bounded
      //                    recenter that lost the race to a still-
      //                    growing displacement (settleViewportAnchor()'s
      //                    own "release must win" check). Magnetism
      //                    stays suppressed until the shell moves back
      //                    OUT of the activation zone entirely (proof of
      //                    genuine departure) and then re-approaches —
      //                    a geometry-based rearm, never an arbitrary
      //                    timer.
      //
      // This deliberately never tries to HOLD the shell in place —
      // that's already native CSS `position: sticky` on
      // [data-mobile-shell]. The only thing this controller ever does is
      // nudge the page toward the target — once to close the initial
      // gap on approach, and (5E.1B) at most once more per light drift
      // once seated — via a single smooth window.scrollBy() each time;
      // sticky's own native engage/release behavior in both directions
      // needs no further JS at all beyond that, which is exactly what
      // makes "the customer can always leave in both directions, no
      // snap-back loop" true by construction rather than by careful
      // tuning. =====

      initMobileViewportAnchor() {
        if (this._viewportAnchorScrollListener) return; // already initialized (e.g. a reconnect)

        // A fresh connect always re-baselines — see evaluateViewportAnchor()'s
        // own "first scroll after connect" guard (Part 24). Also
        // explicitly (re-)establishes every re-entrancy guard field to
        // its rest value, rather than relying on the constructor alone
        // — a reconnect (e.g. a Theme Editor section re-render) reaches
        // this same method without a fresh construction in between.
        this._viewportAnchorHasSeenScroll = false;
        this._viewportAnchorState = 'idle';
        this._viewportAnchorSettling = false;
        this._viewportAnchorSettleTimer = null;
        this._viewportAnchorRafId = null;
        this._viewportAnchorSeated = false;

        this._viewportAnchorScrollListener = () => this.scheduleViewportAnchorEvaluation();
        window.addEventListener('scroll', this._viewportAnchorScrollListener, { passive: true });

        // Observational only (Part 10/30) — never preventDefault(), never
        // used to move the page directly; only to release an in-flight
        // settle the instant a genuine user gesture is observed, rather
        // than waiting for the resulting scroll event.
        this._viewportAnchorTouchStartListener = () => this.noteViewportAnchorUserIntent();
        this._viewportAnchorTouchMoveListener = () => this.noteViewportAnchorUserIntent();
        this._viewportAnchorWheelListener = () => this.noteViewportAnchorUserIntent();
        window.addEventListener('touchstart', this._viewportAnchorTouchStartListener, { passive: true });
        window.addEventListener('touchmove', this._viewportAnchorTouchMoveListener, { passive: true });
        window.addEventListener('wheel', this._viewportAnchorWheelListener, { passive: true });

        // Interaction blocking (Part 13) — one delegated listener per
        // event type on `this` covers every current and future control
        // inside the Builder without enumerating them individually;
        // native bubbling (touchstart/click/change all bubble) makes
        // this safe and cheap.
        this._viewportAnchorInteractionListener = () => { this._viewportAnchorLastInteractionAt = Date.now(); };
        this.addEventListener('touchstart', this._viewportAnchorInteractionListener, { passive: true });
        this.addEventListener('click', this._viewportAnchorInteractionListener);
        this.addEventListener('change', this._viewportAnchorInteractionListener);

        // Part 14 — an active input/textarea/select focus blocks
        // magnetism for its FULL duration, not just the rolling quiet
        // window above (a customer can pause mid-sentence far longer
        // than BPF_VIEWPORT_ANCHOR_INTERACTION_QUIET_MS while still very
        // much "typing").
        this._viewportAnchorFocusInListener = (event) => {
          const tag = event.target && event.target.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
            this._viewportAnchorFocusActive = true;
          }
        };
        this._viewportAnchorFocusOutListener = (event) => {
          const tag = event.target && event.target.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
            this._viewportAnchorFocusActive = false;
            // Part 14 — closing the keyboard must not immediately force
            // a re-anchor; only note this as an ordinary interaction so
            // the normal rolling quiet window still applies, same as any
            // other control.
            this._viewportAnchorLastInteractionAt = Date.now();
          }
        };
        this.addEventListener('focusin', this._viewportAnchorFocusInListener);
        this.addEventListener('focusout', this._viewportAnchorFocusOutListener);

        this._viewportAnchorResizeListener = this.debounce(() => this.scheduleViewportAnchorEvaluation(), BPF_VIEWPORT_ANCHOR_RESIZE_DEBOUNCE_MS);
        window.addEventListener('resize', this._viewportAnchorResizeListener);
        window.addEventListener('orientationchange', this._viewportAnchorResizeListener);

        // Part 4 — the real visible viewport (dynamic browser chrome),
        // not just window.innerHeight; safely absent on browsers without
        // support, in which case the window resize listener above is
        // the only recalculation trigger, exactly as the rest of this
        // file's mobile layout code already relies on for such browsers.
        if (window.visualViewport) {
          this._viewportAnchorVisualViewportResizeListener = this.debounce(() => this.scheduleViewportAnchorEvaluation(), BPF_VIEWPORT_ANCHOR_RESIZE_DEBOUNCE_MS);
          window.visualViewport.addEventListener('resize', this._viewportAnchorVisualViewportResizeListener);
        }
      }

      destroyMobileViewportAnchor() {
        if (this._viewportAnchorScrollListener) {
          window.removeEventListener('scroll', this._viewportAnchorScrollListener);
          this._viewportAnchorScrollListener = null;
        }
        if (this._viewportAnchorTouchStartListener) {
          window.removeEventListener('touchstart', this._viewportAnchorTouchStartListener);
          this._viewportAnchorTouchStartListener = null;
        }
        if (this._viewportAnchorTouchMoveListener) {
          window.removeEventListener('touchmove', this._viewportAnchorTouchMoveListener);
          this._viewportAnchorTouchMoveListener = null;
        }
        if (this._viewportAnchorWheelListener) {
          window.removeEventListener('wheel', this._viewportAnchorWheelListener);
          this._viewportAnchorWheelListener = null;
        }
        if (this._viewportAnchorInteractionListener) {
          this.removeEventListener('touchstart', this._viewportAnchorInteractionListener);
          this.removeEventListener('click', this._viewportAnchorInteractionListener);
          this.removeEventListener('change', this._viewportAnchorInteractionListener);
          this._viewportAnchorInteractionListener = null;
        }
        if (this._viewportAnchorFocusInListener) {
          this.removeEventListener('focusin', this._viewportAnchorFocusInListener);
          this._viewportAnchorFocusInListener = null;
        }
        if (this._viewportAnchorFocusOutListener) {
          this.removeEventListener('focusout', this._viewportAnchorFocusOutListener);
          this._viewportAnchorFocusOutListener = null;
        }
        if (this._viewportAnchorResizeListener) {
          window.removeEventListener('resize', this._viewportAnchorResizeListener);
          window.removeEventListener('orientationchange', this._viewportAnchorResizeListener);
          this._viewportAnchorResizeListener = null;
        }
        if (this._viewportAnchorVisualViewportResizeListener && window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this._viewportAnchorVisualViewportResizeListener);
          this._viewportAnchorVisualViewportResizeListener = null;
        }
        if (this._viewportAnchorSettleTimer) {
          clearTimeout(this._viewportAnchorSettleTimer);
          this._viewportAnchorSettleTimer = null;
        }
        if (this._viewportAnchorRafId !== null) {
          if (typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(this._viewportAnchorRafId);
          } else {
            clearTimeout(this._viewportAnchorRafId);
          }
          this._viewportAnchorRafId = null;
        }
        this._viewportAnchorSettling = false;
        this._viewportAnchorState = 'idle';
      }

      // Part 10/11/13/33 — the customer just produced a real touch/
      // wheel input. Interrupts a still-in-flight, NOT-YET-SEATED
      // approach settle immediately (Part 10's "user intent must
      // override magnetism"), without waiting for the resulting scroll
      // event (touchmove on a still-finger-down gesture can precede any
      // actual scroll delta by a noticeable amount on some devices) —
      // unchanged from 5E.1A for that large, first-time nudge.
      //
      // Phase 5E.1B — once genuinely SEATED, a mere touch/wheel signal
      // no longer forces an immediate release by itself (a finger
      // touching the screen, or a wheel tick, is not yet "meaningful
      // vertical escape" — Part 11/33): the actual resulting scroll
      // GEOMETRY is the sole authority for the groove from here
      // (evaluateViewportAnchor()'s own tolerance/groove/release
      // check), per Part 13's "geometry over velocity" — this is
      // exactly what lets the groove survive light touch/scroll input
      // without instantly popping, while a genuinely large displacement
      // still releases promptly once its resulting scroll event is
      // evaluated (bounded by the same single-rAF-frame throttle as
      // ever, so still feels immediate). Ordinary approach scrolling
      // (nothing settling yet) only records the interaction timestamp
      // here either way, used by isViewportAnchorInteractionBlocked().
      noteViewportAnchorUserIntent() {
        this._viewportAnchorLastInteractionAt = Date.now();
        if (this._viewportAnchorSettling && !this._viewportAnchorSeated) {
          this.releaseViewportAnchor();
        }
      }

      // Part 29 — rAF-throttled: multiple scroll events between frames
      // collapse into a single evaluation, never a per-event synchronous
      // layout read. Reads window.requestAnimationFrame (never the bare
      // global — matches this file's own window.matchMedia()-style
      // convention for every other browser API, and keeps this
      // controllable in the Node-based shadow-mode test harness) with a
      // ~1-frame setTimeout fallback for a browser/webview without RAF.
      scheduleViewportAnchorEvaluation() {
        if (this._viewportAnchorRafId !== null) return;
        const raf = typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame : (fn) => setTimeout(fn, 16);
        this._viewportAnchorRafId = raf(() => {
          this._viewportAnchorRafId = null;
          this.evaluateViewportAnchor();
        });
      }

      // Phase 7H.2 — restored to [data-mobile-shell] (known-good Phase
      // 7G.4 target). Phase 7H.1 Part E had retargeted this to
      // .bpf-memorial-designer__visual because that element briefly
      // carried position:sticky instead of the shell; now that the
      // shell itself carries position:sticky again (assets/bpf-
      // memorial-designer.css, restored), this reads its target from
      // the shell once more.
      getViewportAnchorShellEl() {
        return this.querySelector('[data-mobile-shell]');
      }

      // Part 6/62 — reads the shell's OWN resolved CSS `top` (the exact
      // value [data-mobile-shell]'s `position: sticky` already engages
      // at — see this constant block's own header comment) rather than
      // inventing a second, potentially-diverging target formula.
      // Always read live, never cached (Part 23 — accordion/card height
      // changes constantly, and this value can itself change with
      // --padding4's own responsive rules).
      getViewportAnchorTargetTop(shellEl) {
        const style = window.getComputedStyle(shellEl);
        const top = parseFloat(style.top);
        return Number.isFinite(top) ? top : 0;
      }

      isViewportAnchorInteractionBlocked() {
        if (this._viewportAnchorFocusActive) return true; // Part 14
        if (document.body.classList.contains('overflow-hidden')) return true; // Part 26/27 — cart drawer/modal
        if (this._touchTracking) return true; // Part 15/17 — an in-progress swipe/internal gesture owns this touch (Phase 7H.4: this._touchTracking is never set anymore now that the swipe deck is removed — kept as a harmless, always-false guard rather than an unrelated edit to this function)
        if (Date.now() - this._viewportAnchorLastInteractionAt < BPF_VIEWPORT_ANCHOR_INTERACTION_QUIET_MS) return true; // Part 13
        return false;
      }

      // The single evaluation pass — called (via the RAF throttle above)
      // on every window scroll and on every debounced resize/
      // visualViewport change. Reads current geometry fresh every time
      // (Part 23), never moves anything itself while settling/blocked/
      // desktop/not-yet-close, and only ever starts ONE settle per
      // genuine approach (Part 8/19).
      evaluateViewportAnchor() {
        if (this.isDesktopViewport()) {
          // Part 32 — crossing back to desktop mid-session clears any
          // transient state rather than leaving magnetism suppressed
          // forever if the customer later returns to mobile width.
          this._viewportAnchorState = 'idle';
          this._viewportAnchorSeated = false;
          return;
        }

        // Part 24 — the very first evaluation after connecting only
        // records a baseline; a page that LOADS already near/inside the
        // activation zone (restored scroll position, deep link) must
        // never trigger an immediate settle purely from that first
        // observation.
        if (!this._viewportAnchorHasSeenScroll) {
          this._viewportAnchorHasSeenScroll = true;
          return;
        }

        if (this._viewportAnchorSettling) return; // Part 19 — one in flight already

        const shellEl = this.getViewportAnchorShellEl();
        if (!shellEl) return;

        const targetTop = this.getViewportAnchorTargetTop(shellEl);
        const currentTop = shellEl.getBoundingClientRect().top;
        const distance = currentTop - targetTop;
        const absDistance = Math.abs(distance);

        // Part 11/42 — while released, only rearm once the shell has
        // genuinely left the activation zone (proof of real departure,
        // not just jitter at the boundary), so it can be re-entered
        // fresh.
        if (this._viewportAnchorState === 'released') {
          if (absDistance > BPF_VIEWPORT_ANCHOR_ACTIVATION_DISTANCE_PX) {
            this._viewportAnchorState = 'idle';
          } else {
            return;
          }
        }

        if (this.isViewportAnchorInteractionBlocked()) return;

        // Phase 5E.1B — once genuinely SEATED, displacement is measured
        // against the seated TARGET itself (absDistance, computed fresh
        // above), never against the immediately preceding reading — see
        // BPF_VIEWPORT_ANCHOR_RELEASE_DISTANCE_PX's own comment for why
        // the old trend-based check let light scrolling leak through.
        // This is the deepened groove:
        //   <= TOLERANCE_PX          -> still exactly seated, no-op
        //   <= RELEASE_DISTANCE_PX   -> light drift inside the groove —
        //                               at most one bounded recenter
        //   >  RELEASE_DISTANCE_PX   -> deliberate escape — release,
        //                               never a competing/corrective snap
        if (this._viewportAnchorSeated) {
          if (absDistance <= BPF_VIEWPORT_ANCHOR_TOLERANCE_PX) return;
          if (absDistance <= BPF_VIEWPORT_ANCHOR_RELEASE_DISTANCE_PX) {
            this.settleViewportAnchor(targetTop); // Part 9 — one bounded recenter, guarded by _viewportAnchorSettling as always
            return;
          }
          this._viewportAnchorSeated = false;
          this._viewportAnchorState = 'released';
          return;
        }

        // Not yet seated. Already within tolerance — native sticky is
        // already holding it exactly, no nudge was ever needed; this IS
        // the seated state now.
        if (absDistance <= BPF_VIEWPORT_ANCHOR_TOLERANCE_PX) {
          this._viewportAnchorSeated = true;
          return;
        }

        // Part 7 — only approaching from above (distance > 0, i.e. the
        // shell hasn't reached its sticky point yet) and within the
        // activation window engages the magnet; scrolled past it, or
        // still far above, both do nothing.
        if (distance <= 0 || distance > BPF_VIEWPORT_ANCHOR_ACTIVATION_DISTANCE_PX) return;

        this.settleViewportAnchor(targetTop);
      }

      // Part 8/28 — exactly one smooth (or, for reduced motion, no)
      // scroll toward the target; guarded against re-entrancy by
      // _viewportAnchorSettling, cleared once geometry confirms arrival
      // (tolerance check) or the safety timeout elapses, whichever comes
      // first. Serves BOTH the original approach-phase nudge (not yet
      // seated) and 5E.1B's bounded in-groove recenter (already
      // seated) — the same single guarded implementation for both, per
      // Part 9's "use the existing settling guard" instruction.
      settleViewportAnchor(targetTop) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
          // Part 28 — the least disruptive accessible choice: no
          // programmatic movement at all for a reduced-motion customer,
          // rather than an unannounced instant jump; the customer's own
          // continued scrolling reaches the same native sticky point on
          // its own regardless.
          return;
        }

        const shellEl = this.getViewportAnchorShellEl();
        if (!shellEl) return;

        this._viewportAnchorSettling = true;
        const scrollDelta = shellEl.getBoundingClientRect().top - targetTop;
        window.scrollBy({ top: scrollDelta, behavior: 'smooth' });

        const deadline = Date.now() + BPF_VIEWPORT_ANCHOR_SETTLE_TIMEOUT_MS;
        const checkSettled = () => {
          if (!this._viewportAnchorSettling) return; // already released/cleared elsewhere
          const stillShellEl = this.getViewportAnchorShellEl();
          const currentTargetTop = stillShellEl ? this.getViewportAnchorTargetTop(stillShellEl) : targetTop;
          const currentDistance = stillShellEl ? stillShellEl.getBoundingClientRect().top - currentTargetTop : 0;
          // Part 15 — release must win even over an in-flight bounded
          // recenter: if the customer has kept moving well beyond the
          // groove depth while this small correction was still
          // animating, abandon it as a release rather than fighting to
          // complete a now-stale correction. Only applies once seated —
          // an approach-phase settle (not yet seated) is unaffected,
          // exactly as in 5E.1A.
          if (this._viewportAnchorSeated && Math.abs(currentDistance) > BPF_VIEWPORT_ANCHOR_RELEASE_DISTANCE_PX) {
            this.releaseViewportAnchor();
            return;
          }
          if (Math.abs(currentDistance) <= BPF_VIEWPORT_ANCHOR_TOLERANCE_PX || Date.now() >= deadline) {
            this._viewportAnchorSettling = false;
            this._viewportAnchorSettleTimer = null;
            this._viewportAnchorSeated = true;
            return;
          }
          this._viewportAnchorSettleTimer = setTimeout(checkSettled, 50);
        };
        this._viewportAnchorSettleTimer = setTimeout(checkSettled, 50);
      }

      // Phase 7H.2 — scheduleViewportAnchorStepSettle() (Phase 7H.1 Part
      // E's explicit step-change re-anchor, needed only because mobile
      // cards had become normal document flow) has been removed. With
      // .bpf-accordion restored to position:absolute inside the fixed-
      // height shell (assets/bpf-memorial-designer.css), a freshly-
      // opened card is a freshly-rendered <details> whose own internal
      // scroll region starts at scrollTop 0 natively — there is no
      // stale scroll position to correct on a step change anymore, so
      // no replacement re-anchor call is needed here.

      // Part 10/11/15 — called the moment a genuine user gesture is
      // observed while a still-unseated approach settle is in flight,
      // or when evaluateViewportAnchor()/settleViewportAnchor() itself
      // detects a deliberate escape beyond the groove depth; disables
      // further magnetism until the geometry-based rearm condition in
      // evaluateViewportAnchor() is met. Release always wins — no
      // competing/corrective snap survives this call.
      releaseViewportAnchor() {
        if (this._viewportAnchorSettleTimer) {
          clearTimeout(this._viewportAnchorSettleTimer);
          this._viewportAnchorSettleTimer = null;
        }
        this._viewportAnchorSettling = false;
        this._viewportAnchorSeated = false;
        this._viewportAnchorState = 'released';
      }

      // Phase 5C.1D, Parts 12-14 — binds each mobile color <select> proxy
      // to its authoritative radio group. Native <select> interaction
      // (Part 27) is completely untouched here — no touchstart/touchmove
      // handling of any kind is added to these elements. (Phase 7H.4:
      // the deck no longer has any gesture listeners of its own at
      // all — see initMobileNav()'s own comment.)
      initMobileColorSelects() {
        this.mobileColorSelects.forEach(({ select, summary, stepSelector }) => {
          if (select) {
            select.addEventListener('change', () => {
              const input = Array.from(this.querySelectorAll(stepSelector + ' input')).find(
                (candidate) => candidate.value === select.value
              );
              if (!input) return;
              input.checked = true;
              // Part 17-20 — an explicit customer change is the ONLY
              // trigger that ever collapses this question into its
              // completed summary; a still-default value is never
              // hidden behind a checkmark.
              this._mobileColorManuallyEdited[stepSelector] = true;
              // The exact same event handleChange() already listens for
              // when a customer taps a color pill directly — this is the
              // one and only place authoritative state changes; the
              // select is a pure UI proxy over it, never a second state.
              input.dispatchEvent(new Event('change', { bubbles: true }));
              this.syncMobileColorSelects();
              // Phase 5C.1F, Parts 9/10 — once this compacts, check
              // whether every visible color question for the active
              // style is now compacted too, and if so bring Engraved
              // Pet Name into view (a no-op the moment any color
              // question is still expanded).
              this.maybeRevealPetNameAfterColors();
            });
          }
          if (summary) {
            summary.addEventListener('click', () => {
              this._mobileColorManuallyEdited[stepSelector] = false;
              this.syncMobileColorSelects();
              if (select) select.focus();
            });
          }
        });
      }

      // Phase 5C.1D, Parts 12-14 — the READ direction: mirrors each
      // mobile color <select> (and its completed-summary visibility) to
      // whatever the authoritative radio group's own checked value
      // currently is. Called from the SAME two updateCanvasFromState()
      // call sites already covering every other "authoritative state
      // just changed" moment in this file (constructor end, handleChange()),
      // so it automatically picks up the Phase 5B.1D Google Shopping
      // landed-color correction (protectCurrentVariantInProductMode()/
      // syncColorSelectionToActualVariant() both run before the
      // constructor's own call site) with zero new call sites of its
      // own. Self-guards to a no-op on desktop, matching updateMobileNav().
      syncMobileColorSelects() {
        if (this.isDesktopViewport()) return;

        this.mobileColorSelects.forEach(({ select, summary, stepSelector }) => {
          if (!select) return;
          const checked = this.querySelector(stepSelector + ' input:checked');
          if (!checked) return;
          select.value = checked.value;

          if (!summary) return;
          const labelEl = summary.querySelector('[data-mobile-color-summary-label]');
          if (labelEl) labelEl.textContent = `Color: ${checked.value}`;
          const manuallyEdited = Boolean(this._mobileColorManuallyEdited[stepSelector]);
          summary.hidden = !manuallyEdited;
          select.hidden = manuallyEdited;
        });
      }

      // Phase 5C.1F, Parts 6/7 — the ONE shared "bring the next relevant
      // question into view" primitive, used by Companion->Breed,
      // Personalize's color->Pet Name progression, and invalid-Continue
      // positioning. Deliberately scrolls ONLY `container` (always the
      // currently-open <details> itself — each mobile card already owns
      // its own overflow-y:auto scroll region, see the Phase 5C.1B/
      // 5C.1D CSS) via explicit scrollTop/scrollTo math from bounding
      // rects — never element.scrollIntoView(), whose choice of WHICH
      // scrollable ancestor to adjust this file cannot fully control
      // (.bpf-memorial-designer__accordion-list is itself
      // overflow:hidden, a valid scrollIntoView target in some
      // browsers, and adjusting ITS scrollTop would desync the
      // translateX-positioned card deck). This is the exact safer
      // fallback Part 7 itself calls out. Self-guards to a no-op on
      // desktop so every caller can invoke it unconditionally.
      //
      // Phase 7H.2: restored to card-scroll (known-good Phase 7G.4) —
      // Phase 7H.1 Part E's window.scrollBy retarget only existed to
      // support the now-reverted page-scroll architecture.
      revealInCardQuestion(container, targetEl) {
        if (this.isDesktopViewport()) return;
        if (!container || !targetEl || typeof targetEl.getBoundingClientRect !== 'function') return;
        if (typeof container.getBoundingClientRect !== 'function') return;

        const containerRect = container.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();
        const margin = 12;

        // Part 6/19 — already comfortably visible: do nothing. Never
        // aligns a question to the very top merely because it CAN.
        if (targetRect.top >= containerRect.top + margin && targetRect.bottom <= containerRect.bottom - margin) {
          return;
        }

        const currentScrollTop = typeof container.scrollTop === 'number' ? container.scrollTop : 0;
        const delta = targetRect.top - containerRect.top - margin;
        const targetScrollTop = Math.max(0, currentScrollTop + delta);

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!prefersReducedMotion && typeof container.scrollTo === 'function') {
          container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
        } else {
          container.scrollTop = targetScrollTop;
        }
      }

      // Phase 5C.1F, Parts 1-3 — mobile-only Companion progressive
      // disclosure + Breed auto-reveal. Called from handleChange()'s own
      // companion-type branch (a real customer pill tap) only — never
      // from initBreedEntryContext()/initProductEntryContext()'s own
      // bootstrap calls to handleCompanionTypeChange(), which only ever
      // run while the entire Companion card is hidden anyway (Breed/
      // Product mode, Parts 25/26), so this never has any visible effect
      // there even if it were called.
      updateMobileCompanionProgression() {
        if (this.isDesktopViewport()) return;

        if (this.mobileCompanionFieldset && this.mobileCompanionSummary) {
          const checked = this.companionTypeInputs.find((input) => input.checked);
          const showSummary = Boolean(checked) && !this._mobileCompanionForceExpanded;
          this.mobileCompanionFieldset.hidden = showSummary;
          this.mobileCompanionSummary.hidden = !showSummary;
          if (checked && this.mobileCompanionSummaryLabel) {
            this.mobileCompanionSummaryLabel.textContent = `Companion: ${checked.value}`;
          }
        }

        // Part 3 — Breed's own EXISTING authoritative <select> (never a
        // second one) is simply brought into view the moment
        // updateBreedVisibility() (already called by
        // handleCompanionTypeChange() before this method runs) has made
        // it visible — no compaction/summary for Breed itself (Part 3
        // says "may compact"; the existing native <select> is already a
        // single compact row, so the meaningful win here is purely
        // getting it into view, not shrinking it further).
        if (this.breedField && !this.breedField.hidden) {
          const companionDetails = this.accordions.find((details) => details.dataset.accordionSection === 'companion');
          if (companionDetails) this.revealInCardQuestion(companionDetails, this.breedField);
        }
      }

      // Phase 5C.1F, Parts 9-11 — extends the EXISTING Phase 5C.1D color
      // dropdown/summary mechanism (unmodified) with the SAME
      // revealInCardQuestion() primitive: once every currently-VISIBLE
      // color question for the active style has been explicitly
      // compacted, Engraved Pet Name (data-step="pet-name" — the next
      // real field in existing DOM order, unchanged business logic) is
      // brought into view. A plain Capsule with no further Personalize
      // fields below its one color question is a natural, harmless
      // no-op (petNameField stays hidden for Capsule — data-applies-to
      // "vault" only, unchanged).
      maybeRevealPetNameAfterColors() {
        if (this.isDesktopViewport()) return;
        if (!this.petNameField || this.petNameField.hidden) return;

        const relevantGroups = this.mobileColorSelects.filter(({ stepSelector }) => {
          const groupEl = this.querySelector(stepSelector);
          return groupEl && !groupEl.hidden;
        });
        if (!relevantGroups.length) return;

        const allCompacted = relevantGroups.every(({ stepSelector }) => this._mobileColorManuallyEdited[stepSelector]);
        if (!allCompacted) return;

        const personalizeDetails = this.accordions.find((details) => details.dataset.accordionSection === 'personalize');
        if (personalizeDetails) this.revealInCardQuestion(personalizeDetails, this.petNameField);
      }

      // ----- Phase 2.3: Journey Nav ("Create Your Memorial") -----
      // Pure read-out of the accordion's own state — no separate state is
      // tracked here. "Current" = the open <details>; "completed" = visited
      // and closed; everything else is "upcoming". Distinct icon glyphs (not
      // just color) distinguish each state — see assets/bpf-memorial-designer.css.

      updateJourneyNav() {
        this.accordions.forEach((details) => {
          const key = details.dataset.accordionSection;
          const navItem = this.journeyItems.find((item) => item.dataset.journeyItem === key);
          if (!navItem) return;

          navItem.classList.remove('is-completed', 'is-current', 'is-upcoming');

          if (details.open) {
            navItem.classList.add('is-current');
            navItem.setAttribute('aria-current', 'step');
          } else if (details.classList.contains('is-visited')) {
            navItem.classList.add('is-completed');
            navItem.removeAttribute('aria-current');
          } else {
            navItem.classList.add('is-upcoming');
            navItem.removeAttribute('aria-current');
          }
        });
      }

      // ----- Phase 2.3.3: sticky nav "Step X of N / title" + progress bar -----
      // Also a pure read-out — the title text is read directly from the open
      // section's own .bpf-accordion-summary__title so it can never drift out
      // of sync with the accordion itself. Phase 2.5: index/total now come
      // from getVisibleSteps() instead of the full static accordion list, so
      // an auto-skipped Memorial Style step is correctly excluded from the
      // count.

      updateCurrentStepDisplay() {
        const visibleSteps = this.getVisibleSteps();
        const openDetails = visibleSteps.find((details) => details.open);
        if (!openDetails) return;

        const index = visibleSteps.indexOf(openDetails);
        const total = visibleSteps.length;
        const titleEl = openDetails.querySelector('.bpf-accordion-summary__title');
        const title = titleEl ? titleEl.textContent.trim() : '';
        const stepLabel = `Step ${index + 1} of ${total}`;

        if (this.currentStepNumberEl) this.currentStepNumberEl.textContent = stepLabel;
        if (this.currentStepTitleEl) this.currentStepTitleEl.textContent = title;
        if (this.currentStepNumberCompactEl) this.currentStepNumberCompactEl.textContent = stepLabel;
        if (this.currentStepTitleCompactEl) this.currentStepTitleCompactEl.textContent = title;

        const percent = Math.round(((index + 1) / total) * 100);
        if (this.progressFillEl) this.progressFillEl.style.width = `${percent}%`;
        if (this.progressTrackEl) this.progressTrackEl.setAttribute('aria-valuenow', String(percent));

        this.updateStickyNavOffset();
      }

      updateStickyNavOffset() {
        if (!this.stickyNav) return;
        const height = this.stickyNav.getBoundingClientRect().height;
        if (!height) return;
        this.style.setProperty('--bpf-sticky-nav-offset', `${Math.ceil(height + 24)}px`);
      }

      // ----- Memorial Canvas mount strategy -----
      // One canvas placeholder element, never duplicated, never
      // recreated, so any canvas-internal state survives any future move
      // automatically. Historical note: Phase 2.5.1 through Phase 5C.1A
      // moved this node between three mounts (desktop's original
      // position, a Review-only mobile mount, and a hidden mobile holding
      // mount) so it was only ever visible on mobile once the customer
      // reached Review. Real mobile UX testing (Phase 5C.1) found that
      // behavior hid the memorial from the customer for most of the
      // journey — Phase 5C.1B retires the Review/holding-mount switching
      // for the canvas specifically (see below); the canvas is now
      // persistent in its
      // original .bpf-memorial-designer__visual parent on BOTH desktop
      // and mobile — mobile-only CSS repositions
      // .bpf-memorial-designer__mobile-shell (the new wrapper around
      // visual/sticky-nav/info) to place that same parent at the top of
      // the mobile builder, so no DOM move is needed there anymore. This
      // method's job is now just "make sure the canvas is in its one true
      // home," which is also exactly the desktop behavior already proven
      // — the old isDesktop/reviewOpen mount-switching (canvasMobileMount/
      // canvasHoldingMount, Review-specific relocation) is retired for
      // the canvas specifically. canvasHoldingMount itself is NOT
      // removed: updateProductDetailsPosition()/
      // updateAdditionalPhotosMobileTriggerPosition() below still depend
      // on it independently for the Product Details disclosure and the
      // Additional Photos/Picture Frame Details triggers — out of scope
      // for this phase, untouched.
      updateCanvasPosition() {
        if (!this.canvasPlaceholder || !this._canvasDesktopParent) return;
        if (this.canvasPlaceholder.parentElement === this._canvasDesktopParent) return;

        if (this._canvasDesktopNextSibling && this._canvasDesktopNextSibling.parentElement === this._canvasDesktopParent) {
          this._canvasDesktopParent.insertBefore(this.canvasPlaceholder, this._canvasDesktopNextSibling);
        } else {
          this._canvasDesktopParent.appendChild(this.canvasPlaceholder);
        }
      }

      // Regression fix (post-Migration, live QA — third pass) — the exact
      // same "one real node, moved between mounts by viewport/step"
      // mechanism and the exact same three states as updateCanvasPosition()
      // above, applied to the Product Details disclosure: desktop keeps
      // its original position below the canvas; mobile+Review moves it to
      // data-product-details-review-mount, directly after the canvas
      // inside the Review accordion (so the disclosure always reads
      // "Canvas, then Product Details, then Review summary"); mobile+
      // anything else moves it into the SAME shared data-canvas-holding-
      // mount the canvas itself uses — never visible anywhere else on
      // mobile, and never a second, dedicated holding mount. Called from
      // the same three places updateCanvasPosition() already is
      // (connectedCallback(), resize, and every accordion toggle) — cheap
      // to call redundantly, no-ops once already in place.
      updateProductDetailsPosition() {
        if (
          !this.productDetailsDisclosureEl ||
          !this._productDetailsDesktopParent ||
          !this.productDetailsReviewMount ||
          !this.canvasHoldingMount
        ) {
          return;
        }

        // Phase 7G.3: centralized through isDesktopViewport() (was a raw
        // duplicate matchMedia call) so this stays consistent with the
        // CSS breakpoints under Safari Page Zoom — see that method's
        // own comment.
        const isDesktop = this.isDesktopViewport();
        const reviewOpen = Boolean(this.reviewDetails && this.reviewDetails.open);

        let target;
        if (isDesktop) {
          target = this._productDetailsDesktopParent;
        } else if (reviewOpen) {
          target = this.productDetailsReviewMount;
        } else {
          target = this.canvasHoldingMount;
        }

        if (this.productDetailsDisclosureEl.parentElement === target) return;

        if (target === this._productDetailsDesktopParent) {
          if (
            this._productDetailsDesktopNextSibling &&
            this._productDetailsDesktopNextSibling.parentElement === this._productDetailsDesktopParent
          ) {
            this._productDetailsDesktopParent.insertBefore(
              this.productDetailsDisclosureEl,
              this._productDetailsDesktopNextSibling
            );
          } else {
            this._productDetailsDesktopParent.appendChild(this.productDetailsDisclosureEl);
          }
        } else {
          target.appendChild(this.productDetailsDisclosureEl);
        }
      }

      // Phase 4D.9B.1 — keeps the mobile "View Additional Photos" trigger
      // immediately after wherever the Product Details pill currently is,
      // WITHOUT modifying updateProductDetailsPosition() above at all: a
      // separate node, moved by this separate method, into the exact same
      // two mobile mounts that method already uses (productDetailsReviewMount/
      // canvasHoldingMount) — called right after updateProductDetailsPosition()
      // at each of its own call sites, so target.appendChild() here always
      // lands this trigger directly after the pill within that same
      // parent (appendChild always adds at the end).
      //
      // Desktop is deliberately a no-op: the desktop trigger lives inside
      // the disclosure's own content instead (see sections/bpf-memorial-
      // designer.liquid), and CSS alone (.bpf-view-additional-photos--
      // mobile, assets/bpf-additional-photos-gallery.css) hides this node
      // at >=750px regardless of which DOM parent it's sitting in — so
      // there's nothing useful to move it to on desktop.
      updateAdditionalPhotosMobileTriggerPosition() {
        if (!this.additionalPhotosMobileTrigger || !this.productDetailsReviewMount || !this.canvasHoldingMount) {
          return;
        }

        // Phase 7G.3: centralized through isDesktopViewport() — see that
        // method's own comment.
        const isDesktop = this.isDesktopViewport();
        if (isDesktop) return;

        const reviewOpen = Boolean(this.reviewDetails && this.reviewDetails.open);
        const target = reviewOpen ? this.productDetailsReviewMount : this.canvasHoldingMount;

        if (this.additionalPhotosMobileTrigger.parentElement !== target) {
          target.appendChild(this.additionalPhotosMobileTrigger);
        }

        // Phase 4D.10E.4 QA hotfix, Part 6/7 — the mobile Picture Frame
        // Details trigger rides along to the SAME target, appended right
        // after additionalPhotosMobileTrigger (appendChild always adds
        // at the end, so this always lands directly beneath it — same
        // "immediately after it" contract this method's own header
        // comment already documents for the Additional Photos trigger).
        // Moved unconditionally, regardless of its own hidden state —
        // it needs to already be in the correct on-screen location
        // BEFORE updatePictureFrameDetailsLinkVisibility() ever unhides
        // it, exactly like the always-relocated node above. No-op
        // (skipped entirely) when the feature flag is off.
        if (this.pictureFrameDetailsMobileTriggerWrapper && this.pictureFrameDetailsMobileTriggerWrapper.parentElement !== target) {
          target.appendChild(this.pictureFrameDetailsMobileTriggerWrapper);
        }
      }

      // Binds both the desktop and mobile "View Additional Photos"
      // triggers to populate the shared gallery at click time. Entirely
      // independent of <modal-opener>'s own click listener (assets/
      // global.js) on the same <button>, which handles opening the modal
      // itself — this listener only fetches/populates its contents, so
      // neither depends on the other's presence or ordering.
      bindAdditionalPhotosGallery() {
        this.additionalPhotosTriggers.forEach((trigger) => {
          // Idempotency guard — connectedCallback() (which calls this) can
          // run more than once for the same node (Theme Editor re-render),
          // same reasoning as bindPrePurchaseImageUpload()'s own guard;
          // never attach a second click listener to the same button.
          if (trigger.dataset.bpfAdditionalPhotosBound === 'true') return;
          trigger.dataset.bpfAdditionalPhotosBound = 'true';

          trigger.addEventListener('click', () => {
            const wrapper = trigger.closest('[data-modal]');
            const modal = wrapper ? document.querySelector(wrapper.getAttribute('data-modal')) : null;
            const gallery = modal ? modal.querySelector('bpf-additional-photos-gallery') : null;
            if (gallery) this.loadAdditionalPhotosForResolvedProduct(gallery);
          });
        });
      }

      // Fetches the CURRENTLY resolved product's own image media via
      // Shopify's standard /products/{handle}.js endpoint — the single
      // source of truth is Shopify's existing product Media (no
      // metafield, no Builder-owned image list, per Phase 4D.9B.1). That
      // endpoint's `images` array is documented to include only image-
      // type media, already in Admin's configured order with the
      // primary/featured image first — video/3D/model media is already
      // excluded with no filtering logic needed here.
      //
      // this.state.resolvedProduct is read fresh on every call, never
      // cached across calls, so a Companion/Breed/Memorial Style change
      // before the next click is picked up automatically with zero risk
      // of stale images — and nothing here ever runs until the customer
      // actually clicks a trigger, so Builder's initial load and every
      // product-resolution step stay exactly as fast as before this
      // feature existed.
      loadAdditionalPhotosForResolvedProduct(gallery) {
        const record = this.state.resolvedProduct;
        const handle = record ? record.productHandle : null;
        if (!handle) {
          gallery.showStatus('Additional photos will be available once your memorial is configured.');
          return;
        }

        gallery.showStatus('Loading photos…');

        fetch(`/products/${handle}.js`)
          .then((response) => {
            if (!response.ok) throw new Error('bpf-memorial-designer: product fetch failed with status ' + response.status);
            return response.json();
          })
          .then((product) => {
            const images = Array.isArray(product.images) ? product.images : [];
            gallery.setImages(images);
          })
          .catch(() => {
            gallery.showStatus('Photos couldn’t be loaded right now. Please try again.');
          });
      }

      // ----- Phase 3.0: Memorial Canvas visual resolution -----
      // A dataset and resolver deliberately SEPARATE from
      // eligibilityData/resolveProduct() above — approved explicitly so
      // the proven, purchase-critical product resolver is never touched
      // by this phase. Same documented 3-tier precedence shape (exact
      // breed -> companion-generic -> globally-generic), but its own
      // independent implementation, not a shared helper — any future
      // extraction happens only once both resolvers are independently
      // proven, per the Phase 3.0 approval. A visual record affects
      // ONLY what the canvas renders; it can never block Add to Cart,
      // and product-resolution failure (isAvailable: false) always
      // overrides it — see getCanvasState()/BpfMemorialCanvasRenderer.

      parseVisualMapping() {
        const script = this.querySelector('[data-visual-mapping]');
        if (!script) return [];
        try {
          const parsed = JSON.parse(script.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      // Phase 4D.10C, Part 22 — mirrors parseVisualMapping() above exactly
      // (same defensive try/catch-JSON.parse shape), for the
      // <script data-picture-frame-profiles> tag Liquid only emits when
      // enable_picture_frame_addon is on. {} (never null/undefined) on
      // any absence/parse failure, matching visualMapping's own []
      // fallback convention.
      parsePictureFrameProfiles() {
        const script = this.querySelector('[data-picture-frame-profiles]');
        if (!script) return {};
        try {
          const parsed = JSON.parse(script.textContent);
          return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (error) {
          return {};
        }
      }

      // Phase 4D.10E — mirrors parsePictureFrameProfiles() above exactly,
      // reading the separate [data-picture-frame-commerce] script tag
      // instead. Returns null (not {}) on any failure/absence — this
      // object's OWN shape (variantsByColor keyed by color, each entry
      // either a variant object or null) is what resolvePictureFrameVariant()
      // expects; a bare {} would be misread as "product resolved with
      // zero colors" rather than "commerce data unavailable."
      parsePictureFrameCommerce() {
        const script = this.querySelector('[data-picture-frame-commerce]');
        if (!script) return null;
        try {
          const parsed = JSON.parse(script.textContent);
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (error) {
          return null;
        }
      }

      // Phase 4D.10E, Parts 3/6/8/34/35; extended Phase 4D.10E.2, Part 3 —
      // the ONE place a Builder Frame Color value is turned into a real
      // Picture Frame Shopify variant. Every caller (Step 4 price,
      // Review pricing, Details popup, and now the real cart-line add)
      // goes through this — never a second, independent lookup.
      // Deliberately reads ONLY color — Orientation never participates
      // (Part 9/12: presentation-only, never a manufacturing/variant/
      // price/cart choice).
      //
      // Precedence (Part 3): live Shopify data first — this.pictureFrameCommerce
      // .variantsByColor[color], generically resolved in Liquid against
      // whatever the product's real variants currently are — and ONLY
      // when that finds nothing does this fall through to
      // BPF_PICTURE_FRAME_VERIFIED_FALLBACK, the manually-verified
      // Admin-sourced mapping (see that constant's own header for why
      // it exists and when it stops being needed). Live data always
      // wins when it resolves something for this color; the fallback
      // table is consulted only on a live miss, never merged/blended
      // with it. Returns null (never a guessed/substituted variant)
      // when NEITHER source has this color — a genuine data gap, never
      // silently papered over.
      resolvePictureFrameVariant(color) {
        if (!color) return null;

        if (this.pictureFrameCommerce && this.pictureFrameCommerce.variantsByColor) {
          const liveVariant = this.pictureFrameCommerce.variantsByColor[color];
          if (liveVariant && typeof liveVariant === 'object') return liveVariant;
        }

        const fallbackVariant = BPF_PICTURE_FRAME_VERIFIED_FALLBACK[color];
        return fallbackVariant && typeof fallbackVariant === 'object' ? fallbackVariant : null;
      }

      // Phase 4D.10E, Part 6 — keeps the Step 4 opt-in's "(+$price)"
      // suffix in sync with the CURRENTLY SELECTED Frame Color (defaults
      // to Black — this.state.pictureFrame.color's own initial value,
      // matching the Color <select>'s default). Only ever REPLACES the
      // suffix when a real resolved variant price is available; when
      // resolvePictureFrameVariant() returns null (product/color doesn't
      // resolve — see this phase's report), the initial Liquid-rendered
      // text (bpf_picture_frame_price, from selected_or_first_available_variant)
      // is left completely untouched — never blanked, never a fake value.
      updatePictureFrameStepPrice() {
        if (!this.pictureFramePriceSuffix) return;
        const color = (this.state.pictureFrame && this.state.pictureFrame.color) || 'Black';
        const variant = this.resolvePictureFrameVariant(color);
        if (variant && variant.priceFormatted) {
          this.pictureFramePriceSuffix.textContent = ' (+' + variant.priceFormatted + ')';
        }
      }

      // Phase 4D.10E, Part 5/17 — safe integer-cents money formatting
      // against the shop's OWN configured money_format (embedded once in
      // bpf_pf_commerce_json as moneyFormat, since Liquid is the only
      // place `shop.money_format` can be read) — never a second,
      // independently-invented currency symbol/decimal convention. The
      // placeholder set below ({{amount}}, {{amount_no_decimals}}, {{
      // amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}})
      // is Shopify's own long-published, standard money_format contract
      // (the same one every theme's own formatMoney implementation has
      // supported for years) — not a new convention invented here.
      // cents must be a finite integer; returns null (never a fake/
      // wrong amount) otherwise, so a caller can fall back to hiding the
      // row rather than showing garbage.
      formatMoney(cents, moneyFormat) {
        if (typeof cents !== 'number' || !isFinite(cents)) return null;
        const format = moneyFormat || (this.pictureFrameCommerce && this.pictureFrameCommerce.moneyFormat) || '${{amount}}';
        const dollars = (Math.round(cents) / 100).toFixed(2);
        const parts = dollars.split('.');
        const withComma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const noDecimals = Math.round(cents / 100).toString();
        const noDecimalsWithComma = noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return format
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimalsWithComma)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma + '.' + parts[1])
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
          .replace(/\{\{\s*amount\s*\}\}/g, dollars);
      }

      findVisualRecord(companionType, breed, style) {
        return (
          this.visualMapping.find(
            (record) =>
              record.companionType === companionType && record.breed === breed && record.memorialStyle === style
          ) || null
        );
      }

      // Unlike resolveProduct(), there is no enabled/allowFallback concept
      // here — a visual record either exists at a given tier or it
      // doesn't; the first tier with ANY matching record wins. Returns
      // null (never throws, never falls back to a guessed image) when no
      // tier has one — the renderer treats that exactly like "not yet
      // resolved": the static stage alone, nothing overlaid.
      resolveVisual(companionType, breed, style) {
        if (!companionType || !style) return null;
        const effectiveBreed = breed || BPF_GENERIC_KEY;
        const tiers = [
          { companionType, breed: effectiveBreed },
          { companionType, breed: BPF_GENERIC_KEY },
          { companionType: BPF_GENERIC_KEY, breed: BPF_GENERIC_KEY },
        ];

        for (let i = 0; i < tiers.length; i += 1) {
          const record = this.findVisualRecord(tiers[i].companionType, tiers[i].breed, style);
          if (record) return record;
        }
        return null;
      }

      // The ONE normalized object handed to BpfMemorialCanvasRenderer.
      // productId/productHandle/productTitle/variantId/isResolved/
      // isAvailable all come straight from this.state.resolvedProduct —
      // zero new product-resolution logic. Colors and engravedName are
      // read live from the DOM, exactly like updateReviewSummary() and
      // isPersonalizeValid() already do — this.state deliberately still
      // doesn't carry personalization (see the file header architecture
      // overview), and this method doesn't change that.
      getCanvasState() {
        const record = this.state.resolvedProduct;
        return {
          companionType: this.state.companion.type,
          breed: this.state.breed.value,
          memorialStyle: this.currentUrnStyle || null,
          isResolved: Boolean(record),
          isAvailable: Boolean(record && record.available === true),
          productId: record ? record.productId : null,
          productHandle: record ? record.productHandle : null,
          productTitle: record ? record.productTitle : null,
          variantId: record ? record.variantId : null,
          visualAssets: this.resolveVisual(this.state.companion.type, this.state.breed.value, this.currentUrnStyle),
          capsuleColor: this.getCheckedValue(this.querySelector('[data-step="capsule-color"] input:checked')) || null,
          vaultColor: this.getCheckedValue(this.querySelector('[data-step="vault-color"] input:checked')) || null,
          figureColor:
            this.getCheckedValue(this.querySelector('[data-step="dog-engraving-color"] input:checked')) || null,
          // Not yet consumed by the renderer (engraving is Phase 3.3) —
          // included now so the interface doesn't need to change shape
          // later, per "reserve the renderer layer and configuration
          // interface for it." Still sourced from the Step 3 engraving
          // field only, never Memorial Wall's Companion Name field.
          engravedName: this.petNameInput ? this.petNameInput.value.trim() : '',
          // Engraving Color: only a real override once the customer has
          // explicitly changed the <select> (engravingColorManuallyEdited)
          // — otherwise null, so assets/bpf-vault-engraving-layer.js's
          // render() falls through to visualAssets.engravingColorDefault
          // then figureColor/capsuleColor exactly as it already does,
          // completely unaffected by this field's own synced-but-not-
          // overridden display value.
          engravingColorOverride:
            this.engravingColorManuallyEdited && this.engravingColorSelect ? this.engravingColorSelect.value : null,
          uploadedPhoto: this.selectedPhotoFile,
          // Phase 4D.10B — Picture Frame add-on; Phase 4D.10C wires these
          // into real rendering (BpfMemorialCanvasRenderer.render() /
          // renderMemorialPreviewCanvas() both read them now). null/false
          // (never the frame asset's own literal values) whenever the
          // feature is off or not selected, matching every other optional
          // field's shape above. computeCanvasStateSignature() serializes
          // canvasState wholesale, so toggling/changing any of these
          // (including pictureFrameProfiles, which only changes if the
          // page itself reloads) automatically changes the signature —
          // no redundant signature field needed.
          pictureFrameSelected: this.pictureFrameEnabled ? this.state.pictureFrame.selected : false,
          pictureFrameOrientation:
            this.pictureFrameEnabled && this.state.pictureFrame.selected ? this.state.pictureFrame.orientation : null,
          pictureFrameColor:
            this.pictureFrameEnabled && this.state.pictureFrame.selected ? this.state.pictureFrame.color : null,
          // Phase 4D.10C, Part 22 — the resolved config (aperture +
          // asset URLs) itself; static per page load, but flows through
          // canvasState fresh each call for the same reason visualAssets
          // does — the renderer receives ONLY what getCanvasState()
          // builds, never a second constructor-time parameter.
          pictureFrameProfiles: this.pictureFrameEnabled ? this.pictureFrameProfiles : null,
        };
      }

      // ----- Phase 4D.8C: pre-purchase image persistence -----
      //
      // Uploads a File/Blob to the new, minimal /apps/memorial/image App
      // Proxy route (production-app.ts's handleBuilderImageUpload) and
      // resolves the returned Shopify File GID. Deliberately separate
      // from /apps/memorial itself: this route never creates a Memorial,
      // it only returns an opaque GID for a line-item property. Path A's
      // proven CTA/reveal/submit architecture is never touched by this —
      // it is a different endpoint on the same already-proven App Proxy.
      uploadBuilderImage(fileOrBlob, filename, options) {
        const formData = new FormData();
        formData.append('image', fileOrBlob, filename);

        // Bounded via AbortController — fetch() has no default timeout,
        // and this call sits directly in the intercepted Add to Cart
        // path (see performPrePurchaseImageUpload()), so a stalled
        // network request must not be able to strand that submission
        // indefinitely.
        var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        var timeoutId = controller
          ? setTimeout(() => controller.abort(), BPF_BUILDER_IMAGE_UPLOAD_TIMEOUT_MS)
          : null;

        // Phase 5D.1B — an optional external AbortSignal (options.signal)
        // lets a caller cancel a superseded upload (a newer canvas
        // signature, a replaced photo, or a disconnected component) on
        // top of the internal 20-second timeout above — both conditions
        // remain independently able to abort the SAME underlying fetch.
        // Deliberately a small manual bridge (listen for the external
        // signal's own 'abort' event and forward it to this call's own
        // internal controller) rather than AbortSignal.any(), whose iOS
        // Safari support was inconsistent at the time of writing — no
        // new dependency, no behavior change for any existing caller
        // that never passes options.signal at all.
        var externalSignal = options && options.signal;
        var onExternalAbort = null;
        if (controller && externalSignal) {
          if (externalSignal.aborted) {
            controller.abort();
          } else {
            onExternalAbort = function () { controller.abort(); };
            externalSignal.addEventListener('abort', onExternalAbort);
          }
        }

        return fetch('/apps/memorial/image', { method: 'POST', body: formData, signal: controller ? controller.signal : undefined })
          .then((response) =>
            response
              .json()
              .catch(() => ({}))
              .then((body) => ({ status: response.status, body: body }))
          )
          .then((result) => {
            if (result.status !== 201 || !result.body || typeof result.body.gid !== 'string') {
              throw new Error('bpf-memorial-designer: image upload did not return a usable gid');
            }
            return result.body.gid;
          })
          .finally(() => {
            if (timeoutId !== null) clearTimeout(timeoutId);
            if (externalSignal && onExternalAbort) externalSignal.removeEventListener('abort', onExternalAbort);
          });
      }

      // Writes the resolved GID into the matching hidden line-item
      // property input (see sections/bpf-memorial-designer.liquid) so the
      // ordinary Add to Cart FormData submission carries it — the same
      // mechanism every other Builder property (Companion Name, Pet
      // Story, ...) already uses, nothing new invented at the cart layer.
      setBuilderImageGidField(kind, value) {
        const selector = kind === 'photo' ? '[data-bpf-memorial-photo-gid-field]' : '[data-bpf-memorial-canvas-gid-field]';
        const field = this.querySelector(selector);
        if (field) field.value = value;
      }

      // Intercepts the shared <product-form>'s submit in the CAPTURE
      // phase — capture-phase listeners on an ancestor (here, this
      // component itself, which contains <product-form>) always run
      // before any bubble-phase listener on the form element itself,
      // regardless of script load order, so this reliably runs before
      // assets/product-form.js's own onSubmitHandler every time,
      // without depending on when either script happens to attach its
      // listener. Only ever acts on THIS component's own form — every
      // other form on the page (the standalone Memorial Wall form,
      // Globo, or a different product-form instance) is left completely
      // untouched, preserving Path A/Path B independence.
      bindPrePurchaseImageUpload() {
        if (!this.productForm) return;
        const form = this.productForm.querySelector('form');
        if (!form) return;
        // Idempotency guard: connectedCallback() (which calls this) can in
        // principle run more than once for the same node (e.g. the
        // element is moved in the DOM) — never attach a second listener
        // to the same form.
        if (form.dataset.bpfMemorialUploadBound === 'true') return;
        form.dataset.bpfMemorialUploadBound = 'true';

        this.addEventListener(
          'submit',
          (event) => {
            if (event.target !== form) return;
            // Set only for the duration of the synchronous requestSubmit()/
            // submit() call below and cleared immediately after it returns
            // (see the reset there) — NOT a permanent one-shot latch. The
            // Builder supports adding more than one configured Memorial to
            // the cart in a single page session (reconfigure, Add to Cart
            // again); a permanent flag would silently skip pre-purchase
            // image upload for every submission after the first.
            if (form.dataset.bpfMemorialUploadsComplete === 'true') return; // this exact re-dispatch — let it proceed untouched
            bpfTimingMark('T2 submit interceptor entered');

            // Phase 4D.9B.5, Part 4: Memorial Wall = No fast path.
            // Previously EVERY submission — Wall Yes or No — was
            // unconditionally intercepted (preventDefault +
            // stopImmediatePropagation), routed through
            // performPrePurchaseImageUpload()'s own async decision, then
            // resumed via form.requestSubmit() from inside a .finally()
            // callback. That is correct in principle (performPrePurchaseImageUpload()
            // already no-ops immediately for Wall = No), but it makes the
            // overwhelmingly common Wall = No path depend on promise-
            // microtask timing and a synthetic re-dispatch for zero
            // benefit — there is never any image work to wait for. This
            // now decides synchronously, BEFORE ever calling
            // preventDefault(), whether interception is needed at all: if
            // there is no Memorial image persistence to perform, the
            // ORIGINAL submit is left completely untouched and proceeds
            // on its one and only dispatch straight to product-form.js's
            // own onSubmitHandler — no async gap, no resubmission
            // mechanism, no dependency on requestSubmit() re-entrancy for
            // this path at all.
            // Phase 4D.10E.2, Part 10-14: the fast-path gate now also
            // covers the Picture Frame real cart-line add — requiresPictureFrameCartLine()
            // is Boolean(pictureFrameOptInInput && pictureFrame.selected),
            // completely independent of Wall Yes/No. Extending THIS same
            // gate (rather than adding a second, parallel interception
            // listener) was the explicit architecture decision for this
            // phase — see requiresPictureFrameCartLine()'s own comment
            // and the phase report's "why" — because it's the smallest
            // change to the one already-hardened interception point,
            // reusing every guard/resume mechanism below unmodified. Wall
            // No + Frame off (still the overwhelmingly common case)
            // remains the untouched, zero-interception fast path.
            if (!this.requiresMemorialImagePersistence() && !this.requiresPictureFrameCartLine()) {
              bpfTimingMark('T3 Wall branch resolved: No (fast path) / T5N native resume — no interception at all');
              // Purchase-flow simplification, Full entry mode ONLY: this
              // fast path now covers every Full-mode Add to Cart (both
              // requires* gates above are always false for this mode).
              // product-form.js's own onSubmitHandler is about to call
              // cart.renderContents(), which unconditionally auto-opens
              // the drawer (see cart-drawer.js) — suppress that one open
              // so the post-Add-to-Cart add-on popup can show first; see
              // prepareFullModeAddOnPopup() for how/when it's restored.
              // Wrapped defensively — a failure here must never block
              // the native submit this function is deliberately letting
              // through untouched.
              if (this.entryMode === 'full') {
                try {
                  this.prepareFullModeAddOnPopup(form);
                } catch (error) {
                  // See above — never block the native submit.
                }
              }
              return;
            }
            bpfTimingMark('T3 Wall=' + this.requiresMemorialImagePersistence() + ' Frame=' + this.requiresPictureFrameCartLine());

            // Re-entrancy guard: product-form.js's own double-submit guard
            // (this.submitButton's aria-disabled check in its click
            // handler) never gets a chance to engage while this pass is
            // in flight, because onSubmitHandler() itself hasn't run yet
            // — that's the whole point of intercepting before it. A rapid
            // second click therefore reaches this same listener again
            // before the first pass's async pre-work finishes; without
            // this flag it would start a second, concurrent upload and
            // could eventually fire two real Add to Cart submissions.
            // Deliberately NOT reusing aria-disabled for this — that
            // attribute is already actively managed by this file's own
            // click-time form-validity gate (handleSubmitClick()/
            // updateAddToCartState()), and repurposing it here risks
            // fighting that existing, unrelated logic. Phase 4D.10E.2:
            // this SAME flag now also guards the frame cart-line add —
            // one flag, one guarded async pass, covering both concerns
            // (Part 17: rapid double-click/deferred-click/Statue-resume/
            // Favorite-Photo-promise/Memorial-Wall-persistence-promise
            // all still funnel through exactly one real dispatch of this
            // listener per logical Add to Cart intent, unchanged).
            event.preventDefault();
            event.stopImmediatePropagation();
            if (form.dataset.bpfMemorialUploadInFlight === 'true') return;
            form.dataset.bpfMemorialUploadInFlight = 'true';
            // Phase 4D.9B.7, Part 3: immediate visual feedback for the one
            // real attempt now actually starting — placed after the
            // re-entrancy guard so a swallowed duplicate dispatch never
            // redundantly re-applies it. Always undone in the SAME
            // .finally() below (success, failure, or swallowed error
            // alike), so this can never leave the button stuck. Reused
            // as-is for the frame-only (Wall No + Frame on) case too —
            // its generic "Preparing your memorial…" text still fits;
            // Part 10-14 found no reason to invent a second, frame-
            // specific loading state.
            this.showMemorialPreparingState();

            // Phase 5C.1K, Part 6/7 (supersedes the retired Phase
            // 4D.10E.2 Part 14 Promise.all architecture, and the
            // reverted 5C.1H/5C.1I attempt — see the 5C.1J audit report
            // for the full "why"): the frame item is built by a pure,
            // synchronous resolver (buildPictureFrameCartItem() — no
            // network call of its own), so there is no latency benefit
            // to racing it against Memorial image persistence. Ordering
            // matters instead: image/canvas persistence
            // (performPrePurchaseImageUpload()) must fully complete and
            // update the form's own hidden properties BEFORE the
            // Memorial item is serialized from that form — building the
            // payload first, then waiting, could silently ship stale
            // properties.
            const needsCombinedFrameCart = this.requiresPictureFrameCartLine();
            // Phase 5D.1D — Add to Cart is the other of the two moments
            // that ever trigger a remote /apps/memorial/image upload
            // (the other is Review-open) — see
            // maybePersistMemorialCanvas()'s own comment. A cheap no-op
            // if Review already persisted this exact signature (same-
            // signature dedup); otherwise starts the upload NOW, so
            // performPrePurchaseImageUpload()'s own existing persisted-
            // GID-reuse path (below) can await/reuse it rather than
            // starting a completely separate, redundant upload of its
            // own.
            this.maybePersistMemorialCanvas();
            this.performPrePurchaseImageUpload(form).finally(() => {
              if (needsCombinedFrameCart) {
                // Phase 5C.1I, Part 3 (recovered unmodified — the
                // 5C.1J audit found no defect in this guard logic
                // itself, only in what the combined path DID with the
                // cart response, which 5C.1K replaces below):
                // bpfMemorialUploadInFlight is DELIBERATELY left 'true'
                // here (never reset early the way the no-frame branch
                // below does), and bpfMemorialUploadsComplete is NEVER
                // touched on this branch (it stays 'false', so a
                // genuine second real submit event is NOT waved through
                // — it reaches the bpfMemorialUploadInFlight check
                // above instead, since no synthetic form.requestSubmit()
                // re-dispatch ever happens on this branch at all).
                // bpfMemorialUploadInFlight only releases inside
                // submitCombinedMemorialAndFrameCart()'s OWN .finally(),
                // once the ENTIRE combined transaction — mutation,
                // section refresh, and render-or-redirect-fallback — has
                // actually settled (Phase 5C.1K, Part 21: the guard's
                // scope now explicitly includes the section-refresh
                // request too, not just the merchandise mutation).
                // "Preparing your memorial…" stays visible for that same
                // full duration, since this genuinely is one continuous
                // purchase attempt from the customer's perspective.
                this.submitCombinedMemorialAndFrameCart(form).finally(() => {
                  this.hideMemorialPreparingState();
                  form.dataset.bpfMemorialUploadInFlight = 'false';
                });
                return;
              }

              // Phase 4D.9B.7, Part 4 / 5C.1I, Part 4 / 5C.1K, Part 25:
              // unchanged — no Picture Frame selected, so this is still
              // exactly the prior single-item native resume path (Part
              // 12/21: minimize blast radius for the overwhelmingly
              // common no-frame case — this is the exact behavior real
              // mobile QA confirmed healthy after the 0121a86 revert,
              // and 5C.1K must not risk it). bpfMemorialUploadInFlight
              // releases here (this transaction ends at the native
              // resume — Shopify's own request/response for it is
              // product-form.js's concern, outside this guard's scope)
              // and bpfMemorialUploadsComplete is set 'true' only for
              // the single synchronous form.requestSubmit() call
              // immediately below, so THIS SAME listener lets that one
              // specific re-dispatched submit event through untouched
              // (see the check at the top of this listener) without
              // waving through any other, genuinely new submit.
              form.dataset.bpfMemorialUploadInFlight = 'false';
              this.hideMemorialPreparingState();
              bpfTimingMark('T8Y native submission resume invoked');
              form.dataset.bpfMemorialUploadsComplete = 'true';
              if (typeof form.requestSubmit === 'function') {
                form.requestSubmit();
              } else {
                form.submit();
              }
              form.dataset.bpfMemorialUploadsComplete = 'false';
            });
          },
          true
        );
      }

      // Phase 4D.9B.5, Part 4: the one shared definition of "is there any
      // Memorial image persistence to do at all" — used both by
      // bindPrePurchaseImageUpload()'s synchronous fast-path decision
      // (whether to intercept the submit at all) and by
      // performPrePurchaseImageUpload() below, so the two can never
      // disagree with each other.
      requiresMemorialImagePersistence() {
        // Purchase-flow simplification, Full entry mode ONLY: Memorial
        // Wall's own accordion is forced hidden pre-cart for Full
        // (applyProductDefinitionStepVisibility()), but its "Yes" radio
        // still defaults checked underneath it — without this
        // exemption every Full-mode Add to Cart would still pay for an
        // unneeded pre-purchase image-persistence round trip the
        // customer was never actually offered a choice about pre-cart.
        // Memorial Wall is offered instead in the post-Add-to-Cart
        // add-on popup, which performs its own persistence then, only
        // if actually chosen. Breed/Collection and Product are
        // byte-identical to before this change.
        if (this.entryMode === 'full') return false;
        return this.memorialWallInputs.some((input) => input.checked && input.value === 'Yes');
      }

      // Phase 4D.10E.2, Part 10/11 — the one shared definition of "does
      // this Add to Cart need a real Picture Frame cart-line add too,"
      // mirroring requiresMemorialImagePersistence()'s own shape exactly
      // so the two can be OR'd together in bindPrePurchaseImageUpload()'s
      // fast-path gate without disagreeing. Reads pictureFrameOptInInput
      // first (null/absent when the feature flag is off, matching every
      // other Picture Frame gate in this file) so this is a guaranteed
      // false whenever the add-on doesn't exist at all — no separate
      // flag check needed.
      //
      // Purchase-flow simplification, Full entry mode ONLY: also reused
      // by addPictureFrameFromPopup() (the POST-Add-to-Cart popup's own
      // "Add Frame" action, via buildPictureFrameCartItem()) — for Full
      // mode, this.pictureFrameOptInInput is always null (no checkbox
      // exists in the DOM pre-cart for this mode at all), which would
      // make this permanently false and silently break the popup's
      // frame add every time, confirmed by real end-to-end testing
      // (resolvePictureFrameVariant() resolved a real variant, but
      // buildPictureFrameCartItem() still returned null because of this
      // gate). The `|| this.entryMode === 'full'` arm only ever matters
      // post-cart: pre-cart, state.pictureFrame.selected is always
      // false for Full mode (nothing can ever set it true — see
      // applyProductDefinitionStepVisibility()), so
      // bindPrePurchaseImageUpload()'s own fast-path check above is
      // completely unaffected by this change. Breed/Collection and
      // Product keep pictureFrameOptInInput as their only gate,
      // unchanged.
      requiresPictureFrameCartLine() {
        return Boolean(
          (this.pictureFrameOptInInput || this.entryMode === 'full') &&
            this.state.pictureFrame &&
            this.state.pictureFrame.selected === true
        );
      }

      // Phase 5C.1K, Part 27/3B — refactored from the retired
      // addPictureFrameToCartIfSelected(). Real mobile QA found the
      // legacy two-request architecture (separate frame add, then
      // native Memorial resume regardless of the frame's outcome) could
      // leave the frame missing from the actual Shopify cart while
      // Review still showed its price. 5C.1H/5C.1I's combined-request
      // fix then caused a DIFFERENT regression (blank cart drawer — see
      // the 5C.1J audit report) by passing the raw multi-item add
      // response directly to cart.renderContents(). 5C.1K fixes that
      // without reverting the combined-mutation architecture itself.
      // Single responsibility here, unchanged from 5C.1H: resolve/build
      // the frame's own cart-add item — never a cart mutation of its
      // own. Called from submitCombinedMemorialAndFrameCart() below,
      // immediately before the combined request is sent, so the color
      // read here is always whatever the customer most recently
      // selected — never a value captured earlier in the click
      // lifecycle. Orientation is still never read here at all
      // (presentation/private-property only, never a variant/price
      // input) — resolvePictureFrameVariant() itself is completely
      // unmodified. Returns null (never a guessed/substituted variant)
      // when the frame isn't selected or can't resolve — the caller
      // treats null as "do not submit anything," never as "submit the
      // Memorial alone."
      buildPictureFrameCartItem() {
        if (!this.requiresPictureFrameCartLine()) return null;
        const variant = this.resolvePictureFrameVariant(this.state.pictureFrame.color);
        if (!variant || !variant.id) return null;
        return { id: variant.id, quantity: 1 };
      }

      // Phase 5C.1K, Part 3A/29/30 — the ONE place the real,
      // authoritative memorial <form> state becomes the memorial's own
      // cart-add item, for the combined multi-item request below. Reads
      // the REAL form via FormData — never a second, hand-maintained
      // copy of Builder state, so this can never drift from what the
      // form would otherwise have submitted natively. Matches native
      // <form> submission semantics exactly for free: FormData only
      // ever contains ENABLED, named fields (a disabled input, e.g. a
      // color pill belonging to the currently-inactive style, is simply
      // absent), an unchecked checkbox/radio contributes nothing, and a
      // duplicate-named field collapses to FormData's own "last value
      // wins" iteration order — none of that needs reimplementing here.
      // Every properties[...] field (public AND the private _-prefixed
      // Picture Frame Color/Orientation fields alike) is carried through
      // identically; only the two non-property fields this endpoint
      // needs explicitly (id, quantity) are read by name.
      buildMemorialCartItemFromForm(form) {
        const formData = new FormData(form);
        const rawId = formData.get('id');
        const rawQuantity = formData.get('quantity');
        const item = {
          id: rawId ? Number(rawId) : null,
          quantity: Number(rawQuantity) || 1,
          properties: {},
        };

        formData.forEach((value, key) => {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (!match) return;
          item.properties[match[1]] = value;
        });

        return item;
      }

      // Phase 5C.1K, Parts 10-12/37 — a small, single-responsibility
      // helper: obtain fresh, rendered HTML for exactly the sections the
      // active cart component (cart.getSectionsToRender() — never a
      // hardcoded list, Part 5) actually needs, via Shopify's read-only
      // Section Rendering API. This is NEVER a cart mutation — a plain
      // GET against the CURRENT page's own URL (window.location.href,
      // Part 10/11) with only the `sections` query param set/replaced,
      // so any existing query context (variant=, view=, locale routing)
      // is preserved untouched rather than reconstructed by hand — no
      // hardcoded domain, path, or `/sections` route. Returns the flat
      // {sectionId: html, ...} map the Section Rendering API itself
      // returns, plus the list of IDs that were requested (so the
      // caller can validate against exactly that list, not a duplicated
      // constant). Does not mutate the cart, open the drawer, serialize
      // the Memorial, resolve the frame, or manage the purchase guard —
      // purely a fetch-and-parse.
      async fetchFreshCartSections(cart) {
        const sectionIds = cart.getSectionsToRender().map((section) => section.id);
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sectionIds.join(','));
        const response = await fetch(url.toString(), { method: 'GET' });
        const sections = await response.json();
        if (!response.ok) {
          throw new Error('Section refresh request failed');
        }
        return { sections, sectionIds };
      }

      // Phase 5C.1K, Part 13/38 — a small, pure validator. cart-drawer.js's
      // own renderContents() (Phase 5C.1J's audit) has zero defensive
      // coding of its own — it reads parsedState.sections[section.id]
      // and immediately feeds that into a DOMParser/.querySelector/.innerHTML
      // chain with no null-guards anywhere, so an uncaught exception mid-
      // render is exactly how the 0121a86 regression manifested. Nothing
      // is ever handed to renderContents() without first confirming
      // every section it will read is a real, non-empty, usable string.
      hasRequiredCartSections(sections, requiredIds) {
        if (!sections || typeof sections !== 'object') return false;
        return requiredIds.every((id) => typeof sections[id] === 'string' && sections[id].length > 0);
      }

      // Phase 5C.1K, Parts 18-20/24 — the merchandise mutation has
      // ALREADY succeeded by the time this is ever called; only the
      // READ-ONLY section refresh (or its validation) failed afterward.
      // Never shows a "nothing was added" message (that would be
      // false), never retries the mutation (would risk a duplicate
      // add), never resumes the native Memorial submit (would ALSO
      // duplicate the Memorial line). Matches product-form.js's OWN
      // existing "no cart element" precedent exactly
      // (onSubmitHandler(): `window.location = window.routes.cart_url;
      // return;`) — a direct, silent redirect to the real, authoritative
      // cart page is already this theme's established behavior for
      // "just show me the cart," not a new pattern invented here.
      redirectToCartAfterSuccessfulAdd() {
        // eslint-disable-next-line no-console
        console.error(
          '[bpf-memorial-designer] Combined cart mutation succeeded but the cart preview could not refresh — redirecting to the cart page.'
        );
        if (window.routes && window.routes.cart_url) {
          window.location = window.routes.cart_url;
        }
      }

      // Phase 5C.1K, Parts 4/6-9/16-21 — the combined-cart orchestrator.
      // Called only from bindPrePurchaseImageUpload()'s own submit
      // interception, only when requiresPictureFrameCartLine() is true,
      // and only AFTER performPrePurchaseImageUpload() has already fully
      // resolved (persistence must finish and update the form's hidden
      // properties before buildMemorialCartItemFromForm() reads them).
      //
      // Three strict stages, matching the 5C.1J audit's recommended
      // architecture exactly:
      //
      // STAGE 1 — ONE merchandise MUTATION: POST routes.cart_add_url
      // with {items: [memorialItem, frameItem]} — deliberately WITHOUT
      // `sections`/`sections_url` on this call at all (Part 7/9). This
      // response is used ONLY to detect success/failure — it is NEVER
      // passed to cart.renderContents(). This is the exact regression
      // boundary the 0121a86 revert exists because of; it must never
      // return.
      //
      // STAGE 2 — if the mutation succeeded, ONE READ-ONLY Section
      // Rendering GET (fetchFreshCartSections()) to obtain a response
      // shape proven safe for cart-drawer.js's renderContents() contract
      // (Phase 5C.1J's audit). Validated (hasRequiredCartSections())
      // before ever being handed to renderContents().
      //
      // STAGE 3 — either render (drawer refreshes/opens through Minion's
      // own existing, unmodified renderContents()/open() chain) or, if
      // the refresh/validation failed, redirect to the real cart page
      // (redirectToCartAfterSuccessfulAdd()) — never a stale, unrendered
      // drawer, and never a second purchase attempt of any kind.
      //
      // HARD requirement carried over from 5C.1H/I, unchanged: if the
      // frame item can't resolve at all, NOTHING is submitted — no
      // request is even sent, and this never falls back to a
      // Memorial-only submission.
      async submitCombinedMemorialAndFrameCart(form) {
        const memorialItem = this.buildMemorialCartItemFromForm(form);
        const frameItem = this.buildPictureFrameCartItem();

        if (!memorialItem.id || !frameItem) {
          this.reportCombinedCartFailure(
            'We couldn’t add your Memorial and Picture Frame to your cart. Nothing was added — please try again.'
          );
          return;
        }

        bpfTimingMark('T combined memorial+frame cart mutation begins (frame variant=' + frameItem.id + ')');

        let mutationResponse;
        try {
          const response = await fetch(window.routes.cart_add_url, {
            ...fetchConfig(),
            body: JSON.stringify({ items: [memorialItem, frameItem] }),
          });
          mutationResponse = await response.json();
          if (!response.ok || (mutationResponse && mutationResponse.status)) {
            throw new Error(
              (mutationResponse && (mutationResponse.description || mutationResponse.message)) || 'Add to Cart request failed'
            );
          }
        } catch (error) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'bpf-memorial-designer',
              productVariantId: memorialItem.id,
              errors: error.message,
            });
          }
          this.reportCombinedCartFailure(
            'We couldn’t add your Memorial and Picture Frame to your cart. Nothing was added — please try again.'
          );
          return;
        }

        bpfTimingMark('T combined memorial+frame cart mutation complete');

        // The merchandise IS now in the cart. From this point forward,
        // any failure below must never claim "nothing was added," never
        // retry the mutation, and never resume the native Memorial
        // submit — see redirectToCartAfterSuccessfulAdd()'s own comment.
        const cart = this.productForm && this.productForm.cart;
        if (!cart || typeof cart.getSectionsToRender !== 'function' || typeof cart.renderContents !== 'function') {
          this.redirectToCartAfterSuccessfulAdd();
          return;
        }

        try {
          // Phase 5C.1V — proven from real, unmodified assets/cart-
          // drawer.js + snippets/cart-drawer.liquid + assets/component-
          // cart-drawer.css source (executed directly in a faithful
          // test-harness fixture, not inferred): CartDrawer.renderContents()
          // only ever clears a stale 'is-empty' class from
          // '.drawer__inner' — but this theme's own markup never puts
          // that class there; Liquid puts it on the OUTER <cart-drawer>
          // element itself (and on <cart-drawer-items>) when the cart is
          // empty at the moment the PAGE was first rendered.
          // renderContents() only ever replaces DESCENDANTS of that outer
          // element (the '#CartDrawer' inner div's children), so a stale
          // 'is-empty' on the outer element survives every section
          // refresh untouched — and component-cart-drawer.css's own
          // `cart-drawer.is-empty .drawer__footer { display: none; }`
          // then hides the ENTIRE cart-items-and-checkout block, leaving
          // only the drawer shell/overlay/close-button visible. This
          // exactly matches real mobile QA's screenshot, and explains the
          // intermittency: it only manifests when the customer's cart was
          // genuinely empty when the product page was first loaded (so
          // 'is-empty' was baked into the initial HTML at all).
          // product-form.js's own native path already guards against this
          // exact scenario, on this exact element, in its own .finally()
          // (`if (this.cart && this.cart.classList.contains('is-empty'))
          // this.cart.classList.remove('is-empty');`) — the combined path
          // never replicated that cleanup. This mirrors it exactly, never
          // touching cart-drawer.js itself, and stays inside this same
          // try/catch so any unexpected failure still falls through to
          // the existing redirectToCartAfterSuccessfulAdd() safety net
          // rather than throwing uncaught.
          if (cart.classList && cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
          const { sections, sectionIds } = await this.fetchFreshCartSections(cart);
          const sectionsValid = this.hasRequiredCartSections(sections, sectionIds);
          if (!sectionsValid) {
            throw new Error('Section refresh returned incomplete data');
          }
          cart.renderContents({ sections });
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bpf-memorial-designer', productVariantId: memorialItem.id });
          }
        } catch (error) {
          this.redirectToCartAfterSuccessfulAdd();
        }
      }

      // Phase 5C.1K, Part 40 — a controlled, VISIBLE failure state
      // (never a silent drop) for a genuine COMMERCE MUTATION failure —
      // i.e. nothing was added at all. Distinct from
      // redirectToCartAfterSuccessfulAdd(), which handles the separate
      // "merchandise added, only the preview refresh failed" case and
      // must never share this message. Reuses the theme's own existing
      // product-form error UI (product-form.js's own
      // handleErrorMessage()) rather than inventing new UI. Also logged
      // to the console for QA/diagnostics. Deliberately does not throw
      // — the caller always resolves normally right after calling this,
      // and the button/loading state is restored by
      // bindPrePurchaseImageUpload()'s own .finally(), never here, so
      // this stays a pure "show the message" concern.
      reportCombinedCartFailure(message) {
        // eslint-disable-next-line no-console
        console.error('[bpf-memorial-designer] Combined Memorial + Picture Frame cart add failed:', message);
        if (this.productForm && typeof this.productForm.handleErrorMessage === 'function') {
          this.productForm.handleErrorMessage(message);
        }
      }

      // Purchase-flow simplification, Full entry mode ONLY — post-Add-to-
      // Cart add-on popup. Everything below this point is reached ONLY
      // when this.entryMode === 'full'; every method guards on it (or on
      // this.addOnPopup being null, which bindFullModeAddOnPopup() below
      // guarantees whenever the mode isn't 'full', since the markup
      // itself never renders for other modes — see sections/bpf-
      // memorial-designer.liquid).

      // Caches the popup's markup/controls (a no-op if the mode isn't
      // 'full' or the markup isn't present) and subscribes exactly once
      // to the two pub/sub events product-form.js's native
      // onSubmitHandler already publishes on every Add to Cart
      // (PUB_SUB_EVENTS.cartUpdate on success, right before it calls
      // cart.renderContents(); PUB_SUB_EVENTS.cartError on failure) — no
      // changes to product-form.js itself were needed. Called once from
      // connectedCallback(), wrapped in try/catch there.
      bindFullModeAddOnPopup() {
        if (this.entryMode !== 'full') return;
        this.addOnPopup = document.querySelector('[data-bpf-addon-popup]');
        if (!this.addOnPopup) return;
        // Idempotency guard — connectedCallback() (which calls this) can
        // in principle run more than once for the same node (Theme
        // Editor section reload, same reasoning as
        // bindPrePurchaseImageUpload()'s own guard). subscribe() itself
        // has no dedup (assets/pubsub.js just appends), so without this
        // a reconnect would stack a second cartUpdate/cartError
        // subscription and a second click listener on every button,
        // each firing once per real event/click.
        if (this.addOnPopup.dataset.bpfAddonPopupBound === 'true') return;
        this.addOnPopup.dataset.bpfAddonPopupBound = 'true';

        this.addOnPopupTitle = this.addOnPopup.querySelector('[data-bpf-addon-popup-title]');
        this.addOnFrameSection = this.addOnPopup.querySelector('[data-bpf-addon-frame-section]');
        this.addOnFrameColorSelect = this.addOnPopup.querySelector('[data-bpf-addon-frame-color]');
        this.addOnFrameAddBtn = this.addOnPopup.querySelector('[data-bpf-addon-frame-add]');
        this.addOnFrameStatus = this.addOnPopup.querySelector('[data-bpf-addon-frame-status]');
        this.addOnWallSection = this.addOnPopup.querySelector('[data-bpf-addon-wall-section]');
        this.addOnWallNameInput = this.addOnPopup.querySelector('[data-bpf-addon-wall-name]');
        this.addOnWallDetails = this.addOnPopup.querySelector('[data-bpf-addon-wall-details]');
        this.addOnWallStoryInput = this.addOnPopup.querySelector('[data-bpf-addon-wall-story]');
        this.addOnWallPhotoInput = this.addOnPopup.querySelector('[data-bpf-addon-wall-photo-input]');
        this.addOnWallPhotoFilename = this.addOnPopup.querySelector('[data-bpf-addon-wall-photo-filename]');
        this.addOnWallAddBtn = this.addOnPopup.querySelector('[data-bpf-addon-wall-add]');
        this.addOnWallStatus = this.addOnPopup.querySelector('[data-bpf-addon-wall-status]');
        this.addOnDoneBtn = this.addOnPopup.querySelector('[data-bpf-addon-popup-done]');
        this.addOnCloseBtn = this.addOnPopup.querySelector('[data-bpf-addon-popup-close]');
        this._addOnWallPhotoFile = null;
        // Captured once, here, before either button's textContent can
        // ever have been mutated by a successful add — addPictureFrameFromPopup()/
        // addMemorialWallFromPopup() overwrite it to "✓ ... Added" on
        // success. openFullModeAddOnPopup() needs these to restore the
        // original label on reopen (a second Memorial in the same page
        // session, or the cart drawer's own re-add trigger reusing
        // this same popup/button instance) — without this, real
        // testing found the button stayed stuck on "✓ Frame Added"
        // even after its data-bpf-addon-added attribute was correctly
        // cleared, a confusing added-looking-but-actually-clickable
        // state.
        this._addOnFrameAddBtnDefaultText = this.addOnFrameAddBtn ? this.addOnFrameAddBtn.textContent : '';
        this._addOnWallAddBtnDefaultText = this.addOnWallAddBtn ? this.addOnWallAddBtn.textContent : '';

        if (this.addOnWallPhotoInput) {
          this.addOnWallPhotoInput.addEventListener('change', () => {
            const file = this.addOnWallPhotoInput.files && this.addOnWallPhotoInput.files[0];
            this._addOnWallPhotoFile = file || null;
            if (this.addOnWallPhotoFilename) {
              this.addOnWallPhotoFilename.textContent = file ? file.name : 'No photo selected';
            }
          });
        }

        // Refinement: Pet's Name alone drives whether the optional
        // Description/Photo group is shown — plain `hidden` toggle
        // (updateAddOnWallDetailsVisibility() below), the same native-
        // attribute mechanism already proven by updatePictureFrameVisibility().
        // Deliberately never clears addOnWallStoryInput/addOnWallPhotoInput's
        // own values when collapsing (only toggles `hidden`) — a
        // customer who types a description, briefly clears the name,
        // then retypes it, never loses what they already entered in
        // this popup session. Typing a name by itself never calls any
        // API — only the explicit "Add to Memorial Wall" click
        // (addMemorialWallFromPopup()) ever does that.
        if (this.addOnWallNameInput) {
          this.addOnWallNameInput.addEventListener('input', () => this.updateAddOnWallDetailsVisibility());
        }

        if (this.addOnFrameAddBtn) {
          this.addOnFrameAddBtn.addEventListener('click', () => this.addPictureFrameFromPopup());
        }
        if (this.addOnWallAddBtn) {
          this.addOnWallAddBtn.addEventListener('click', () => this.addMemorialWallFromPopup());
        }
        if (this.addOnDoneBtn) {
          this.addOnDoneBtn.addEventListener('click', () => this.closeFullModeAddOnPopup());
        }
        if (this.addOnCloseBtn) {
          this.addOnCloseBtn.addEventListener('click', () => this.closeFullModeAddOnPopup());
        }
        // ModalDialog.hide() (assets/global.js) is also reachable via
        // Escape/backdrop-click, neither of which goes through either
        // button above — without this, those paths would leave
        // cart.open() permanently suppressed instead of restoring it.
        // Real mobile testing caught a genuine bug here: hide() itself
        // dispatches 'modalClosed' on document.body (assets/global.js:524),
        // never on the modal-dialog element itself — a listener
        // attached to this.addOnPopup (as this originally was) can
        // never receive it, since the event neither originates there
        // nor bubbles backward from body into it. Escape/backdrop-
        // close was silently leaving cart.open() suppressed forever
        // (confirmed: the popup itself closed correctly via hide()'s
        // own synchronous removeAttribute('open'), but the drawer
        // never opened afterward because this listener never fired).
        document.body.addEventListener('modalClosed', () => {
          if (this._addOnPopupOpen) this.closeFullModeAddOnPopup({ alreadyHidden: true });
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (this.entryMode !== 'full' || !this._suppressedCartOpen) return;
            // Belt-and-suspenders: only react to the cartUpdate this
            // component's own suppressed submit is actually waiting
            // for, never an unrelated product form elsewhere on the
            // page (see prepareFullModeAddOnPopup() for where
            // _pendingMemorialVariantId is captured).
            if (
              this._pendingMemorialVariantId != null &&
              event &&
              event.productVariantId != null &&
              String(event.productVariantId) !== String(this._pendingMemorialVariantId)
            ) {
              return;
            }
            // Deliberately does NOT restore cart.open() here. publish()
            // (assets/pubsub.js) calls every subscriber synchronously,
            // and product-form.js's own onSubmitHandler publishes
            // cartUpdate (line ~63) BEFORE its very next line calls
            // cart.renderContents(response) — whose own auto-open is a
            // zero-delay setTimeout (cart-drawer.js), i.e. still
            // strictly in the future at this exact point. Restoring the
            // real open() here would put it back in place before that
            // setTimeout ever fires, defeating the suppression entirely
            // (confirmed by real end-to-end testing: the drawer and the
            // popup both ended up open at once). The suppression must
            // stay live through renderContents()'s own call — it's
            // lifted only in closeFullModeAddOnPopup(), when the
            // customer actually dismisses this popup.
            if (this._suppressedCartOpenSafetyTimer) {
              window.clearTimeout(this._suppressedCartOpenSafetyTimer);
              this._suppressedCartOpenSafetyTimer = null;
            }
            const cart = this._suppressedCartOpen ? this._suppressedCartOpen.cart : null;
            this.openFullModeAddOnPopup(cart);
          });
          subscribe(PUB_SUB_EVENTS.cartError, () => {
            if (this.entryMode !== 'full') return;
            // The native Add to Cart itself failed — nothing was added,
            // so there is nothing to offer add-ons for. Just release the
            // suppressed open; product-form.js's own error UI already
            // handles telling the customer. Safe to fully restore here
            // (unlike cartUpdate above) — a failed submit never reaches
            // product-form.js's renderContents() call at all, so there
            // is no pending setTimeout(...open()) this could race with.
            this.restoreCartOpen();
          });
          // Purchase-flow simplification, Full entry mode ONLY — the
          // reported defect this closes: removing a Picture Frame line
          // via the cart drawer used to leave the memorial's own
          // _Picture Frame Orientation/_Picture Frame Color properties
          // stale (the cart preview kept showing a frame that was no
          // longer actually in the cart). assets/cart.js's own
          // updateQuantity() now publishes this event after every
          // quantity change/removal (a new, but inert-unless-subscribed,
          // reuse of an event name assets/constants.js already defined
          // and no other code published before this) — see
          // cleanupOrphanedFrameProperties() for the actual cleanup.
          subscribe(PUB_SUB_EVENTS.quantityUpdate, (event) => {
            if (this.entryMode !== 'full' || !event || !event.cart) return;
            this.cleanupOrphanedFrameProperties(event.cart);
          });
        }

        this.bindCartAddFrameTrigger();
      }

      // Called from the fast-path return in bindPrePurchaseImageUpload(),
      // synchronously, before the native submit it's deliberately
      // letting through ever reaches product-form.js. Captures which
      // variant this submit is for (so the cartUpdate subscriber above
      // can confirm a later event is really this one) and swaps the
      // live cart component's own open() method for a no-op for exactly
      // the duration of this one pending Add to Cart — restored by
      // restoreCartOpen() the moment cartUpdate/cartError fires, or by
      // the safety timer below if neither ever does.
      //
      // Also fires off (never awaited here — this must stay
      // synchronous so it never delays/reorders the native submit it's
      // deliberately letting through) a /cart.js snapshot of every line
      // key that exists BEFORE this Add to Cart. resolvePendingMemorialLineKey()
      // uses this to positively identify the exact line THIS submission
      // creates — real end-to-end testing with two memorials sharing
      // the same variant but different personalization found that
      // matching by variant id alone (the previous approach) cannot
      // tell them apart; Shopify's own cart line `key` already encodes
      // variant + properties, so "the matching-variant line whose key
      // wasn't here before" is a correct, unambiguous identifier even
      // when two memorials share a variant.
      // crypto.randomUUID() is available in every browser this theme
      // already requires (fetch/async-await/FormData, used unconditionally
      // throughout this file) under HTTPS, which every real storefront
      // request is — the manual fallback only matters for an unusual
      // non-secure-context edge case and never needs to be
      // cryptographically strong, just unique enough to tell two
      // memorials apart within one browser session.
      generateMemorialInstanceId() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
          return crypto.randomUUID();
        }
        return 'bpf-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
      }

      prepareFullModeAddOnPopup(form) {
        if (!this.addOnPopup) return;
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (!cart || typeof cart.open !== 'function') return;

        const idField = form.querySelector('[name="id"]');
        this._pendingMemorialVariantId = idField ? idField.value : null;

        // Multi-memorial frame association fix — generated fresh for
        // THIS submission specifically (never reused across a second
        // Memorial added later in the same page session) and written
        // directly into the hidden field (sections/bpf-memorial-
        // designer.liquid, data-bpf-memorial-instance-id-field) so
        // it's baked into the memorial's cart line from this exact
        // Add to Cart — see that field's own comment for why this,
        // not the cart line `key`, is the one stable identifier the
        // frame-association logic below can trust. Queried via
        // this.querySelector(), NOT form.querySelector() — like the
        // GID fields it sits beside, this input lives outside the
        // <form> element itself and is only cross-associated to it via
        // its own form="..." attribute (setBuilderImageGidField()'s
        // own comment explains why), so form.querySelector() would
        // never find it — confirmed by real end-to-end testing: every
        // memorial's _Memorial Instance ID was silently blank, so two
        // same-variant memorials' frames were indistinguishable and
        // merged into one line instead of two.
        const instanceIdField = this.querySelector('[data-bpf-memorial-instance-id-field]');
        if (instanceIdField) {
          instanceIdField.value = this.generateMemorialInstanceId();
        }

        this._pendingPreSubmitCartKeysPromise = fetch('/cart.js')
          .then((response) => response.json())
          .then((snapshotCart) => new Set((snapshotCart.items || []).map((item) => item.key)))
          .catch(() => new Set());

        // Don't stack a second suppression on top of one already
        // pending (e.g. a rapid double Add to Cart) — restore the first
        // one's safety timer instead of leaking it, then install fresh.
        if (this._suppressedCartOpen) this.restoreCartOpen();

        const originalOpen = cart.open.bind(cart);
        cart.open = () => {};
        this._suppressedCartOpen = { cart, originalOpen };
        this._suppressedCartOpenSafetyTimer = window.setTimeout(() => {
          this.restoreCartOpen();
        }, 8000);
      }

      // The one place cart.open() is ever put back — restores the
      // original method (so every later, unrelated cart interaction on
      // the page behaves normally again) and clears the safety timer.
      // Deliberately does NOT call the restored open() itself; callers
      // decide separately whether/when the drawer should actually open.
      // Returns the cart element for convenience (callers that just
      // restored it usually need it immediately after).
      restoreCartOpen() {
        if (this._suppressedCartOpenSafetyTimer) {
          window.clearTimeout(this._suppressedCartOpenSafetyTimer);
          this._suppressedCartOpenSafetyTimer = null;
        }
        if (!this._suppressedCartOpen) return null;
        const { cart, originalOpen } = this._suppressedCartOpen;
        cart.open = originalOpen;
        this._suppressedCartOpen = null;
        return cart;
      }

      // Resets the popup to its initial, un-added state (in case the
      // customer adds a second Memorial and sees this popup again later
      // in the same page session — see the re-entrancy comment on
      // bindPrePurchaseImageUpload()'s own uploads-complete flag for why
      // that scenario is explicitly supported, not an edge case to
      // ignore) and shows it. this._pendingCart is stashed for the
      // add-on actions below, which need it to refresh cart sections
      // after their own mutation.
      //
      // `options.cartTriggered` is set only by bindCartAddFrameTrigger()
      // below (the cart drawer's own "+ Add a Memorial Picture Frame"
      // affordance for a line that lost its frame) — that context opens
      // with the drawer already visible and no cart.open() suppression
      // in play at all, so this hides the Memorial Wall section
      // entirely (re-adding a frame is the one and only thing that
      // trigger offers) and closeFullModeAddOnPopup() below knows not
      // to try opening/suppressing the drawer.
      openFullModeAddOnPopup(cart, options) {
        if (!this.addOnPopup) return;
        const cartTriggered = Boolean(options && options.cartTriggered);
        this._pendingCart = cart || null;
        this._addOnPopupCartTriggered = cartTriggered;

        if (this.addOnFrameAddBtn) {
          this.addOnFrameAddBtn.removeAttribute('data-bpf-addon-added');
          this.addOnFrameAddBtn.removeAttribute('disabled');
          this.addOnFrameAddBtn.textContent = this._addOnFrameAddBtnDefaultText;
        }
        if (this.addOnWallAddBtn) {
          this.addOnWallAddBtn.removeAttribute('data-bpf-addon-added');
          this.addOnWallAddBtn.removeAttribute('disabled');
          this.addOnWallAddBtn.textContent = this._addOnWallAddBtnDefaultText;
        }
        this.setAddOnStatus(this.addOnFrameStatus, '');
        this.setAddOnStatus(this.addOnWallStatus, '');
        if (this.addOnWallSection) this.addOnWallSection.hidden = cartTriggered;
        this._addOnWallPhotoFile = null;
        if (this.addOnWallPhotoInput) this.addOnWallPhotoInput.value = '';
        if (this.addOnWallPhotoFilename) this.addOnWallPhotoFilename.textContent = 'No photo selected';
        if (this.addOnWallStoryInput) this.addOnWallStoryInput.value = '';
        if (this.addOnWallNameInput) {
          // Pre-fill from whatever the main form's own Companion Name
          // field already holds (Step 1 — that field/input is never
          // removed for Full mode, only the Memorial Wall accordion
          // that used to require it is) — never required to retype a
          // name the customer may have already entered.
          this.addOnWallNameInput.value = this.companionNameInput ? this.companionNameInput.value.trim() : '';
        }
        // Syncs the Description/Photo reveal to whatever name value
        // was just set above (expanded immediately if pre-filled from
        // Step 1, collapsed otherwise) — this is a genuinely NEW popup
        // appearance (a different memorial, or the cart-triggered re-
        // add flow), so starting collapsed/matching-the-prefill here
        // is correct; it's only CLEARING the name WITHIN one already-
        // open popup that must preserve the fields' values (handled by
        // the plain `hidden` toggle in updateAddOnWallDetailsVisibility()
        // itself, never by this reset).
        this.updateAddOnWallDetailsVisibility();
        if (this.addOnPopupTitle) {
          this.addOnPopupTitle.textContent = cartTriggered
            ? 'Add a Memorial Picture Frame'
            : 'Your memorial has been added to your cart!';
        }
        if (this.addOnDoneBtn) {
          this.addOnDoneBtn.textContent = cartTriggered ? 'Close' : 'No Thanks, Take Me To My Cart';
        }
        // Footer label fix: reset per popup appearance (a new memorial,
        // or this same memorial's popup reopening) so a previous
        // memorial's successful add-on never leaves "View My Cart"
        // showing before anything has actually been saved for THIS
        // one — see markAddOnSaved() for the one place this flips.
        this._addOnAnySuccess = false;

        this._addOnPopupOpen = true;
        this.addOnPopup.show(this.addOnFrameAddBtn || null);
      }

      // The one place this popup is ever dismissed, whichever of its
      // three exits (Close ✕, "No Thanks," or Escape/backdrop-click via
      // the modalClosed listener in bindFullModeAddOnPopup()) triggered
      // it. Opens the real cart drawer afterward — by this point any
      // add-on the customer chose has already refreshed its contents
      // (see addPictureFrameFromPopup()/addMemorialWallFromPopup()), so
      // the drawer that finally appears always reflects the complete,
      // final cart. When this popup was opened from
      // bindCartAddFrameTrigger() (the cart drawer's own "+ Add a
      // Memorial Picture Frame" affordance), that handler already
      // closed the drawer before showing this popup — real mobile
      // testing found both overlays open at once otherwise (same
      // z-index, popup genuinely open but visually buried behind the
      // still-open drawer) — so this reopens it directly with a plain
      // cart.open() call, none of the cart.open()-suppression
      // machinery below involved (nothing was ever suppressed in this
      // path — see bindCartAddFrameTrigger()'s own comment).
      closeFullModeAddOnPopup(options) {
        const alreadyHidden = Boolean(options && options.alreadyHidden);
        this._addOnPopupOpen = false;
        if (!alreadyHidden && this.addOnPopup) this.addOnPopup.hide();
        if (this._addOnPopupCartTriggered) {
          const cart = this._pendingCart;
          this._pendingCart = null;
          this._addOnPopupCartTriggered = false;
          if (cart && typeof cart.open === 'function') cart.open();
          return;
        }
        // restoreCartOpen() puts the cart component's REAL open() method
        // back in place — it's still the no-op prepareFullModeAddOnPopup()
        // installed, deliberately held through the entire time this
        // popup was showing (see the cartUpdate subscriber's own
        // comment in bindFullModeAddOnPopup() for why). Only now, with
        // the real method restored, is it safe to actually call it.
        const cart = this.restoreCartOpen() || this._pendingCart;
        if (cart && typeof cart.open === 'function') {
          cart.open();
        }
        this._pendingCart = null;
      }

      setAddOnStatus(statusEl, message, tone) {
        if (!statusEl) return;
        statusEl.textContent = message || '';
        if (tone) {
          statusEl.setAttribute('data-bpf-addon-status-tone', tone);
        } else {
          statusEl.removeAttribute('data-bpf-addon-status-tone');
        }
      }

      // Footer label fix: the ONLY place "No Thanks, Take Me To My
      // Cart" ever becomes "View My Cart" — called exclusively from
      // addPictureFrameFromPopup()/addMemorialWallFromPopup()'s own
      // SUCCESS branches, never from an input/change listener and
      // never from either function's error branch, so typing into a
      // field, picking a color, or a failed save can never trigger it.
      // Deliberately a no-op for the cart-triggered re-add flow (its
      // "Close" button already means something different — the drawer
      // is already open behind it, nothing to "go to"). this._addOnAnySuccess
      // itself isn't read anywhere else; it exists purely so a future
      // caller could check "did anything save this popup session" —
      // openFullModeAddOnPopup() resets it (and the label) on every
      // new appearance, which is what actually prevents carryover to a
      // different memorial.
      markAddOnSaved() {
        this._addOnAnySuccess = true;
        if (this._addOnPopupCartTriggered) return;
        if (this.addOnDoneBtn) this.addOnDoneBtn.textContent = 'View My Cart';
      }

      // The one place the Description/Photo group's visibility is ever
      // decided — a plain `hidden` toggle keyed on whether Pet's Name
      // is non-whitespace, same native-attribute mechanism as
      // updatePictureFrameVisibility(). Never touches either field's
      // own value, so collapsing (clearing the name) never discards an
      // already-typed description or a chosen photo file within the
      // same popup appearance — only openFullModeAddOnPopup() resets
      // those, for a genuinely new popup appearance.
      updateAddOnWallDetailsVisibility() {
        if (!this.addOnWallDetails || !this.addOnWallNameInput) return;
        const hasName = this.addOnWallNameInput.value.trim().length > 0;
        this.addOnWallDetails.hidden = !hasName;
      }

      // Fetches the CURRENT cart and returns the exact line item this
      // popup's own Add to Cart created, never merely "a" line that
      // happens to share its variant. Real end-to-end testing with two
      // memorials sharing the same variant but different
      // personalization found that variant-id matching alone cannot
      // tell them apart — Shopify's own cart line `key` already
      // encodes variant + properties, so "the matching-variant line
      // whose key did NOT exist before this submission" (the pre-
      // submit snapshot captured in prepareFullModeAddOnPopup(), before
      // ANY of this popup's own actions can have changed anything) is
      // a correct, unambiguous identifier even when two memorials
      // share a variant. Deliberately never caches the resolved key —
      // patching a line's properties changes its key (Shopify derives
      // it from variant + properties), so any cache would go stale the
      // instant the FIRST patch (e.g. the frame add-on's Orientation/
      // Color write) succeeded, which is exactly the bug a previous
      // version of this code had (confirmed by real end-to-end
      // testing: the second patch 400'd against an already-stale
      // cached key). Every caller re-resolves fresh instead.
      //
      // Falls back to "the last matching-variant line" only when no
      // snapshot is available or nothing qualifies as new — the one
      // case that degrades to is a customer adding a BYTE-FOR-BYTE
      // duplicate memorial (identical personalization too), which
      // Shopify itself merges into the existing line's quantity rather
      // than creating a new key, so there is genuinely no "new" key to
      // find.
      async resolvePendingMemorialLineItem() {
        if (this._pendingMemorialVariantId == null) return null;
        const response = await fetch('/cart.js');
        if (!response.ok) return null;
        const cart = await response.json();
        const items = Array.isArray(cart.items) ? cart.items : [];
        // bindCartAddFrameTrigger() sets this when the popup is opened
        // from the cart drawer's own "+ Add a Memorial Picture Frame"
        // affordance — in that context the EXACT line key is already
        // known directly from the clicked element's own data attribute
        // (no snapshot-diff ambiguity possible), so prefer it outright.
        if (this._pendingKnownLineKey) {
          const known = items.find((item) => item.key === this._pendingKnownLineKey);
          if (known) return known;
        }
        const variantMatches = items.filter(
          (item) => String(item.variant_id) === String(this._pendingMemorialVariantId)
        );
        if (this._pendingPreSubmitCartKeysPromise) {
          const preSubmitKeys = await this._pendingPreSubmitCartKeysPromise;
          const newOnes = variantMatches.filter((item) => !preSubmitKeys.has(item.key));
          if (newOnes.length) return newOnes[newOnes.length - 1];
        }
        return variantMatches.length ? variantMatches[variantMatches.length - 1] : null;
      }

      // Reported-defect fix: removing a Picture Frame line via the
      // cart drawer used to leave the memorial line's own
      // _Picture Frame Orientation/_Picture Frame Color properties
      // stale (the cart preview kept showing a frame that was no
      // longer actually in the cart, and there was no way to re-add
      // one). Called from the quantityUpdate subscriber in
      // bindFullModeAddOnPopup() with the FULL current cart (no extra
      // fetch needed — assets/cart.js's updateQuantity() already has
      // it and this event's payload carries it straight through).
      //
      // Multi-memorial frame association fix: identifies an orphan by
      // EXACT pairing, not a count heuristic — every frame line now
      // carries `_Associated Memorial Instance ID` (set in
      // addPictureFrameFromPopup()), matched against the memorial
      // line's own `_Memorial Instance ID` (written once, at Add to
      // Cart time, by prepareFullModeAddOnPopup() — see that field's
      // own comment in sections/bpf-memorial-designer.liquid for why
      // this, not the cart line `key`, is the stable identifier). A
      // memorial's frame properties are cleared only when its OWN
      // instance id has no backing frame line left, so two memorials
      // each with their own frame are never confused with each other —
      // removing one clears only its associated memorial, the other's
      // claim is untouched regardless of how many frames exist
      // simultaneously. Runs every clearing in parallel and refreshes
      // the drawer once afterward.
      async cleanupOrphanedFrameProperties(cart) {
        if (this.entryMode !== 'full' || !this.pictureFrameCommerce) return;
        const frameVariantIds = new Set(
          Object.values(this.pictureFrameCommerce.variantsByColor || {})
            .filter((variant) => variant && variant.id)
            .map((variant) => String(variant.id))
        );
        if (!frameVariantIds.size) return;
        const items = Array.isArray(cart.items) ? cart.items : [];
        const backedInstanceIds = new Set(
          items
            .filter((item) => frameVariantIds.has(String(item.variant_id)))
            .map((item) => item.properties && item.properties['_Associated Memorial Instance ID'])
            .filter(Boolean)
        );
        const orphanedMemorials = items.filter((item) => {
          if (!item.properties) return false;
          const claimsFrame = item.properties['_Picture Frame Orientation'] || item.properties['_Picture Frame Color'];
          if (!claimsFrame) return false;
          const instanceId = item.properties['_Memorial Instance ID'];
          // No instance id at all (a line from before this fix shipped,
          // or the field genuinely never got written) can't be matched
          // either way — treat it the same as "unbacked" rather than
          // leaving a permanently stale claim no cart state could ever
          // clear.
          return !instanceId || !backedInstanceIds.has(instanceId);
        });
        if (!orphanedMemorials.length) return;

        try {
          await Promise.all(
            orphanedMemorials.map((orphan) => {
              const mergedProperties = Object.assign({}, orphan.properties, {
                '_Picture Frame Orientation': '',
                '_Picture Frame Color': '',
              });
              return fetch(window.routes.cart_change_url, {
                ...fetchConfig(),
                body: JSON.stringify({ id: orphan.key, properties: mergedProperties }),
              });
            })
          );
          await this.refreshCartSectionsAfterAddOn(
            document.querySelector('cart-notification') || document.querySelector('cart-drawer')
          );
        } catch (error) {
          // Best-effort cleanup only — the frame line is already gone
          // either way; a failure here just leaves a stale
          // presentation property until the next successful cart
          // mutation, never a billing/inventory problem.
        }
      }

      // Wires the cart drawer/cart page's own "+ Add a Memorial
      // Picture Frame" affordance (snippets/bpf-cart-memorial-preview.liquid,
      // shown only for a Full-mode memorial line with no frame
      // properties — see that snippet's own bpf_show_addon_frame_cta).
      // A single document-level delegated click listener, not a
      // direct per-button binding: the cart drawer's own JS (assets/
      // cart.js) replaces that markup's innerHTML on every quantity
      // change, which would silently detach any directly-bound
      // listener — delegation survives every re-render for free, the
      // same reason bindPrePurchaseImageUpload() et al. never need to
      // re-run after a drawer refresh.
      bindCartAddFrameTrigger() {
        if (this.entryMode !== 'full' || document.documentElement.dataset.bpfCartAddFrameTriggerBound === 'true') return;
        document.documentElement.dataset.bpfCartAddFrameTriggerBound = 'true';
        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-bpf-cart-add-frame-trigger]');
          if (!trigger || !this.addOnPopup) return;
          event.preventDefault();
          this._pendingMemorialVariantId = trigger.dataset.variantId || null;
          this._pendingKnownLineKey = trigger.dataset.lineKey || null;
          this._pendingPreSubmitCartKeysPromise = null;
          // Real mobile testing caught this: the customer clicks this
          // trigger FROM INSIDE the already-open cart drawer, so unlike
          // the post-Add-to-Cart flow (where the drawer's own open()
          // is suppressed before it ever shows), both overlays would
          // otherwise end up simultaneously "open" at the same z-index
          // — the popup was confirmed genuinely open in the DOM but
          // visually hidden behind the still-open drawer. Closing the
          // drawer first, then reopening it when the popup is
          // dismissed (closeFullModeAddOnPopup()'s own cartTriggered
          // branch), keeps exactly one overlay visible at a time, the
          // same principle the suppressed-open mechanism already
          // applies to the main flow.
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cart && typeof cart.close === 'function') cart.close();
          this.openFullModeAddOnPopup(cart, { cartTriggered: true });
        });
      }

      // Shared by both add-on actions below: /cart/change.js's own
      // `properties` param REPLACES a line's entire property set rather
      // than merging into it, so this always reads the line's CURRENT
      // properties first (resolvePendingMemorialLineItem() above is
      // always a fresh /cart.js read, never a stale cache) and merges
      // `patch` on top before sending, never risking silently wiping
      // out Companion Name/Pet Story/the photo GID fields/whichever
      // properties already got there through the normal Add to Cart
      // submission.
      async applyCartLinePropertyPatch(patch) {
        const item = await this.resolvePendingMemorialLineItem();
        if (!item) throw new Error('Could not locate the memorial line to update');
        const existingProperties = item.properties || {};
        const mergedProperties = Object.assign({}, existingProperties, patch);
        const changeResponse = await fetch(window.routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({ id: item.key, properties: mergedProperties }),
        });
        if (!changeResponse.ok) throw new Error('Cart line property update failed');
        return changeResponse.json();
      }

      // Refreshes the cart-drawer's own section HTML in the background
      // (never opens it — see closeFullModeAddOnPopup() for the one
      // place that happens) after an add-on mutation has already
      // succeeded. Mirrors submitCombinedMemorialAndFrameCart()'s own
      // Stage 2/3 exactly (fetchFreshCartSections()/
      // hasRequiredCartSections()/cart.renderContents()) — the one
      // difference is the failure path: that function redirects to the
      // real cart page on a refresh failure, which would here mean
      // navigating the customer away while this popup is still open.
      // Since the merchandise mutation has already succeeded either
      // way, a refresh failure here just leaves the drawer to pick up
      // the correct, complete cart the next time anything else
      // refreshes it (any later Add to Cart, or a manual page reload) —
      // never a false "nothing was added," and never a forced
      // navigation out of this popup.
      async refreshCartSectionsAfterAddOn(cart) {
        if (!cart || typeof cart.getSectionsToRender !== 'function' || typeof cart.renderContents !== 'function') {
          return;
        }
        try {
          if (cart.classList && cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
          const { sections, sectionIds } = await this.fetchFreshCartSections(cart);
          if (!this.hasRequiredCartSections(sections, sectionIds)) {
            throw new Error('Section refresh returned incomplete data');
          }
          cart.renderContents({ sections });
        } catch (error) {
          // See this method's own comment above — intentionally silent
          // beyond this; the merchandise is already safely in the cart.
        }
      }

      // Click handler for the popup's "Add Frame to My Order" button.
      // The memorial is already in the cart by the time this can run —
      // this is deliberately NOT submitCombinedMemorialAndFrameCart()
      // (that function reads and re-adds the memorial itself, which
      // would create a SECOND memorial line). Reuses
      // buildPictureFrameCartItem() as-is for the frame's own real
      // variant/cart-line resolution (this.state.pictureFrame is set
      // from the popup's own controls immediately before calling it,
      // exactly like the pre-cart flow already did), then a second,
      // small /cart/change.js call attaches the presentation-only
      // _Picture Frame Orientation/_Picture Frame Color properties onto
      // the MEMORIAL line (see snippets/bpf-cart-memorial-preview.liquid,
      // which reads them from there, not from the frame's own line).
      //
      // Refinement: Orientation is no longer a popup control — it was
      // always a cart-preview asset-selection detail (which overlay
      // PNG to draw), never a purchase choice (buildPictureFrameCartItem()
      // never reads it; the frame PRODUCT has no portrait/landscape
      // variants). BPF_ADDON_FRAME_ORIENTATION_DEFAULT is the exact
      // same 'Portrait' value this file's own pre-cart
      // syncPictureFrameOrientationFromPhoto()/updatePictureFrameVisibility()
      // already use as their no-photo/reset default — not a new,
      // independently-invented fallback.
      async addPictureFrameFromPopup() {
        if (!this.addOnFrameAddBtn || this.addOnFrameAddBtn.hasAttribute('data-bpf-addon-added')) return;
        const orientation = 'Portrait';
        const color = this.addOnFrameColorSelect ? this.addOnFrameColorSelect.value : 'Black';

        this.addOnFrameAddBtn.setAttribute('disabled', 'disabled');
        this.setAddOnStatus(this.addOnFrameStatus, 'Adding frame…');

        this.state.pictureFrame.selected = true;
        this.state.pictureFrame.orientation = orientation;
        this.state.pictureFrame.color = color;

        const frameItem = this.buildPictureFrameCartItem();
        if (!frameItem) {
          this.state.pictureFrame.selected = false;
          this.addOnFrameAddBtn.removeAttribute('disabled');
          this.setAddOnStatus(this.addOnFrameStatus, 'Sorry, that frame color isn’t available right now.', 'error');
          return;
        }

        // Multi-memorial frame association fix: stamp the frame's OWN
        // cart line with the memorial's _Memorial Instance ID (read
        // fresh off the memorial's current line — resolvePendingMemorialLineItem()
        // already correctly disambiguates same-variant memorials via
        // the pre-submit snapshot diff / known-key path). This is a
        // deliberate change to buildPictureFrameCartItem()'s RETURNED
        // object, not to that shared function itself — breed/Product's
        // own pre-cart combined flow (submitCombinedMemorialAndFrameCart())
        // calls buildPictureFrameCartItem() independently and is
        // completely unaffected. Without this, cleanupOrphanedFrameProperties()
        // has no way to tell which of several memorials, each with its
        // own frame, a given frame line actually belongs to.
        const memorialItemForFrame = await this.resolvePendingMemorialLineItem();
        const instanceId =
          memorialItemForFrame && memorialItemForFrame.properties
            ? memorialItemForFrame.properties['_Memorial Instance ID']
            : null;
        if (instanceId) {
          frameItem.properties = { '_Associated Memorial Instance ID': instanceId };
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            ...fetchConfig(),
            body: JSON.stringify({ items: [frameItem] }),
          });
          const body = await response.json();
          if (!response.ok || (body && body.status)) {
            throw new Error((body && (body.description || body.message)) || 'Add to Cart request failed');
          }

          try {
            await this.applyCartLinePropertyPatch({
              '_Picture Frame Orientation': orientation,
              '_Picture Frame Color': color,
            });
          } catch (error) {
            // The frame IS in the cart at this point — a failure here
            // only means the cart preview's presentation of it (which
            // image/orientation to draw) may fall back to a default
            // rather than the customer's exact choice. Never worth
            // treating as "the add failed."
          }

          await this.refreshCartSectionsAfterAddOn(this._pendingCart);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bpf-memorial-designer-addon', productVariantId: frameItem.id });
          }

          this.addOnFrameAddBtn.setAttribute('data-bpf-addon-added', 'true');
          this.addOnFrameAddBtn.textContent = '✓ Frame Added';
          this.setAddOnStatus(this.addOnFrameStatus, 'Your frame has been added to your order.', 'success');
          this.markAddOnSaved();
        } catch (error) {
          this.state.pictureFrame.selected = false;
          this.addOnFrameAddBtn.removeAttribute('disabled');
          this.setAddOnStatus(this.addOnFrameStatus, 'We couldn’t add the frame — please try again.', 'error');
        }
      }

      // Click handler for the popup's "Add to Memorial Wall" button.
      // Memorial Wall has no variant/price of its own (see
      // requiresMemorialImagePersistence()'s own header comment — it's
      // pure properties[...] on the memorial's existing line), so this
      // never calls cart/add.js at all: a single /cart/change.js patch
      // (properties[Remember on Memorial Wall] = "Yes", same name/value
      // the pre-cart radio used, plus properties[Companion Name] from
      // the popup's own name field — the same single field Step 1's
      // Companion Name and the Wall's own Pet's Name have always
      // shared, see the Phase 2.5.3 header comment above) is the
      // bulk of the mutation. If the customer also chose a photo (the
      // popup's own file input — Step 4's pre-cart picker is hidden
      // for Full mode now, see applyProductDefinitionStepVisibility(),
      // since the Wall was always its only real destination), it's
      // uploaded here via the existing, standalone uploadBuilderImage()
      // (never awaited inside the ORIGINAL pre-cart submit path for
      // Full mode, by design) and its GID merged into the same patch,
      // under the exact property name (_Memorial Photo File GID) the
      // pre-cart flow already used for every other mode.
      async addMemorialWallFromPopup() {
        if (!this.addOnWallAddBtn || this.addOnWallAddBtn.hasAttribute('data-bpf-addon-added')) return;
        const name = this.addOnWallNameInput ? this.addOnWallNameInput.value.trim() : '';
        if (!name) {
          this.setAddOnStatus(this.addOnWallStatus, 'Please enter your pet’s name.', 'error');
          if (this.addOnWallNameInput) this.addOnWallNameInput.focus();
          return;
        }

        this.addOnWallAddBtn.setAttribute('disabled', 'disabled');
        this.setAddOnStatus(this.addOnWallStatus, 'Adding to the Memorial Wall…');

        const patch = {
          'Remember on Memorial Wall': 'Yes',
          'Companion Name': name,
        };

        // Optional — mirrors the pre-cart Pet Story field's own
        // property name exactly (sections/bpf-memorial-designer.liquid,
        // properties[Pet Story]) so this Wall entry's description lands
        // in the same, already-established place every other consumer
        // of that property already reads from.
        const story = this.addOnWallStoryInput ? this.addOnWallStoryInput.value.trim() : '';
        if (story) {
          patch['Pet Story'] = story;
        }

        if (this._addOnWallPhotoFile) {
          try {
            const gid = await this.uploadBuilderImage(this._addOnWallPhotoFile, this._addOnWallPhotoFile.name, {});
            patch['_Memorial Photo File GID'] = gid;
          } catch (error) {
            // The photo is cosmetic for the Wall listing, never a
            // billing/inventory concern — proceed with the Wall opt-in
            // and name regardless, same "never block on a non-critical
            // failure" posture as every other add-on action here.
          }
        }

        try {
          await this.applyCartLinePropertyPatch(patch);
          await this.refreshCartSectionsAfterAddOn(this._pendingCart);

          this.addOnWallAddBtn.setAttribute('data-bpf-addon-added', 'true');
          this.addOnWallAddBtn.textContent = '✓ Added to Memorial Wall';
          this.setAddOnStatus(this.addOnWallStatus, 'Your companion will be remembered on our Memorial Wall.', 'success');
          this.markAddOnSaved();
        } catch (error) {
          this.addOnWallAddBtn.removeAttribute('disabled');
          this.setAddOnStatus(this.addOnWallStatus, 'We couldn’t complete that — please try again.', 'error');
        }
      }

      // Phase 4D.9B.7, Part 3: shown ONLY on the Memorial Wall = Yes path
      // (bindPrePurchaseImageUpload() calls this immediately after
      // deciding to intercept — Wall = No's fast path never reaches this
      // at all), for exactly the duration of Builder-side image
      // persistence. Reuses the exact same visual language
      // updateAddToCartAvailability() (sold-out state) and product-
      // form.js (its own in-flight state) already use — native
      // `disabled` + the theme's own .loading class/.loading-overlay__spinner
      // — no new UI pattern. hideMemorialPreparingState() is always
      // called from performPrePurchaseImageUpload()'s caller inside the
      // SAME .finally() that already unconditionally resets the upload
      // guards (success, failure, or swallowed error alike), so this can
      // never get stuck — see bindPrePurchaseImageUpload().
      showMemorialPreparingState() {
        if (!this.submitButton) return;
        const span = this.submitButton.querySelector('span');
        this._preparingStateOriginalText = span ? span.textContent : null;
        if (span) span.textContent = 'Preparing your memorial…';
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.classList.add('loading');
        const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      hideMemorialPreparingState() {
        if (!this.submitButton) return;
        const span = this.submitButton.querySelector('span');
        if (span && this._preparingStateOriginalText !== null) {
          span.textContent = this._preparingStateOriginalText;
        }
        this._preparingStateOriginalText = null;
        this.submitButton.removeAttribute('disabled');
        this.submitButton.classList.remove('loading');
        const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.add('hidden');
      }

      // Phase 4D.9B.7, Part 8: the exact, and only, inputs that affect
      // the exported Memorial Canvas PNG — canvasState (everything
      // BpfMemorialRenderer.exportMemorialPreviewPNG() itself reads) plus
      // stageSrc (passed alongside it, not part of canvasState). Excludes
      // canvasState.uploadedPhoto deliberately: a File object doesn't
      // serialize meaningfully via JSON.stringify, and it's irrelevant
      // here anyway — maybePrepareMemorialCanvas() never runs at all once
      // a photo is selected (Priority 1 skips canvas entirely). Returns
      // null (never throws) if serialization fails for any reason —
      // callers treat null as "never matches a cached signature," which
      // safely falls back to a fresh render, exactly like no early
      // preparation had ever happened.
      computeCanvasStateSignature(canvasState, stageSrc) {
        try {
          return JSON.stringify(Object.assign({}, canvasState, { uploadedPhoto: undefined })) + '||' + (stageSrc || '');
        } catch (error) {
          return null;
        }
      }

      // Phase 4D.9B.7, Parts 7-11: best-effort early canvas preparation —
      // renders (never uploads) the Memorial Canvas PNG Blob in memory
      // ahead of Add to Cart, so only the network upload remains once the
      // customer actually clicks. Called from initAccordion()'s existing
      // Review-open handler (see that method) — a single, low-frequency,
      // natural "the customer has reached the final step and there is
      // dwell time before they click Add to Cart" signal, not a new
      // debounced listener on every canvas-affecting input. Safe by
      // construction rather than by proactive invalidation: this method
      // only ever WRITES a candidate Blob; performPrePurchaseImageUpload()
      // is the one place that ever READS it, and only after recomputing
      // and comparing the signature fresh at that moment — so a stale
      // cache (customer edited something after Review opened, or this
      // export ultimately failed) is always detected and safely
      // discarded in favor of a fresh render, never used as-is. A second
      // call before an earlier one resolves simply overwrites both
      // fields together, synchronously — the earlier promise is never
      // read again by anything, so a slow/stale result can never
      // overwrite a newer one.
      maybePrepareMemorialCanvas() {
        if (typeof window.BpfMemorialRenderer === 'undefined') return;
        if (!this.requiresMemorialImagePersistence()) return;
        if (this.selectedPhotoFile) return; // Priority 1 — canvas is never used once a photo exists.

        const canvasState = this.getCanvasState();
        const stageImg = this.querySelector('.bpf-memorial-canvas__stage');
        const stageSrc = stageImg ? stageImg.src : undefined;
        const signature = this.computeCanvasStateSignature(canvasState, stageSrc);
        if (signature === null) return;
        if (signature === this._preparedCanvasSignature && this._preparedCanvasBlobPromise) return; // already prepared/preparing this exact state

        bpfTimingMark('early canvas prep started (Review opened)');
        this._preparedCanvasSignature = signature;
        this._preparedCanvasBlobPromise = window.BpfMemorialRenderer
          .exportMemorialPreviewPNG(canvasState, { stageSrc: stageSrc })
          .then((blob) => {
            bpfTimingMark('early canvas prep complete');
            return blob;
          })
          .catch(() => null); // a failed early prepare is not fatal — performPrePurchaseImageUpload() detects the null and renders fresh instead.
      }

      // Phase 4D.10D — early canvas PERSISTENCE: takes maybePrepareMemorialCanvas()
      // one step further by also UPLOADING the prepared Blob (a real
      // network request to /apps/memorial/image) well before Add to
      // Cart, caching the resolved Shopify File GID against the exact
      // signature it was uploaded for. This is what actually eliminates
      // the ~3-4s Wall=Yes/no-photo click-time delay: the render itself
      // was already made cheap by early preparation (Phase 4D.9B.7 /
      // 4D.10C.3), but performPrePurchaseImageUpload() was still doing
      // the real file upload synchronously at click time — that upload,
      // not the render, is the dominant cost for a multi-hundred-KB PNG.
      //
      // Same guards as maybePrepareMemorialCanvas() (Wall !== Yes, or a
      // customer photo already exists — Priority 1, a fallback canvas is
      // never uploaded once a real photo exists — Part 8/9 locked
      // requirements), so this is a guaranteed no-op for every case that
      // doesn't need a persisted fallback canvas at all. Deliberately
      // its own method rather than folded into maybePrepareMemorialCanvas()
      // itself — keeps the well-established, already-tested prep-only
      // behavior completely unchanged; this only ever ADDS a further
      // step on top of it.
      //
      // Phase 5D.1D — the ONLY two call sites are Review-open and Add to
      // Cart (both gated on Wall=Yes actually being selected already, so
      // this never fires on initial page load or an unrelated early-
      // Builder-step change). handleChange() no longer calls this at
      // all: 5D.1C proved the backend's 5-requests/60-minutes-per-IP
      // limiter is shared with the Memorial submission endpoint and was
      // sized for a once-per-session form, not a per-edit upload — even
      // 5D.1B's 750ms debounce still produced one real request per
      // distinct settled configuration a customer paused on, which
      // ordinary product comparison (several colors/options, each
      // considered for >750ms) can easily exceed. Reuses the exact same
      // signature-based dedup this method already had — Review opened
      // twice with nothing changed, or Add to Cart right after a Review
      // that already persisted the current signature, are both cheap
      // no-ops via the check below, never a second upload.
      maybePersistMemorialCanvas() {
        if (typeof window.BpfMemorialRenderer === 'undefined') return;
        if (!this.requiresMemorialImagePersistence()) return;
        if (this.selectedPhotoFile) return; // Priority 1 — a fallback canvas is never persisted once a photo exists.

        const canvasState = this.getCanvasState();
        const stageImg = this.querySelector('.bpf-memorial-canvas__stage');
        const stageSrc = stageImg ? stageImg.src : undefined;
        const signature = this.computeCanvasStateSignature(canvasState, stageSrc);
        if (signature === null) return;
        if (signature === this._persistedCanvasSignature && this._persistedCanvasGidPromise) return; // already persisted/persisting this exact state

        // Ensures a prepared Blob exists/starts preparing for this exact
        // signature — reuses maybePrepareMemorialCanvas()'s own cache
        // when it's already current (the common case, since both are
        // triggered from the same call sites) rather than duplicating
        // the render logic here.
        this.maybePrepareMemorialCanvas();
        if (signature !== this._preparedCanvasSignature || !this._preparedCanvasBlobPromise) return; // prep didn't start/match — performPrePurchaseImageUpload()'s own safety net still covers this at click time

        // Phase 5D.1B — at most one active canvas upload per instance: a
        // still-running upload for an OLDER, now-superseded signature is
        // aborted here — never left to complete wastefully — before this
        // new one starts (Part 10/11). The abort is intentional control
        // flow, not a failure: the old attempt's own .catch() below
        // detects this purely via controller identity (see the check
        // there) and stays completely silent — no console error, no
        // effect on the state this new attempt is about to set up.
        if (this._activeCanvasUploadController) {
          this._activeCanvasUploadController.abort();
        }

        bpfTimingMark('early canvas persistence started');
        this._persistedCanvasSignature = signature;
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        this._activeCanvasUploadController = controller;
        this._persistedCanvasGidPromise = this._preparedCanvasBlobPromise
          .then((blob) => {
            if (!blob) throw new Error('bpf-memorial-designer: no prepared canvas Blob to persist');
            return this.uploadBuilderImage(blob, 'memorial-canvas.png', controller ? { signal: controller.signal } : undefined);
          })
          .then((gid) => {
            // Stale-completion protection (Part 13): only a still-current
            // attempt (this exact controller is still the active one) may
            // ever be treated as authoritative — guards the (normally
            // impossible, since abort() is synchronous, but still worth
            // defending against) race where a superseded attempt somehow
            // resolves instead of rejecting.
            if (this._activeCanvasUploadController !== controller) return undefined;
            this._activeCanvasUploadController = null;
            bpfTimingMark('early canvas persistence complete');
            return gid;
          })
          .catch((error) => {
            // Race-safe cleanup (Part 13/30): only clear state that
            // STILL belongs to THIS exact attempt. Checking controller
            // identity first — rather than signature, which a newer call
            // already overwrote synchronously above — is what makes this
            // safe regardless of arrival order: a superseded/aborted
            // attempt's own late rejection can never null out a NEWER
            // attempt's signature, promise, or controller.
            if (this._activeCanvasUploadController !== controller) {
              return undefined; // intentionally superseded, or a genuinely-late stale rejection — either way, no longer relevant, nothing to clean up here.
            }
            this._activeCanvasUploadController = null;
            // Failure — never leave a broken promise cached against this
            // signature; the next canvas-affecting change (or a later
            // call with the same signature) gets a genuinely fresh
            // attempt. performPrePurchaseImageUpload()'s own existing
            // fresh-render-and-upload fallback already handles this
            // safely at click time regardless, so Add to Cart is never
            // blocked by an early persistence failure.
            if (this._persistedCanvasSignature === signature) {
              this._persistedCanvasSignature = null;
              this._persistedCanvasGidPromise = null;
            }
            throw error;
          });
        // Best-effort, fire-and-forget from every call site — silence
        // the "unhandled rejection" this derived chain would otherwise
        // produce on failure; the STORED promise (this._persistedCanvasGidPromise)
        // still correctly rejects for performPrePurchaseImageUpload() to
        // catch when it actually awaits it.
        this._persistedCanvasGidPromise.catch(() => {});
      }

      // The Favorite Photo path already starts uploading eagerly in
      // handlePhotoChange() as soon as the customer picks a file, but a
      // fast Add to Cart click can arrive before that upload settles —
      // this function awaits the SAME stored promise (never starts a
      // second upload) so the photo GID field is reliably populated
      // before the real submission proceeds. Either way, a photo
      // selection means Priority 1 applies and canvas export never runs,
      // even if the photo upload itself ultimately failed/timed out — a
      // failed photo upload falls through to Priority 3 (no image), per
      // the locked precedence rule, never silently to Priority 2/canvas.
      // This function never throws — a canvas export/upload failure must
      // never block Add to Cart (Phase 4D.8C Part 2); the post-purchase
      // processor's Priority 3 fallback already handles a missing canvas
      // GID safely. uploadBuilderImage()'s own internal timeout bounds
      // the canvas branch's awaits; the photo branch is additionally
      // wrapped in bpfWithTimeout() (Phase 4D.9B.9) as an independent
      // backstop, so neither branch can ever hang indefinitely even if
      // one timeout mechanism doesn't fire as expected.
      async performPrePurchaseImageUpload(form) {
        // Defensive — bindPrePurchaseImageUpload() already only calls this
        // when requiresMemorialImagePersistence() is true, so this should
        // never actually be reached with it false; kept so this function
        // stays correct on its own if ever called from elsewhere.
        if (!this.requiresMemorialImagePersistence()) return;
        bpfTimingMark('T4Y image persistence begins');

        if (this.selectedPhotoFile) {
          bpfTimingMark('T4Y branch: Favorite Photo (awaiting eager upload if still in flight)');
          try {
            if (this._photoUploadPromise) {
              // Phase 4D.9B.9: bounded independently of
              // uploadBuilderImage()'s own internal timeout — see
              // bpfWithTimeout()'s comment. Guarantees this await can
              // never leave Add to Cart waiting indefinitely.
              await bpfWithTimeout(this._photoUploadPromise, BPF_BUILDER_IMAGE_UPLOAD_TIMEOUT_MS);
            }
          } catch (error) {
            // Deliberately swallowed — matches this function's own
            // documented "never throws" contract, same as the canvas
            // branch below.
          }
          bpfTimingMark('T7Y image persistence complete (photo)');
          return; // Priority 1 — never fall through to canvas, regardless of outcome above.
        }

        if (typeof window.BpfMemorialRenderer === 'undefined') return;

        try {
          const canvasState = this.getCanvasState();
          const stageImg = this.querySelector('.bpf-memorial-canvas__stage');
          const stageSrc = stageImg ? stageImg.src : undefined;
          const signature = this.computeCanvasStateSignature(canvasState, stageSrc);

          // Phase 4D.10D: reuse an already-PERSISTED canvas (rendered AND
          // uploaded, GID already resolved) for this exact signature
          // first — zero new export, zero new upload, this is the entire
          // point of early persistence (maybePersistMemorialCanvas()).
          // Recomputed and compared fresh right here, same discipline as
          // the prepared-Blob reuse below: a signature mismatch or a
          // failed/timed-out early persistence both fall straight through
          // to the existing prepared-Blob-then-fresh-export-then-upload
          // path below, completely unchanged — never a stale image,
          // never a hard dependency on early persistence having happened
          // or succeeded.
          if (signature !== null && signature === this._persistedCanvasSignature && this._persistedCanvasGidPromise) {
            try {
              bpfTimingMark('T4Y branch: reusing early-persisted canvas GID (zero new upload)');
              // Deliberately NOT bpfWithTimeout() here — that helper
              // never rejects and discards the settled value by design
              // (see its own header comment; the photo branch above
              // never needs its resolved value). This reuse path DOES
              // need the actual GID, and needs to tell "timed out" apart
              // from "resolved" so a slow-but-still-pending persistence
              // correctly falls through to the fresh render+upload path
              // below instead of being mistaken for success with an
              // undefined GID. A plain bounded Promise.race gives both.
              let timedOut = false;
              const persistedGid = await Promise.race([
                this._persistedCanvasGidPromise,
                new Promise((resolve) => {
                  setTimeout(() => {
                    timedOut = true;
                    resolve(undefined);
                  }, BPF_BUILDER_IMAGE_UPLOAD_TIMEOUT_MS);
                }),
              ]);
              if (!timedOut && persistedGid) {
                bpfTimingMark('T7Y image persistence complete (canvas, early-persisted)');
                this.setBuilderImageGidField('canvas', persistedGid);
                return;
              }
            } catch (error) {
              // Early persistence failed or timed out — fall through to
              // the existing fresh render+upload path below, same as any
              // other prepared-Blob miss.
            }
          }

          // Phase 4D.9B.7, Part 7: reuse maybePrepareMemorialCanvas()'s
          // early-rendered Blob ONLY if it was prepared for this exact
          // canvas-affecting state — recomputed and compared fresh right
          // here, never trusted from an earlier check. A signature
          // mismatch (customer edited something after Review opened) or
          // a failed early prepare (resolves to null) both fall straight
          // through to the same fresh render this function has always
          // done — never a stale image, never a hard dependency on early
          // preparation having happened or succeeded at all.
          //
          // Phase 5C.1Q — ONE shared renderer deadline for this purchase
          // attempt, computed once here and reused (never reset) by both
          // possible renderer waits below: the prepared-Blob reuse wait
          // and the fresh-export fallback. Each wait is bounded by
          // whatever time is actually LEFT on this same deadline, not by
          // a fresh BPF_CANVAS_EXPORT_TIMEOUT_MS each time — a prepared
          // Blob that actively rejects after burning part of the budget
          // lets the fresh attempt use only what remains (still-valid
          // recovery, Part 9/13), never a fresh full 10 seconds stacked
          // on top (Part 12's hard total-budget invariant). If the
          // budget is already exhausted by the time a wait is reached,
          // that wait is skipped outright — canvas persistence is
          // abandoned via the existing best-effort contract without
          // spending a zero/negative-duration timeout just to manufacture
          // an async rejection (Part 6).
          const rendererDeadline = Date.now() + BPF_CANVAS_EXPORT_TIMEOUT_MS;
          let blob = null;
          let abandonCanvasPersistence = false;
          if (signature !== null && signature === this._preparedCanvasSignature && this._preparedCanvasBlobPromise) {
            const preparedRemainingMs = rendererDeadline - Date.now();
            if (preparedRemainingMs <= 0) {
              abandonCanvasPersistence = true;
            } else {
              bpfTimingMark('T4Y branch: reusing early-prepared canvas Blob (Review-open head start)');
              // Phase 5C.1P — explicitly distinguishes WHY the reuse
              // didn't produce a Blob:
              //
              // - an ACTIVE rejection (error.name !== 'TimeoutError')
              //   means the early prepare's OWN state was
              //   invalid/failed promptly — the purchase-time canvas
              //   state may still be perfectly valid, so one fresh
              //   attempt below (bounded by whatever budget remains) is
              //   still worth making (Part 9's required recovery
              //   behavior).
              // - a TIMEOUT (error.name === 'TimeoutError') means the
              //   renderer itself is genuinely stuck AND, by
              //   definition, the shared deadline has now been reached
              //   — abandonCanvasPersistence skips the fresh-export
              //   attempt entirely in this case; existing best-effort
              //   semantics (no GID set, Add to Cart proceeds) take
              //   over from here, same as any other canvas failure.
              try {
                blob = await bpfWithTimeoutOrReject(this._preparedCanvasBlobPromise, preparedRemainingMs);
              } catch (error) {
                blob = null;
                if (error && error.name === 'TimeoutError') {
                  abandonCanvasPersistence = true;
                }
              }
            }
          }
          if (!blob && !abandonCanvasPersistence) {
            const freshRemainingMs = rendererDeadline - Date.now();
            if (freshRemainingMs > 0) {
              bpfTimingMark('T4Y branch: canvas export begins (no valid early-prepared Blob)');
              // Phase 5C.1O — the actual root cause real mobile QA
              // traced: this call had no timeout anywhere in its chain.
              // A timeout here rejects exactly like an active renderer
              // failure already would, so the unmodified outer
              // try/catch below (this function's own long-established
              // "canvas persistence failure is swallowed, Add to Cart
              // is never blocked by it" contract) handles it
              // identically — no new failure branch.
              // Phase 5C.1Q — bounded by whatever remains of the SAME
              // shared renderer deadline established above (cold start
              // with no prepared Blob gets essentially the full budget;
              // a prior active rejection gets only what's left) — this
              // is the ONE and ONLY renderer attempt reachable from this
              // branch, so total customer-facing renderer-stall delay
              // across both waits combined is capped at one 10-second
              // budget, never two.
              blob = await bpfWithTimeoutOrReject(
                window.BpfMemorialRenderer.exportMemorialPreviewPNG(canvasState, { stageSrc: stageSrc }),
                freshRemainingMs
              );
            }
          }
          if (blob) {
            bpfTimingMark('T5Y canvas export/generation complete, T6Y image network persistence begins');
            const gid = await this.uploadBuilderImage(blob, 'memorial-canvas.png');
            bpfTimingMark('T7Y image persistence complete (canvas)');
            this.setBuilderImageGidField('canvas', gid);
          }
        } catch (error) {
          // Deliberately swallowed — see method header.
        }
      }

      // The one centralized canvas update path — called from the
      // existing delegated handleChange() only for Phase 3.0 (every
      // input this phase's canvas cares about — companion, breed, style,
      // colors — is a `change` event; handleInput() gains its own call
      // to this in Phase 3.3, once engraving is actually rendered from
      // it). No control-specific handler calls this directly.
      updateCanvasFromState() {
        if (!this.canvasRenderer) return;
        const canvasState = this.getCanvasState();
        this.canvasRenderer.render(canvasState);
        this.preloadVisualAssets(canvasState.visualAssets);
        this.updateEngravingFromState();
      }

      // Color-switch latency fix: once companion+breed+style resolves to
      // a real visual record (the exact same visualAssets object
      // resolveVisual() already produced for the render call above —
      // never a separately-invented URL), proactively fetch every color
      // variant THAT identity could show, so a later color switch is a
      // browser-cache hit instead of a fresh network request. Only the
      // resolved compositionMode's own map(s) are preloaded — Vault's
      // base (White/Black) + figure (White/Black/Metallic Blue/Metallic
      // Gold/Metallic Pink), or Capsule's flattened map alone — never
      // both styles at once, and nothing at all until visualAssets
      // itself resolves (i.e. not before the customer has an eligible
      // Memorial Style). this._preloadedVisualUrls is a session-lifetime
      // Set so the same URL is never requested twice, even across
      // repeated companion/breed/style changes. Advisory only: no
      // onerror handling, no state written, nothing awaited — a missing
      // asset here changes nothing, since _applyLayer()'s own load/
      // decode remains the single source of truth for what actually
      // gets shown on screen.
      preloadVisualAssets(visualAssets) {
        if (!visualAssets) return;
        if (!this._preloadedVisualUrls) this._preloadedVisualUrls = new Set();

        const maps =
          visualAssets.compositionMode === 'flattened'
            ? [visualAssets.flattenedAssetsByColor]
            : [visualAssets.baseAssetsByColor, visualAssets.figureAssetsByColor];

        maps.forEach((map) => {
          if (!map) return;
          Object.keys(map).forEach((color) => {
            const entry = map[color];
            const src = entry && entry.src;
            if (!src || this._preloadedVisualUrls.has(src)) return;
            this._preloadedVisualUrls.add(src);
            const preloadImg = new Image();
            preloadImg.src = src;
          });
        });
      }

      // Phase 5.3.0: separated from updateCanvasFromState() above so
      // Pet Name keystrokes (handleInput()) can refresh only the
      // engraving layer, without also invoking the image compositor on
      // every keystroke — color/style changes (handleChange()) still
      // refresh both, via updateCanvasFromState() calling this too.
      //
      // Phase 5C.1F root-cause fix (real mobile QA: typed engraving
      // appeared only after navigating to Review, ~7s in practice —
      // actually the time it took the customer to reach Review, not a
      // literal timer): Phase 5.3.1's `if (!this.isDesktopViewport())
      // return;` gate here was correct THEN — the canvas itself was
      // never visible on mobile until Review (pre-5C.1B), so rendering
      // engraving live would have drawn to a hidden, offscreen node for
      // nothing. Phase 5C.1B made the canvas persistently visible on
      // mobile from the very first step, and this gate was never
      // updated to match — leaving mobile customers looking at a
      // persistently-visible canvas whose engraving simply never
      // updated live, only catching up whenever they happened to open
      // Review (renderEngravingForMobileReview() below, still correct
      // and still called from there — now simply one of two live
      // trigger paths instead of the only one). Desktop's own branch is
      // completely unchanged (still immediate, still undebounced — the
      // exact behavior already proven acceptable). Mobile now renders
      // too, through the debounced path (this._debouncedMobileEngravingRender,
      // constructed once above) rather than immediately, since mobile
      // hardware is typically less powerful than the desktop hardware
      // this render() call has always run per-keystroke on.
      updateEngravingFromState() {
        if (!this.engravingLayer) return;
        if (this.isDesktopViewport()) {
          this.engravingLayer.render(this.getCanvasState());
          return;
        }
        this._debouncedMobileEngravingRender();
      }

      // Phase 5.4: still called from initAccordion()'s toggle listener
      // whenever Review becomes the open step on mobile — Phase 5C.1F
      // makes this redundant-but-harmless rather than the only mobile
      // trigger (updateEngravingFromState() above now also renders live
      // on mobile): reopening Review still legitimately wants one
      // immediate, undebounced render of whatever the current state is,
      // exactly like this already did before. Same renderer, same
      // engravingLayer instance, same render() call desktop already
      // uses — no forked drawing logic, only a different WHEN. Desktop
      // does not call this: it doesn't need to, already rendering
      // continuously via updateEngravingFromState().
      renderEngravingForMobileReview() {
        if (!this.engravingLayer) return;
        this.engravingLayer.render(this.getCanvasState());
      }

      // Phase 5.3.1: the single Builder-level desktop determination,
      // reused by every engraving trigger — same breakpoint semantics
      // updateCanvasPosition() already uses elsewhere in this file, not
      // a new/conflicting one.
      //
      // Phase 7G.3: a plain (min-width: 750px) check can be fooled by
      // Safari's Page Zoom (distinct from pinch-zoom), which inflates
      // the reported CSS viewport width without changing a physical
      // phone's actual screen size or touch-only input — this JS check
      // and the matching CSS breakpoints (assets/bpf-memorial-
      // designer.css, assets/bpf-memorial-designer-theme-cohesion.css)
      // must agree on what counts as "desktop," or JS-driven behavior
      // (mobile nav, canvas positioning, mobile no-collapse) could end
      // up disagreeing with what the CSS layout is actually doing.
      // Identical compound query to those CSS files' own Phase 7G.3
      // comments: real desktop/laptop browsers almost always report a
      // fine pointer, which alone covers the vast majority of genuine
      // desktop cases; the second, comma-joined alternative additionally
      // covers genuine touch-only tablets via min-device-width (the
      // actual screen, not the zoom-affected layout viewport) so a real
      // iPad is unaffected. A zoomed-out phone matches neither branch.
      isDesktopViewport() {
        return window.matchMedia(
          'screen and (min-width: 750px) and (pointer: fine), screen and (min-width: 750px) and (min-device-width: 750px)'
        ).matches;
      }

      // Called only from the debounced resize handler. Phase 5C.1F: both
      // desktop and mobile now render engraving live (see
      // updateEngravingFromState() above), so a viewport transition in
      // EITHER direction just needs one fresh render of current state —
      // never hidden/invalidated anymore, since there's no longer a
      // viewport where engraving is intentionally never shown. Not
      // routed through the debounced mobile path: a viewport transition
      // is a discrete, infrequent event (a resize or a Theme Editor
      // device-preview toggle), not a keystroke burst, so an immediate
      // render is correct and never competes with in-flight typing.
      syncEngravingViewportTransition() {
        const isDesktop = this.isDesktopViewport();
        const transitioned = isDesktop !== this._engravingWasDesktop;
        this._engravingWasDesktop = isDesktop;
        if (!transitioned || !this.engravingLayer) return;
        this.engravingLayer.render(this.getCanvasState());
      }

      updateSectionSummary(details) {
        const valuesEl = details.querySelector('[data-summary-values]');
        if (!valuesEl) return;

        let lines = [];
        switch (details.dataset.accordionSection) {
          case 'companion':
            lines = this.buildCompanionSummaryLines();
            break;
          case 'style':
            lines = this.buildStyleSummaryLines();
            break;
          case 'personalize':
            lines = this.buildPersonalizeSummaryLines();
            break;
          case 'photo':
            lines = this.buildPhotoSummaryLines();
            break;
          case 'memorial-wall':
            lines = this.buildMemorialWallSummaryLines();
            break;
          default:
            lines = [];
        }

        this.renderSummaryLines(valuesEl, lines);
      }

      // Phase 2.3.2: at most two-to-three short, grouped lines per card —
      // concise and human-readable, not one line per raw field ("dense
      // stacked text") and not one long run-on sentence either.
      renderSummaryLines(container, lines) {
        container.innerHTML = '';
        lines.filter(Boolean).forEach((line) => {
          const span = document.createElement('span');
          span.className = 'bpf-accordion-summary__value-line';
          span.textContent = line;
          container.appendChild(span);
        });
      }

      buildCompanionSummaryLines() {
        const checked = this.companionTypeInputs.find((input) => input.checked);
        const lines = [checked ? checked.value : ''];
        if (this.isBreedRequired() && this.breedSelect && this.breedSelect.value) {
          lines.push(this.breedSelect.value);
        }
        return lines;
      }

      buildStyleSummaryLines() {
        return [this.getStyleDisplayValue() || ''];
      }

      // Phase 2.5.1: Pet's Name no longer lives on this card (relocated to
      // Memorial Wall — see buildMemorialWallSummaryLines()), so this is
      // back to product personalization only: colors + engraved name.
      buildPersonalizeSummaryLines() {
        const lines = [];

        if (this.isVaultSelected()) {
          const vaultColor = this.getCheckedValue(this.querySelector('[data-step="vault-color"] input:checked'));
          const dogColor = this.getCheckedValue(this.querySelector('[data-step="dog-engraving-color"] input:checked'));
          const colors = [vaultColor, dogColor].filter(Boolean).join(' / ');
          lines.push(['Vault', colors].filter(Boolean).join(' · '));
        } else {
          const capsuleColor = this.getCheckedValue(this.querySelector('[data-step="capsule-color"] input:checked'));
          lines.push(['Capsule', capsuleColor].filter(Boolean).join(' · '));
        }

        const engravedName = this.petNameInput && this.petNameInput.value.trim();
        if (this.currentStyleSupportsEngraving() && engravedName) {
          lines.push(`Engraved: ${engravedName}`);
        }

        const engravingColor = this.engravingColorSelect && this.engravingColorSelect.value;
        if (this.currentStyleSupportsEngraving() && engravingColor) {
          lines.push(`Engraving Color: ${engravingColor}`);
        }

        return lines;
      }

      // Phase 4D.10C, Part 20 — Step 4 is now Picture-Frame-first, so its
      // closed summary is too: flag off keeps the exact pre-4D.10C
      // "1 photo selected"/"No photo selected" wording (Step 4 is still a
      // plain Favorite Photo card in that case — see the Liquid {%- else
      // -%} branch). Flag on: "No Picture Frame" when declined (no photo
      // status shown at all — Step 4 offered no photo opportunity in that
      // case), or "Picture Frame · Orientation · Color" plus a second
      // "Photo uploaded"/"No photo selected" line when selected.
      buildPhotoSummaryLines() {
        if (!this.pictureFrameEnabled) {
          return [this.selectedPhotoFile ? '1 photo selected' : 'No photo selected'];
        }
        if (!this.state.pictureFrame.selected) {
          return ['No Picture Frame'];
        }
        return [
          `Picture Frame · ${this.state.pictureFrame.orientation} · ${this.state.pictureFrame.color}`,
          this.selectedPhotoFile ? 'Photo uploaded' : 'No photo selected',
        ];
      }

      buildMemorialWallSummaryLines() {
        const checked = this.memorialWallInputs.find((input) => input.checked);
        const optedIn = Boolean(checked && checked.value === 'Yes');
        const lines = [optedIn ? 'Yes' : 'Not at this time'];
        // Phase 2.5.3: shown for both styles now — see
        // isCompanionNameValid()/updateMemorialWallPetNameVisibility().
        if (optedIn && this.companionNameInput && this.companionNameInput.value.trim()) {
          lines.push(`Pet Name: ${this.companionNameInput.value.trim()}`);
        }
        if (optedIn && this.storyInput && this.storyInput.value.trim()) {
          lines.push('Story added');
        }
        return lines;
      }

      updateReviewSummary() {
        if (!this.reviewSummary) return;

        const dash = '—';
        const rows = [];

        const companionTypeChecked = this.companionTypeInputs.find((input) => input.checked);
        rows.push(['Companion Type', (companionTypeChecked && companionTypeChecked.value) || dash]);

        if (this.isBreedRequired()) {
          rows.push(['Breed', (this.breedSelect && this.breedSelect.value) || dash]);
        }

        rows.push(['Memorial Style', this.getStyleDisplayValue() || dash]);

        if (this.isVaultSelected()) {
          const vaultColor = this.getCheckedValue(this.querySelector('[data-step="vault-color"] input:checked'));
          const dogColor = this.getCheckedValue(this.querySelector('[data-step="dog-engraving-color"] input:checked'));
          const colorParts = [vaultColor && `${vaultColor} Vault`, dogColor && `${dogColor} Dog & Engraving`].filter(Boolean);
          rows.push(['Colors', colorParts.length ? colorParts.join(', ') : dash]);
        } else {
          const capsuleColor = this.getCheckedValue(this.querySelector('[data-step="capsule-color"] input:checked'));
          rows.push(['Colors', capsuleColor ? `${capsuleColor} Capsule` : dash]);
        }

        // Phase 2.5.3: shown whenever an engraving value exists,
        // independent of Memorial Wall — this is a genuinely different
        // concept from the Memorial Wall "Pet Name" row below (physical
        // engraving vs. the name shown alongside the memorial), and both
        // may legitimately hold different values. Dog Bone Capsule phase:
        // no longer Vault-only — currentStyleSupportsEngraving() covers
        // both Vault (unconditionally, unchanged) and the one Capsule
        // whose visual mapping record declares engravingSurface:
        // 'capsule' (today, only Dog Bone Capsule); every other Capsule
        // still never shows this row, since the field itself stays
        // hidden/empty for them.
        if (this.currentStyleSupportsEngraving()) {
          const engravedName = this.petNameInput && this.petNameInput.value.trim();
          if (engravedName) {
            rows.push(['Engraved Name', engravedName]);
          }

          // Engraving Color: shown alongside Engraved Name for the same
          // engraving-capable products, never for a normal Capsule (this
          // whole block is already gated on currentStyleSupportsEngraving()).
          // Always the <select>'s own current value — kept synced to the
          // resolved effective default until the customer explicitly
          // overrides it, so this row is accurate whether or not they
          // ever touched the field.
          const engravingColor = this.engravingColorSelect && this.engravingColorSelect.value;
          if (engravingColor) {
            rows.push(['Engraving Color', engravingColor]);
          }
        }

        const wallChecked = this.memorialWallInputs.find((input) => input.checked);
        const wallOptedIn = Boolean(wallChecked && wallChecked.value === 'Yes');

        // Phase 2.5.3: the Memorial Wall's own Pet Name row — for both
        // styles now, shown only while Memorial Wall is opted into
        // (omitted otherwise, matching Story/State's existing omit-when-
        // not-applicable pattern below, rather than a dash).
        if (wallOptedIn) {
          rows.push(['Pet Name', (this.companionNameInput && this.companionNameInput.value.trim()) || dash]);
        }

        rows.push(['Photo', this.selectedPhotoFile ? '1 photo selected' : 'No photo selected']);

        // Phase 4D.10B — Picture Frame add-on. Optional: no row at all
        // when not selected (never a dash), matching Pet Name/Story's own
        // omit-when-not-applicable convention above rather than the
        // dash-when-empty convention used for required-ish fields like
        // Companion Type/Breed/Colors. No price/total row — the frame is
        // not yet connected to the Shopify cart (Part 15/16).
        if (this.pictureFrameEnabled && this.state.pictureFrame.selected) {
          rows.push(['Picture Frame', `${this.state.pictureFrame.orientation} · ${this.state.pictureFrame.color}`]);
        }

        rows.push(['Memorial Wall', wallOptedIn ? 'Yes' : 'Not at this time']);

        if (wallOptedIn) {
          rows.push(['Story', this.storyInput && this.storyInput.value.trim() ? 'Story added' : 'No story shared']);
          rows.push(['State', (this.stateSelect && this.stateSelect.value) || dash]);
        }

        this.reviewSummary.innerHTML = '';
        rows.forEach(([label, value]) => {
          const row = document.createElement('div');
          row.className = 'bpf-review-summary__row';

          const dt = document.createElement('dt');
          dt.textContent = label;

          const dd = document.createElement('dd');
          dd.textContent = value;

          row.appendChild(dt);
          row.appendChild(dd);
          this.reviewSummary.appendChild(row);
        });
      }

    }
  );
}
